import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { parseCTe, isCTe, isNFe } from '../utils/parseCTe';
import { classifyNotaFinanceira, financialClassificationUtils } from '../utils/financialClassification';
import { Eye, Edit, Rocket, Trash2, DollarSign, ArrowLeft, Printer, Search } from 'lucide-react';
import { getPaymentMethodName } from '../utils/paymentMethods';
import {
  Upload,
  FileText,
  Type,
  Building2,
  Hash,
  Calendar,
  AlertTriangle,
  X,
  Info,
  Plus,
  ClipboardList,
  CreditCard,
  Truck,
  Ban,
  CheckCircle2,
  Clock,
  ListFilter,
  FileWarning,
  Layers,
  Settings
} from 'lucide-react';
import { api } from '../services/api';
import ImportRuleConfigurator from '../components/ImportRuleConfigurator';
import LoadingOverlay from '../components/LoadingOverlay';
import { applyNotaImportRules } from '../utils/notaImportRules';

const initialManualForm = {
  numero: '',
  data_emissao: '',
  valor_total: '',
  tipo_operacao: 'pagar',
  emitente_nome: '',
  emitente_cnpj: '',
  destinatario_nome: '',
  destinatario_cnpj: '',
  observacoes: '',
  data_vencimento: '',
  cfop: '',
  natureza_operacao: '',
  gera_financeiro: true,
  motivo_financeiro: ''
};

const sanitizeCnpj = (value) => (value ? value.replace(/\D/g, '') : '');

const formatCnpj = (value) => {
  const digits = sanitizeCnpj(value);
  if (digits.length !== 14) return value || '';
  return digits.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
};

const parseCurrencyString = (value) => {
  if (typeof value === 'number') return value;
  if (value === null || value === undefined) return 0;

  const raw = value.toString().trim();
  if (!raw) return 0;

  const cleaned = raw.replace(/\s+/g, '').replace(/[^0-9,.-]/g, '');
  const commaIndex = cleaned.lastIndexOf(',');
  const dotIndex = cleaned.lastIndexOf('.');

  if (commaIndex > -1 && dotIndex > -1) {
    if (dotIndex < commaIndex) {
      const semPontos = cleaned.replace(/\./g, '');
      return parseFloat(semPontos.replace(',', '.')) || 0;
    }
    const semVirgulas = cleaned.replace(/,/g, '');
    return parseFloat(semVirgulas) || 0;
  }

  if (commaIndex > -1) {
    return parseFloat(cleaned.replace(/\./g, '').replace(',', '.')) || 0;
  }

  if ((cleaned.match(/\./g) || []).length > 1) {
    return parseFloat(cleaned.replace(/\./g, '')) || 0;
  }

  return parseFloat(cleaned) || 0;
};

const formatCurrency = (value) => {
  const number = typeof value === 'number' ? value : parseCurrencyString(value);
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL'
  }).format(Number.isFinite(number) ? number : 0);
};

const formatDate = (date) => {
  if (!date) return '';
  try {
    return new Date(date + 'T00:00:00').toLocaleDateString('pt-BR');
  } catch (error) {
    return date;
  }
};

const parseBrazilianDate = (value) => {
  if (!value) return '';
  const parts = value.split(/[\/]/);
  if (parts.length === 3) {
    const [day, month, year] = parts;
    if (day.length === 2 && month.length === 2 && year.length === 4) {
      return `${year}-${month}-${day}`;
    }
  }
  return value;
};

const parseBooleanFlag = (value, defaultValue = true) => {
  if (value === null || value === undefined) return defaultValue;
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return value !== 0;
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    if (['1', 'true', 't', 'yes', 'y', 'sim'].includes(normalized)) return true;
    if (['0', 'false', 'f', 'no', 'n', 'nao', 'não'].includes(normalized)) return false;
  }
  return defaultValue;
};

const fallbackNormalizeCfop = (value) => {
  if (!value) return '';
  return String(value).replace(/\D/g, '').slice(0, 4);
};

const fallbackFormatCfop = (value) => {
  const digits = fallbackNormalizeCfop(value);
  if (!digits) return '';
  if (digits.length !== 4) return digits;
  return `${digits[0]}.${digits.slice(1)}`;
};

const analisarParcelasNota = (nota) => {
  const parcelas = Array.isArray(nota?.parcelas) ? nota.parcelas : [];
  const possuiParcelas = parcelas.length > 0;
  const possuiVencimentos = parcelas.some((parcela) => Boolean(parcela?.data_vencimento));
  const possuiParcelasSemVencimento = parcelas.some((parcela) => !parcela?.data_vencimento);
  const possuiParcelasComValor = parcelas.some((parcela) => Number(parcela?.valor) > 0);

  return {
    parcelas,
    possuiParcelas,
    possuiVencimentos,
    possuiParcelasSemVencimento,
    possuiParcelasComValor
  };
};

const notaPossuiAlgumVencimento = (nota) => {
  if (!nota) return false;
  const analise = analisarParcelasNota(nota);
  if (analise.possuiVencimentos) {
    return true;
  }
  return Boolean(nota?.data_vencimento);
};

const inferTipoOperacao = (emitenteCnpj, destinatarioCnpj, empresaCnpj) => {
  const empresa = sanitizeCnpj(empresaCnpj);
  if (!empresa) return null;
  if (empresa && sanitizeCnpj(emitenteCnpj) === empresa) return 'receber';
  if (empresa && sanitizeCnpj(destinatarioCnpj) === empresa) return 'pagar';
  return null;
};

const describeEmpresaNota = (nota) => {
  if (!nota) {
    return { nome: 'Empresa não identificada', subtitulo: '' };
  }

  const origem = typeof nota.origem === 'string' ? nota.origem.toLowerCase() : '';
  const tipoDocumento = typeof nota.tipo_documento === 'string' ? nota.tipo_documento.toLowerCase() : '';
  const isTransportador = origem.includes('cte') || tipoDocumento.includes('cte');

  if (isTransportador) {
    const nome =
      nota.emitente?.nome ||
      nota.emitente?.fantasia ||
      nota.emitente?.cnpj ||
      nota.fornecedor ||
      nota.destinatario?.nome ||
      'Transportador não identificado';
    return { nome, subtitulo: 'Transportador' };
  }

  if (nota.tipo_operacao === 'pagar') {
    const nome =
      nota.emitente?.nome ||
      nota.emitente?.fantasia ||
      nota.emitente?.cnpj ||
      nota.destinatario?.nome ||
      'Fornecedor não identificado';
    return { nome, subtitulo: 'Fornecedor' };
  }

  if (nota.tipo_operacao === 'receber') {
    const nome =
      nota.destinatario?.nome ||
      nota.destinatario?.fantasia ||
      nota.destinatario?.cnpj ||
      nota.emitente?.nome ||
      'Cliente não identificado';
    return { nome, subtitulo: 'Cliente' };
  }

  const nomeBase =
    nota.emitente?.nome ||
    nota.destinatario?.nome ||
    nota.emitente?.cnpj ||
    nota.destinatario?.cnpj ||
    'Empresa não identificada';

  return { nome: nomeBase, subtitulo: 'Entidade' };
};

const buildMetadataFromNota = (nota) => {
  if (!nota) return null;
  return {
    numero: nota.numero || '',
    valor_total: nota.valor_total || 0,
    data_emissao: nota.data_emissao || '',
    data_vencimento: nota.data_vencimento || '',
    tipo_operacao: nota.tipo_operacao || null,
    emitente: nota.emitente || null,
    destinatario: nota.destinatario || null,
    parcelas: Array.isArray(nota.parcelas) ? nota.parcelas : [],
    pagamentos: Array.isArray(nota.pagamentos) ? nota.pagamentos : [],
    faturas: Array.isArray(nota.faturas) ? nota.faturas : [],
    chave_acesso: nota.chave_acesso || null,
    origem: nota.origem || null,
    observacoes: nota.observacoes || '',
    cfop: nota.cfop || null,
    natureza_operacao: nota.natureza_operacao || null,
    gera_financeiro:
      nota.gera_financeiro === null || nota.gera_financeiro === undefined
        ? null
        : Boolean(nota.gera_financeiro),
    motivo_financeiro: nota.motivo_financeiro || null
  };
};

const findDuplicateNota = (lista, referencia, ignoreId = null) => {
  if (!Array.isArray(lista) || !referencia) return null;

  const numeroReferencia = referencia.numero ? referencia.numero.toString().trim() : '';
  const chaveReferencia = referencia.chave_acesso ? referencia.chave_acesso.toString().trim() : '';

  if (!numeroReferencia && !chaveReferencia) {
    return null;
  }

  return (
    lista.find((nota) => {
      if (!nota) return false;
      if (ignoreId && (nota.id === ignoreId || nota.numero === ignoreId)) {
        return false;
      }
      const numeroNota = nota.numero ? nota.numero.toString().trim() : '';
      const chaveNota = nota.chave_acesso ? nota.chave_acesso.toString().trim() : '';
      return (
        (numeroReferencia && numeroNota && numeroNota === numeroReferencia) ||
        (chaveReferencia && chaveNota && chaveNota === chaveReferencia)
      );
    }) || null
  );
};

const buildNotaDuplicateKey = (nota) => {
  if (!nota) return null;
  const numero = nota.numero ? String(nota.numero).trim() : '';
  const chave = nota.chave_acesso ? String(nota.chave_acesso).trim() : '';

  if (chave) {
    return `chave:${chave}`;
  }

  if (numero) {
    return `numero:${numero}`;
  }

  return null;
};

const enhanceBulkImportItems = (items, existingNotas, { preserveSelection = true } = {}) => {
  if (!Array.isArray(items)) return [];
  const duplicateCounts = new Map();

  items.forEach((item) => {
    if (!item || !item.nota) return;
    const key = buildNotaDuplicateKey(item.nota);
    if (!key) return;
    duplicateCounts.set(key, (duplicateCounts.get(key) || 0) + 1);
  });

  return items.map((item) => {
    if (!item || !item.nota) {
      const situacaoFinanceira = getBulkImportFinanceStatus({
        nota: null,
        pendencias: [],
        geraFinanceiro: false,
        status: item?.status
      });
      return {
        ...item,
        selected: false,
        duplicateExisting: false,
        duplicateInBatch: false,
        pendencias: [],
        situacaoFinanceira,
        hasAutomaticAdjustments: false,
        appliedImportRules: [],
        importRuleAdjustments: [],
        importRuleChangedFields: []
      };
    }

    const duplicateKey = buildNotaDuplicateKey(item.nota);
    const duplicateInBatch = duplicateKey ? (duplicateCounts.get(duplicateKey) || 0) > 1 : false;
    const duplicateExisting = Boolean(
      findDuplicateNota(existingNotas, {
        numero: item.nota.numero,
        chave_acesso: item.nota.chave_acesso
      })
    );

    const analiseParcelas = analisarParcelasNota(item.nota);
    const geraFinanceiro = parseBooleanFlag(item.nota.gera_financeiro, true);
    const valorTotalNormalizado = parseCurrencyString(item.nota.valor_total);
    const pendencias = [];

    if (!item.nota.tipo_operacao) {
      pendencias.push('Tipo não definido');
    }

    if (!valorTotalNormalizado) {
      pendencias.push('Valor total ausente');
    }

    if (geraFinanceiro) {
      const possuiClassificacao = Boolean(item.nota.cfop || item.nota.natureza_operacao);
      if (!analiseParcelas.possuiParcelas && !item.nota.data_vencimento) {
        pendencias.push('Sem vencimento');
      }
      if (analiseParcelas.possuiParcelasSemVencimento) {
        pendencias.push('Parcelas sem data');
      }
      if (!analiseParcelas.possuiParcelasComValor && !valorTotalNormalizado) {
        pendencias.push('Parcelas sem valor');
      }
      if (!possuiClassificacao) {
        pendencias.push('Classificação pendente');
      }
    }

    const allowDuplicate = Boolean(item.allowDuplicate);
    const defaultSelected =
      !item.error &&
      (!duplicateExisting || allowDuplicate) &&
      (!duplicateInBatch || allowDuplicate) &&
      item.status !== 'saved';

    const selected =
      preserveSelection && typeof item.selected === 'boolean' ? item.selected : defaultSelected;

    const situacaoFinanceira = getBulkImportFinanceStatus({
      nota: item.nota,
      pendencias,
      geraFinanceiro,
      duplicateExisting,
      duplicateInBatch,
      status: item.status
    });

    return {
      ...item,
      selected,
      duplicateKey,
      duplicateExisting,
      duplicateInBatch,
      pendencias,
      analiseParcelas,
      geraFinanceiro,
      situacaoFinanceira,
      tipoDocumento: item.nota.origem === 'xml_cte' ? 'cte' : 'nfe',
      valorTotalNormalizado,
      possuiPendencias: pendencias.length > 0,
      parcelasCount: Array.isArray(item.nota.parcelas) ? item.nota.parcelas.length : 0,
      hasAutomaticAdjustments:
        Array.isArray(item.importRuleAdjustments) && item.importRuleAdjustments.length > 0,
      appliedImportRules: Array.isArray(item.appliedImportRules) ? item.appliedImportRules : [],
      importRuleAdjustments: Array.isArray(item.importRuleAdjustments)
        ? item.importRuleAdjustments
        : [],
      importRuleChangedFields: Array.isArray(item.importRuleChangedFields)
        ? item.importRuleChangedFields
        : []
    };
  });
};

const buildNotaPayloadForApi = (nota) => {
  if (!nota) return null;

  const parcelas = Array.isArray(nota.parcelas)
    ? nota.parcelas.map((parcela, index) => ({
        id: parcela.id,
        numero: parcela.numero ?? String(index + 1),
        data_vencimento: parcela.data_vencimento || null,
        valor: parseCurrencyString(parcela.valor),
        status: parcela.status || null
      }))
    : [];

  const faturas = Array.isArray(nota.faturas)
    ? nota.faturas.map((fatura) => ({
        id: fatura.id,
        numero: fatura.numero || null,
        valor_original: parseCurrencyString(fatura.valor_original),
        valor_desconto: parseCurrencyString(fatura.valor_desconto),
        valor_liquido: parseCurrencyString(fatura.valor_liquido)
      }))
    : [];

  const pagamentos = Array.isArray(nota.pagamentos)
    ? nota.pagamentos.map((pagamento) => ({
        id: pagamento.id,
        meio: pagamento.meio || null,
        indicador: pagamento.indicador || null,
        valor: parseCurrencyString(pagamento.valor),
        data_pagamento: pagamento.data_pagamento || null
      }))
    : [];

  const cfop = nota.cfop ? String(nota.cfop).trim() : null;
  const naturezaOperacao = nota.natureza_operacao ? String(nota.natureza_operacao).trim() : null;
  const geraFinanceiro =
    nota.gera_financeiro === null || nota.gera_financeiro === undefined
      ? null
      : Boolean(nota.gera_financeiro);
  const motivoFinanceiro = nota.motivo_financeiro
    ? String(nota.motivo_financeiro).trim() || null
    : null;

  const metadata = {
    emitente: nota.emitente || null,
    destinatario: nota.destinatario || null,
    parcelas,
    faturas,
    pagamentos,
    chave_acesso: nota.chave_acesso || null,
    tipo_operacao: nota.tipo_operacao || null,
    data_vencimento: nota.data_vencimento || null,
    observacoes: nota.observacoes || '',
    cfop,
    natureza_operacao: naturezaOperacao,
    gera_financeiro: geraFinanceiro,
    motivo_financeiro: motivoFinanceiro,
  };

  const payload = {
    numero: nota.numero || '',
    serie: nota.serie || null,
    chave_acesso: nota.chave_acesso || null,
    data_emissao: nota.data_emissao || '',
    valor_total: parseCurrencyString(nota.valor_total),
    status: nota.status || null,
    tipo_operacao: nota.tipo_operacao || null,
    data_vencimento: nota.data_vencimento || null,
    origem: nota.origem || null,
    observacoes: nota.observacoes || '',
    parcelas,
    faturas,
    pagamentos,
    empresa_id: nota.empresa_id || null,
    cfop,
    natureza_operacao: naturezaOperacao,
    gera_financeiro: geraFinanceiro,
    motivo_financeiro: motivoFinanceiro,
    metadata
  };

  return payload;
};

const mergeNotaWithMetadata = (nota, metadata) => {
  if (!metadata) {
    return {
      ...nota,
      parcelas: Array.isArray(nota.parcelas) ? nota.parcelas : [],
      pagamentos: Array.isArray(nota.pagamentos) ? nota.pagamentos : [],
      faturas: Array.isArray(nota.faturas) ? nota.faturas : []
    };
  }

  return {
    ...nota,
    numero: metadata.numero || nota.numero || '',
    valor_total: metadata.valor_total ?? nota.valor_total ?? 0,
    data_emissao: metadata.data_emissao || nota.data_emissao || '',
    data_vencimento: metadata.data_vencimento || nota.data_vencimento || '',
    tipo_operacao: metadata.tipo_operacao || nota.tipo_operacao || null,
    emitente: metadata.emitente || nota.emitente || null,
    destinatario: metadata.destinatario || nota.destinatario || null,
    parcelas: Array.isArray(metadata.parcelas) && metadata.parcelas.length > 0
      ? metadata.parcelas
      : Array.isArray(nota.parcelas)
        ? nota.parcelas
        : [],
    pagamentos: Array.isArray(metadata.pagamentos) ? metadata.pagamentos : nota.pagamentos || [],
    faturas: Array.isArray(metadata.faturas) ? metadata.faturas : nota.faturas || [],
    chave_acesso: metadata.chave_acesso || nota.chave_acesso || null,
    origem: metadata.origem || nota.origem || null,
    observacoes: metadata.observacoes || nota.observacoes || '',
    cfop: metadata.cfop ?? nota.cfop ?? null,
    natureza_operacao: metadata.natureza_operacao ?? nota.natureza_operacao ?? null,
    gera_financeiro:
      metadata.gera_financeiro ??
      (nota.gera_financeiro === null || nota.gera_financeiro === undefined
        ? true
        : nota.gera_financeiro),
    motivo_financeiro: metadata.motivo_financeiro ?? nota.motivo_financeiro ?? null,
    indicador_lancamentos: Object.prototype.hasOwnProperty.call(metadata, 'indicador_lancamentos')
      ? metadata.indicador_lancamentos
      : nota.indicador_lancamentos ?? null,
    lancamentos_status: Object.prototype.hasOwnProperty.call(metadata, 'lancamentos_status')
      ? metadata.lancamentos_status
      : nota.lancamentos_status ?? null,
    lancamentos: Array.isArray(metadata.lancamentos) && metadata.lancamentos.length > 0
      ? metadata.lancamentos
      : Array.isArray(nota.lancamentos)
        ? nota.lancamentos
        : []
  };
};

const normalizeNotaFromApi = (nota) => ({
  id: nota.id,
  empresa_id: nota.empresa_id ?? null,
  numero: nota.numero || '',
  fornecedor: nota.fornecedor || '',
  valor_total: nota.valor_total || 0,
  data_emissao: nota.data_emissao || '',
  data_vencimento: nota.data_vencimento || '',
  status: nota.status || 'Pendente',
  tipo_operacao: nota.tipo_operacao || null,
  parcelas: Array.isArray(nota.parcelas) ? nota.parcelas : [],
  pagamentos: Array.isArray(nota.pagamentos) ? nota.pagamentos : [],
  faturas: Array.isArray(nota.faturas) ? nota.faturas : [],
  emitente: nota.emitente || null,
  destinatario: nota.destinatario || null,
  chave_acesso: nota.chave_acesso || null,
  origem: nota.origem || null,
  observacoes: nota.observacoes || '',
  cfop: nota.cfop || null,
  natureza_operacao: nota.natureza_operacao || null,
  gera_financeiro: parseBooleanFlag(nota.gera_financeiro, true),
  motivo_financeiro: nota.motivo_financeiro || null,
  indicador_lancamentos: Object.prototype.hasOwnProperty.call(nota, 'indicador_lancamentos')
    ? nota.indicador_lancamentos
    : null,
  lancamentos_status: Object.prototype.hasOwnProperty.call(nota, 'lancamentos_status')
    ? nota.lancamentos_status
    : null,
  lancamentos: Array.isArray(nota.lancamentos) ? nota.lancamentos : []
});

const parseXmlNota = (xmlText, empresaCnpj) => {
  const parser = new DOMParser();
  const xml = parser.parseFromString(xmlText, 'application/xml');
  const errorNode = xml.querySelector('parsererror');
  if (errorNode) {
    throw new Error('Arquivo XML inválido.');
  }

  // ADICIONE ESTA VERIFICAÇÃO PARA CTe
  if (isCTe(xml)) {
    console.log('Detectado CTe, usando parser específico');
    const dadosCTe = parseCTe(xml);
    
    // Adaptar dados do CTe para o formato esperado pelo sistema
    return {
      numero: dadosCTe.numero,
      data_emissao: dadosCTe.data_emissao,
      valor_total: parseFloat(dadosCTe.valor_total),
      tipo_operacao: 'pagar', // CTe sempre é despesa
      
      // Para CTe, o emitente é a transportadora
      emitente: {
        nome: dadosCTe.transportadora?.nome || dadosCTe.fornecedor_cliente,
        fantasia: dadosCTe.transportadora?.fantasia,
        cnpj: dadosCTe.transportadora?.cnpj || dadosCTe.cnpj_cpf,
        ie: dadosCTe.transportadora?.ie || dadosCTe.inscricao_estadual
      },
      
      // Destinatário do frete
      destinatario: dadosCTe.destinatario ? {
        nome: dadosCTe.destinatario.nome,
        cnpj: dadosCTe.destinatario.cnpj
      } : null,
      
      // Remetente (adicional para CTe)
      remetente: dadosCTe.remetente ? {
        nome: dadosCTe.remetente.nome,
        cnpj: dadosCTe.remetente.cnpj
      } : null,
      
      parcelas: dadosCTe.data_vencimento ? [{
        numero: '1',
        data_vencimento: dadosCTe.data_vencimento,
        valor: parseFloat(dadosCTe.valor_total)
      }] : [],
      
      faturas: [],
      pagamentos: [],
      chave_acesso: dadosCTe.chave_acesso,
      origem: 'xml_cte',
      status: 'Importada',
      observacoes: dadosCTe.observacoes || '',
      data_vencimento: dadosCTe.data_vencimento || '',
      cfop: dadosCTe.cfop || null,
      cfop_codigo: dadosCTe.cfop_codigo || null,
      cfop_formatado: dadosCTe.cfop_formatado || null,
      natureza_operacao: dadosCTe.natureza_operacao || null,
      gera_financeiro: dadosCTe.gera_financeiro,
      motivo_financeiro: dadosCTe.gera_financeiro
        ? null
        : dadosCTe.motivo_financeiro || dadosCTe.classificacao_financeira?.motivoFinanceiro || null,
      classificacao_financeira: dadosCTe.classificacao_financeira,

      // Metadados específicos do CTe
      tipo_documento: 'CTe',
      fornecedor: dadosCTe.fornecedor_cliente, // Nome da transportadora
      produto: dadosCTe.produto,
      valor_carga: dadosCTe.valor_carga,
      componentes_valor: dadosCTe.componentes_valor,
      chave_nfe_relacionada: dadosCTe.chave_nfe_relacionada,

      // Visual
      icone_tipo: '🚚',
      badge_tipo: 'CT-e',
      cor_badge: 'bg-primary-100 text-primary-800 dark:bg-primary-900 dark:text-primary-200'
    };
  }

  const infNFe = xml.getElementsByTagName('infNFe')[0];
  if (!infNFe) {
    throw new Error('Estrutura da NF-e não identificada.');
  }

  const getTagValue = (parent, tag) => {
    if (!parent) return '';
    const element = parent.getElementsByTagName(tag)[0];
    return element ? element.textContent.trim() : '';
  };

  const emitenteNode = infNFe.getElementsByTagName('emit')[0];
  const destinatarioNode = infNFe.getElementsByTagName('dest')[0];
  const totalNode = infNFe.getElementsByTagName('total')[0];
  const cobrNode = infNFe.getElementsByTagName('cobr')[0];
  const pagNode = infNFe.getElementsByTagName('pag')[0];
  const ideNode = infNFe.getElementsByTagName('ide')[0];

  let cfop = '';
  const detNodes = Array.from(infNFe.getElementsByTagName('det'));
  for (const det of detNodes) {
    const prodNode = det.getElementsByTagName('prod')[0];
    const cfopValor = getTagValue(prodNode, 'CFOP');
    if (cfopValor) {
      cfop = cfopValor;
      break;
    }
  }

  const emitente = emitenteNode
    ? {
        nome: getTagValue(emitenteNode, 'xNome'),
        fantasia: getTagValue(emitenteNode, 'xFant'),
        cnpj: getTagValue(emitenteNode, 'CNPJ') || getTagValue(emitenteNode, 'CPF'),
        ie: getTagValue(emitenteNode, 'IE'),
        endereco: {
          logradouro: getTagValue(emitenteNode, 'xLgr'),
          numero: getTagValue(emitenteNode, 'nro'),
          bairro: getTagValue(emitenteNode, 'xBairro'),
          municipio: getTagValue(emitenteNode, 'xMun'),
          uf: getTagValue(emitenteNode, 'UF')
        }
      }
    : null;

  const destinatario = destinatarioNode
    ? {
        nome: getTagValue(destinatarioNode, 'xNome'),
        fantasia: getTagValue(destinatarioNode, 'xFant'),
        cnpj: getTagValue(destinatarioNode, 'CNPJ') || getTagValue(destinatarioNode, 'CPF'),
        ie: getTagValue(destinatarioNode, 'IE'),
        endereco: {
          logradouro: getTagValue(destinatarioNode, 'xLgr'),
          numero: getTagValue(destinatarioNode, 'nro'),
          bairro: getTagValue(destinatarioNode, 'xBairro'),
          municipio: getTagValue(destinatarioNode, 'xMun'),
          uf: getTagValue(destinatarioNode, 'UF')
        }
      }
    : null;

  const parcelas = [];
  if (cobrNode) {
    const duplicatas = Array.from(cobrNode.getElementsByTagName('dup'));
    duplicatas.forEach((dup, index) => {
      parcelas.push({
        numero: getTagValue(dup, 'nDup') || String(index + 1),
        data_vencimento: getTagValue(dup, 'dVenc'),
        valor: parseCurrencyString(getTagValue(dup, 'vDup'))
      });
    });
  }

  const faturas = [];
  if (cobrNode) {
    const fatNodes = Array.from(cobrNode.getElementsByTagName('fat'));
    fatNodes.forEach((fat) => {
      faturas.push({
        numero: getTagValue(fat, 'nFat'),
        valor_original: parseCurrencyString(getTagValue(fat, 'vOrig')),
        valor_desconto: parseCurrencyString(getTagValue(fat, 'vDesc')),
        valor_liquido: parseCurrencyString(getTagValue(fat, 'vLiq'))
      });
    });
  }

  const pagamentos = [];
  if (pagNode) {
    const detPagNodes = Array.from(pagNode.getElementsByTagName('detPag'));
    detPagNodes.forEach((pag) => {
      pagamentos.push({
        meio: getTagValue(pag, 'tPag'),
        valor: parseCurrencyString(getTagValue(pag, 'vPag')),
        indicador: getTagValue(pag, 'indPag')
      });
    });
  }

  const numero = getTagValue(ideNode, 'nNF');
  const dataEmissao = getTagValue(ideNode, 'dhEmi') || getTagValue(ideNode, 'dEmi');
  const chave = infNFe.getAttribute('Id') ? infNFe.getAttribute('Id').replace(/^NFe/i, '') : '';
  const valorTotal = parseCurrencyString(getTagValue(totalNode, 'vNF'));
  const naturezaOperacao = getTagValue(ideNode, 'natOp');

  const classificacaoFinanceira = classifyNotaFinanceira({
    cfop,
    naturezaOperacao,
    valorTotal,
    documento: 'nfe'
  });

  const tipo = inferTipoOperacao(emitente?.cnpj, destinatario?.cnpj, empresaCnpj);

  return {
    numero,
    data_emissao: dataEmissao ? dataEmissao.substring(0, 10) : '',
    valor_total: valorTotal,
    tipo_operacao: tipo,
    emitente,
    destinatario,
    parcelas,
    faturas,
    pagamentos,
    chave_acesso: chave,
    origem: 'xml',
    status: 'Importada',
    observacoes: '',
    data_vencimento: parcelas.length === 0 ? getTagValue(cobrNode, 'dVenc') || '' : '',
    cfop: classificacaoFinanceira.cfopFormatado || cfop || null,
    natureza_operacao: naturezaOperacao || null,
    gera_financeiro: classificacaoFinanceira.geraFinanceiro,
    motivo_financeiro: classificacaoFinanceira.geraFinanceiro
      ? null
      : classificacaoFinanceira.motivoFinanceiro,
    classificacao_financeira: classificacaoFinanceira
  };
};

const getTipoOperacaoBadge = (tipo) => {
  if (tipo === 'pagar') {
    return 'bg-danger-100 text-danger-700 dark:bg-danger-900/40 dark:text-danger-200';
  }
  if (tipo === 'receber') {
    return 'bg-success-100 text-success-700 dark:bg-success-900/40 dark:text-success-200';
  }
  return 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300';
};

const tipoOperacaoLabel = (tipo) => {
  if (tipo === 'pagar') return 'Conta a pagar';
  if (tipo === 'receber') return 'Conta a receber';
  return 'Tipo não identificado';
};

const resolveLancamentoIndicator = (nota) => {
  if (!nota) return null;

  const indicatorCandidates = [
    nota.indicador_lancamentos,
    nota.lancamentos_status,
    nota.lancamentosIndicador,
    nota.lancamentos_status?.status,
    nota?.metadata?.indicador_lancamentos
  ];

  for (const candidate of indicatorCandidates) {
    if (candidate === undefined || candidate === null) continue;

    if (typeof candidate === 'boolean') {
      return candidate ? 'lancado' : 'pendente';
    }

    if (typeof candidate === 'number') {
      return candidate === 0 ? 'pendente' : 'lancado';
    }

    if (typeof candidate === 'string') {
      const normalized = candidate.trim().toLowerCase();
      if (!normalized) continue;
      if (['lancado', 'lançado', 'gerado', 'concluido', 'concluído'].includes(normalized)) {
        return 'lancado';
      }
      if (['pendente', 'aguardando', 'nenhum', 'sem'].includes(normalized)) {
        return 'pendente';
      }
    }

    if (typeof candidate === 'object') {
      if ('lancado' in candidate) {
        return candidate.lancado ? 'lancado' : 'pendente';
      }
      if ('status' in candidate) {
        const normalized = String(candidate.status).trim().toLowerCase();
        if (normalized) {
          if (normalized.includes('lanc') || normalized.includes('lanç')) {
            return 'lancado';
          }
          if (normalized.includes('pend')) {
            return 'pendente';
          }
        }
      }
    }
  }

  if (Array.isArray(nota.lancamentos) && nota.lancamentos.length > 0) {
    return 'lancado';
  }

  return null;
};

const buildLancamentoDuplicateKey = (referencia = {}) => {
  if (!referencia || typeof referencia !== 'object') {
    return 'parcela-unica';
  }

  if (referencia.nota_parcela_id !== undefined && referencia.nota_parcela_id !== null) {
    return `parcela-id:${referencia.nota_parcela_id}`;
  }

  if (referencia.id !== undefined && referencia.id !== null) {
    return `parcela-id:${referencia.id}`;
  }

  const numeroBase =
    referencia.parcela_numero ??
    referencia.numero ??
    referencia.parcelaNumero ??
    null;

  if (numeroBase !== null && numeroBase !== undefined && numeroBase !== '') {
    return `parcela-numero:${String(numeroBase)}`;
  }

  if (referencia.data_vencimento) {
    return `parcela-venc:${referencia.data_vencimento}`;
  }

  return 'parcela-unica';
};

const situacaoFinanceiraStyles = {
  sem_financeiro: 'bg-gray-200 text-gray-700 dark:bg-gray-800 dark:text-gray-300',
  classificacao_pendente: 'bg-warning-100 text-warning-700 dark:bg-warning-900/40 dark:text-warning-200',
  aguardando: 'bg-primary-100 text-primary-700 dark:bg-primary-900/40 dark:text-primary-200',
  lancado: 'bg-success-100 text-success-700 dark:bg-success-900/40 dark:text-success-200'
};

const situacaoFinanceiraIcons = {
  sem_financeiro: Ban,
  classificacao_pendente: AlertTriangle,
  aguardando: Clock,
  lancado: CheckCircle2
};

const situacaoFinanceiraLabel = {
  sem_financeiro: 'Sem efeito financeiro',
  classificacao_pendente: 'Classificação pendente',
  aguardando: 'Não lançada',
  lancado: 'Lançado'
};

const getSituacaoFinanceira = (nota) => {
  const geraFinanceiro = parseBooleanFlag(nota?.gera_financeiro, true);
  const motivoFinanceiro = nota?.motivo_financeiro ? String(nota.motivo_financeiro).trim() || null : null;

  if (!geraFinanceiro) {
    return {
      key: 'sem_financeiro',
      tooltip: motivoFinanceiro || 'Classificada como sem efeito financeiro.',
      tone: situacaoFinanceiraStyles.sem_financeiro,
      Icon: situacaoFinanceiraIcons.sem_financeiro,
      label: situacaoFinanceiraLabel.sem_financeiro
    };
  }

  const lancamentoIndicator = resolveLancamentoIndicator(nota);
  if (lancamentoIndicator === 'lancado') {
    return {
      key: 'lancado',
      tooltip: 'Lançamentos financeiros gerados para esta nota.',
      tone: situacaoFinanceiraStyles.lancado,
      Icon: situacaoFinanceiraIcons.lancado,
      label: situacaoFinanceiraLabel.lancado
    };
  }

  const cfop = nota?.cfop ? String(nota.cfop).trim() : '';
  const naturezaOperacao = nota?.natureza_operacao ? String(nota.natureza_operacao).trim() : '';
  const possuiClassificacao = Boolean(cfop || naturezaOperacao);
  if (!possuiClassificacao) {
    return {
      key: 'classificacao_pendente',
      tooltip: 'Defina o CFOP ou a natureza da operação para liberar a geração financeira.',
      tone: situacaoFinanceiraStyles.classificacao_pendente,
      Icon: situacaoFinanceiraIcons.classificacao_pendente,
      label: situacaoFinanceiraLabel.classificacao_pendente
    };
  }

  const valorTotal = Number(nota?.valor_total) || 0;
  const analiseParcelas = analisarParcelasNota(nota);
  const possuiParcelas = analiseParcelas.possuiParcelas;
  const possuiVencimentos = analiseParcelas.possuiVencimentos || Boolean(nota?.data_vencimento);
  const possuiParcelasComValor = analiseParcelas.possuiParcelasComValor;
  const possuiParcelasSemVencimento = analiseParcelas.possuiParcelasSemVencimento;

  const tooltipParts = [];
  if (!valorTotal) {
    tooltipParts.push('Informe o valor total da nota.');
  }
  if (!possuiParcelas) {
    tooltipParts.push('Adicione as parcelas ou um vencimento único.');
  } else {
    if (!possuiVencimentos) {
      tooltipParts.push('Preencha as datas de vencimento das parcelas.');
    }
    if (!possuiParcelasComValor && valorTotal > 0) {
      tooltipParts.push('Distribua o valor total entre as parcelas.');
    }
    if (possuiParcelasSemVencimento) {
      tooltipParts.push('Algumas parcelas ainda estão sem data de vencimento.');
    }
  }

  const tooltip = tooltipParts.length > 0
    ? tooltipParts.join(' ')
    : 'Nenhum lançamento financeiro foi gerado para esta nota.';

  return {
    key: 'aguardando',
    tooltip,
    tone: situacaoFinanceiraStyles.aguardando,
    Icon: situacaoFinanceiraIcons.aguardando,
    label: situacaoFinanceiraLabel.aguardando
  };
};

const getNotaSelectionKey = (nota) => {
  if (!nota) return null;
  if (nota.id != null) {
    return `id:${nota.id}`;
  }
  if (nota.numero != null) {
    return `numero:${nota.numero}`;
  }
  return null;
};

const avaliarNotaParaLancamento = (nota) => {
  const warnings = [];
  const errors = [];
  const geraFinanceiro = parseBooleanFlag(nota?.gera_financeiro, true);

  if (!geraFinanceiro) {
    warnings.push({ type: 'financeiro', message: 'Documento marcado como sem efeito financeiro.' });
  }

  const tipoOperacao = nota?.tipo_operacao || null;
  if (!tipoOperacao) {
    errors.push({ type: 'tipo_operacao', message: 'Defina se a nota é conta a pagar ou receber.' });
  }

  const analiseParcelas = analisarParcelasNota(nota);
  const parcelasNormalizadas = [];

  if (analiseParcelas.possuiParcelas) {
    analiseParcelas.parcelas.forEach((parcela, index) => {
      const numeroRef =
        parcela?.numero ??
        parcela?.parcela_numero ??
        parcela?.parcelaNumero ??
        index + 1;
      const valorNumero = parseCurrencyString(parcela?.valor ?? 0);
      const dataVencimento = parcela?.data_vencimento || null;

      if (!dataVencimento) {
        errors.push({ type: 'vencimento', message: `Parcela ${numeroRef} sem data de vencimento.` });
      }

      if (!Number.isFinite(valorNumero) || valorNumero <= 0) {
        errors.push({ type: 'valor', message: `Parcela ${numeroRef} sem valor informado.` });
      }

      parcelasNormalizadas.push({
        numero: numeroRef,
        data_vencimento: dataVencimento,
        valor: Number.isFinite(valorNumero) ? valorNumero : 0,
        raw: parcela
      });
    });
  } else {
    const valorTotalNumero = parseCurrencyString(nota?.valor_total ?? 0);
    if (nota?.data_vencimento) {
      if (!Number.isFinite(valorTotalNumero) || valorTotalNumero <= 0) {
        errors.push({ type: 'valor_total', message: 'Valor total da nota não informado.' });
      }

      parcelasNormalizadas.push({
        numero: 1,
        data_vencimento: nota.data_vencimento,
        valor: Number.isFinite(valorTotalNumero) ? valorTotalNumero : 0,
        fallback: true
      });
    } else {
      errors.push({
        type: 'parcelas',
        message: 'Informe ao menos um vencimento ou parcela para gerar os lançamentos.'
      });
    }
  }

  const indicadorLancamento = resolveLancamentoIndicator(nota);
  const lancamentosExistentes = Array.isArray(nota?.lancamentos) ? nota.lancamentos : [];
  if (indicadorLancamento === 'lancado' || lancamentosExistentes.length > 0) {
    warnings.push({
      type: 'duplicidade',
      message: 'Já existem lançamentos associados a este documento.'
    });
  }

  const valorTotalNumero = parseCurrencyString(nota?.valor_total ?? 0);

  return {
    geraFinanceiro,
    tipoOperacao,
    valorTotalNumero,
    parcelas: parcelasNormalizadas,
    warnings,
    errors,
    situacaoFinanceira: getSituacaoFinanceira(nota),
    indicadorLancamento,
    possuiLancamentos: lancamentosExistentes.length > 0
  };
};

const construirResumoMassivo = (notas) => {
  const itens = notas.map((nota) => {
    const avaliacao = avaliarNotaParaLancamento(nota);
    const empresaDescricao = describeEmpresaNota(nota);
    const documentoTipo = nota?.origem === 'xml_cte' ? 'CT-e' : 'NF-e';
    const key = getNotaSelectionKey(nota);
    const warningMessages = Array.from(new Set(avaliacao.warnings.map((warn) => warn.message)));
    const errorMessages = Array.from(new Set(avaliacao.errors.map((warn) => warn.message)));

    return {
      key,
      nota,
      avaliacao,
      documentoTipo,
      empresaDescricao,
      warningMessages,
      errorMessages,
      valorFormatado: formatCurrency(avaliacao.valorTotalNumero),
      valorTotalNumero: avaliacao.valorTotalNumero,
      tipoOperacaoLabel: tipoOperacaoLabel(nota?.tipo_operacao)
    };
  });

  const totais = itens.reduce(
    (acc, item) => {
      acc.valor += item.valorTotalNumero || 0;
      acc.parcelas += item.avaliacao.parcelas.length;
      if (item.nota?.tipo_operacao === 'pagar') {
        acc.pagar += item.valorTotalNumero || 0;
      } else if (item.nota?.tipo_operacao === 'receber') {
        acc.receber += item.valorTotalNumero || 0;
      }
      return acc;
    },
    { valor: 0, parcelas: 0, pagar: 0, receber: 0 }
  );

  const contagens = itens.reduce(
    (acc, item) => {
      if (item.warningMessages.length > 0) acc.warnings += item.warningMessages.length;
      if (item.errorMessages.length > 0) acc.errors += item.errorMessages.length;
      if (!item.avaliacao.geraFinanceiro) acc.semFinanceiro += 1;
      if (item.avaliacao.indicadorLancamento === 'lancado' || item.avaliacao.possuiLancamentos) {
        acc.jaLancados += 1;
      }
      if (item.errorMessages.length === 0) {
        acc.prontos += 1;
      } else {
        acc.bloqueados += 1;
      }
      return acc;
    },
    { warnings: 0, errors: 0, semFinanceiro: 0, jaLancados: 0, prontos: 0, bloqueados: 0 }
  );

  return { itens, totais, contagens };
};

const bulkFinanceStatusStyles = {
  apto: 'bg-success-100 text-success-700 dark:bg-success-900/35 dark:text-success-200',
  pendencias: 'bg-warning-100 text-warning-700 dark:bg-warning-900/35 dark:text-warning-200',
  sem_financeiro: situacaoFinanceiraStyles.sem_financeiro,
  indisponivel: 'bg-gray-200 text-gray-700 dark:bg-gray-800 dark:text-gray-300'
};

const bulkFinanceStatusIcons = {
  apto: CheckCircle2,
  pendencias: AlertTriangle,
  sem_financeiro: Ban,
  indisponivel: FileWarning
};

const getBulkImportFinanceStatus = ({
  nota,
  pendencias = [],
  geraFinanceiro = true,
  duplicateExisting = false,
  duplicateInBatch = false,
  status
}) => {
  if (!nota || status === 'error') {
    return {
      key: 'indisponivel',
      label: 'Análise indisponível',
      tone: bulkFinanceStatusStyles.indisponivel,
      Icon: bulkFinanceStatusIcons.indisponivel,
      tooltip: 'Não foi possível interpretar os dados financeiros deste arquivo.'
    };
  }

  if (!geraFinanceiro) {
    return {
      key: 'sem_financeiro',
      label: 'Sem efeito financeiro',
      tone: bulkFinanceStatusStyles.sem_financeiro,
      Icon: bulkFinanceStatusIcons.sem_financeiro,
      tooltip: 'Marcado como sem geração financeira.'
    };
  }

  const alertas = [];
  if (duplicateExisting) {
    alertas.push('Já existe uma nota equivalente salva no sistema.');
  }
  if (duplicateInBatch) {
    alertas.push('Outro arquivo importado contém esta mesma nota.');
  }

  if (pendencias.length > 0) {
    const tooltipParts = [...pendencias];
    if (alertas.length > 0) {
      tooltipParts.push(...alertas);
    }
    return {
      key: 'pendencias',
      label: 'Dados pendentes',
      tone: bulkFinanceStatusStyles.pendencias,
      Icon: bulkFinanceStatusIcons.pendencias,
      tooltip: tooltipParts.join(' • ')
    };
  }

  const tooltip =
    alertas.length > 0
      ? alertas.join(' ')
      : 'Todos os requisitos financeiros foram preenchidos.';

  return {
    key: 'apto',
    label: 'Apto ao lançamento financeiro',
    tone: bulkFinanceStatusStyles.apto,
    Icon: bulkFinanceStatusIcons.apto,
    tooltip
  };
};

const EmpresaInfoFallback = { id: null, nome: '', cnpj: '', email: '', telefone: '', endereco: '' };

const mapEmpresaInfo = (data) => ({
  id: data?.id ?? null,
  nome: data?.nome || data?.razao_social || '',
  cnpj: data?.cnpj || '',
  email: data?.email || '',
  telefone: data?.telefone || '',
  endereco: data?.endereco || ''
});

// ————————————————————————————————————————————————
// Helpers de formatação / resumo de parcelas
function getParcelasInfo(nota) {
  const lista = Array.isArray(nota.parcelas) ? nota.parcelas : [];
  if (lista.length > 0) {
    // Normaliza e ordena por data
    const norm = lista.map((p, i) => ({
      numero: p.numero ?? i + 1,
      data_vencimento: p.data_vencimento || null,
      valor: Number.isFinite(p.valor) ? p.valor : 0
    }));
    norm.sort((a, b) => (a.data_vencimento || '').localeCompare(b.data_vencimento || ''));
    const total = norm.reduce((acc, p) => acc + (p.valor || 0), 0);
    const proxima = norm.find(p => p.data_vencimento);
    return { kind: norm.length === 1 ? 'single' : 'multi', list: norm, total, next: proxima || null };
  }

  // fallback: “vencimento único” via campo da nota
  if (nota.data_vencimento) {
    return {
      kind: 'single',
      list: [{ numero: 1, data_vencimento: nota.data_vencimento, valor: Number(nota.valor_total) || 0 }],
      total: Number(nota.valor_total) || 0,
      next: { data_vencimento: nota.data_vencimento }
    };
  }

  return { kind: 'none', list: [], total: 0, next: null };
}

function Badge({ children, tone = 'neutral' }) {
  const tones = {
    neutral: 'bg-gray-100 text-gray-700 dark:bg-gray-800/70 dark:text-gray-200',
    green: 'bg-success-100 text-success-700 dark:bg-success-900/40 dark:text-success-200',
    blue: 'bg-primary-100 text-primary-700 dark:bg-primary-900/40 dark:text-primary-200'
  };
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-[11px] font-semibold ${tones[tone]}`}>
      {children}
    </span>
  );
}

function Pill({ date, value }) {
  return (
    <span className="inline-flex items-center gap-2 rounded-full border border-gray-300/60 dark:border-gray-700 px-2.5 py-1 text-xs">
      <span className="text-gray-600 dark:text-gray-300">{date}</span>
      <span className="font-mono tabular-nums font-semibold text-primary-600 dark:text-primary-400">{value}</span>
    </span>
  );
}

function ParcelasCell({ nota, formatDate, formatCurrency, compact = false }) {
  const listaBruta = Array.isArray(nota.parcelas) ? nota.parcelas : [];
  const lista = listaBruta.length
    ? listaBruta.map((p, i) => ({
        numero: p.numero ?? i + 1,
        data_vencimento: p.data_vencimento || null,
        valor: Number.isFinite(p.valor) ? p.valor : Number(nota.valor_total) || 0,
      }))
    : (nota.data_vencimento
        ? [{ numero: 1, data_vencimento: nota.data_vencimento, valor: Number(nota.valor_total) || 0 }]
        : []);

  let headerText = 'Sem parcelas';
  let headerTone = 'bg-gray-100 text-gray-700 dark:bg-gray-800/70 dark:text-gray-200';
  if (lista.length === 1) {
    headerText = 'Parcela única';
    headerTone = 'bg-success-100 text-success-700 dark:bg-success-900/40 dark:text-success-200';
  }
  if (lista.length > 1) {
    headerText = `${lista.length} parcelas`;
    headerTone = 'bg-primary-100 text-primary-700 dark:bg-primary-900/40 dark:text-primary-200';
  }

  // tamanhos “apertados”
  const wrapW = compact ? 'w-[240px] md:w-[260px]' : 'w-[300px]';
  const pad = compact ? 'px-2.5 py-2' : 'px-3 py-2.5';
  const gapX = compact ? 'gap-x-2' : 'gap-x-3';
  const valCls = compact
    ? 'text-center font-mono tabular-nums font-semibold text-sm md:text-base text-primary-600 dark:text-primary-400'
    : 'text-center font-mono tabular-nums font-semibold text-primary-600 dark:text-primary-400';
  const dateCls = compact
    ? 'text-center text-xs md:text-sm text-gray-700 dark:text-gray-200'
    : 'text-center text-sm text-gray-700 dark:text-gray-200';

  return (
    <div className="flex justify-center">
      <div className={`${wrapW} max-w-full`}>
        <div className="rounded-xl border border-gray-300/30 dark:border-gray-700 bg-gray-50/40 dark:bg-gray-800/30 relative">
          <div className="flex justify-center">
            <div className={`-mt-3 px-3 py-1 rounded-full text-[11px] font-semibold ${headerTone}`}>
              {headerText}
            </div>
          </div>

          <div className={pad}>
            {lista.length === 0 ? (
              <div className={`grid grid-cols-2 ${gapX}`}>
                <div className="text-center text-sm text-gray-500 dark:text-gray-400">—</div>
                <div className="text-center text-sm text-gray-500 dark:text-gray-400">—</div>
              </div>
            ) : (
              <>
                <div
                  className={`grid grid-cols-[minmax(0,1fr)_minmax(0,1fr)] ${gapX} items-center mb-1 text-[10px] md:text-[11px] uppercase tracking-wide text-gray-500 dark:text-gray-400`}
                >
                  <div className="text-center">Valor</div>
                  <div className="text-center">Vencimento</div>
                </div>

                <div
                  className={`grid grid-cols-[minmax(0,1fr)_minmax(0,1fr)] ${gapX} gap-y-1 items-start`}
                >
                  {lista.map((p, idx) => (
                    <Fragment key={`parcela-${idx}`}>
                      <div className={`${valCls} leading-tight`}>{formatCurrency(p.valor)}</div>
                      <div className={`${dateCls} leading-tight`}>
                        {p.data_vencimento ? formatDate(p.data_vencimento) : 'Sem vencimento'}
                      </div>
                    </Fragment>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}



export default function NotasFiscais() {
  const [notas, setNotas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [selectedNotaId, setSelectedNotaId] = useState(null);
  const [selectedNotaState, setSelectedNotaState] = useState(null);
  const [selectedNotaEditingField, setSelectedNotaEditingField] = useState(null);
  const [inlineEditDraft, setInlineEditDraft] = useState('');
  const [inlineEditOriginal, setInlineEditOriginal] = useState('');
  const [empresaInfo, setEmpresaInfo] = useState(EmpresaInfoFallback);
  const [importProcessing, setImportProcessing] = useState(false);
  const [importError, setImportError] = useState('');
  const [importedNota, setImportedNota] = useState(null);
  const [editingImportId, setEditingImportId] = useState(null);
  const [bulkImportItems, setBulkImportItems] = useState([]);
  const [isBulkPreviewOpen, setIsBulkPreviewOpen] = useState(false);
  const [bulkImportResult, setBulkImportResult] = useState(null);
  const [showBulkResultDetails, setShowBulkResultDetails] = useState(false);
  const [bulkImportFilters, setBulkImportFilters] = useState({
    tipo: 'todos',
    situacao: 'todas',
    financeiro: 'todos'
  });
  const [cfopClassificacoes, setCfopClassificacoes] = useState({});
  const [bulkSaving, setBulkSaving] = useState(false);
  const [globalOverlay, setGlobalOverlay] = useState(null);
  const [importRules, setImportRules] = useState([]);
  const [isRulesModalOpen, setIsRulesModalOpen] = useState(false);
  const [rulesLoading, setRulesLoading] = useState(false);
  const [rulesSubmitting, setRulesSubmitting] = useState(false);
  const [rulesBusyId, setRulesBusyId] = useState(null);
  const [notasFilters, setNotasFilters] = useState({
    search: '',
    tipoDocumento: 'todos',
    tipoOperacao: 'todas',
    financeiro: 'todos',
    situacao: 'todas'
  });
  const [selectedNotas, setSelectedNotas] = useState(() => new Set());
  const [massPreview, setMassPreview] = useState({ open: false, type: null, itens: [], resumo: null });
  const [forceGenerateSemFinanceiro, setForceGenerateSemFinanceiro] = useState(false);
  const [massActionProcessing, setMassActionProcessing] = useState(false);
  const [massActionFeedback, setMassActionFeedback] = useState(null);
  const selectAllCheckboxRef = useRef(null);
  const recentlyLaunchedRef = useRef(new Set());

  useEffect(() => {
    if (bulkImportItems.length === 0) {
      setIsBulkPreviewOpen(false);
    }
  }, [bulkImportItems.length]);

  useEffect(() => {
    setSelectedNotas((prev) => {
      if (!(prev instanceof Set)) {
        return new Set();
      }
      const validKeys = new Set(
        notas
          .map((nota) => getNotaSelectionKey(nota))
          .filter((key) => Boolean(key))
      );
      let changed = false;
      const next = new Set();
      prev.forEach((key) => {
        if (validKeys.has(key)) {
          next.add(key);
        } else {
          changed = true;
        }
      });
      return changed || next.size !== prev.size ? next : prev;
    });
  }, [notas]);

  const selectionKeys = useMemo(() => Array.from(selectedNotas), [selectedNotas]);

  const filteredNotas = useMemo(() => {
    const searchTerm = notasFilters.search.trim().toLowerCase();
    const tipoDocumentoFiltro = notasFilters.tipoDocumento;
    const tipoOperacaoFiltro = notasFilters.tipoOperacao;
    const financeiroFiltro = notasFilters.financeiro;
    const situacaoFiltro = notasFilters.situacao;

    return notas.filter((nota) => {
      if (searchTerm) {
        const camposBusca = [
          nota?.numero,
          nota?.chave_acesso,
          nota?.emitente?.nome,
          nota?.emitente?.fantasia,
          nota?.destinatario?.nome,
          nota?.destinatario?.fantasia,
          nota?.fornecedor,
          nota?.cfop,
          nota?.natureza_operacao
        ]
          .filter(Boolean)
          .map((valor) => String(valor).toLowerCase());

        const encontrou = camposBusca.some((campo) => campo.includes(searchTerm));
        if (!encontrou) {
          return false;
        }
      }

      if (tipoDocumentoFiltro !== 'todos') {
        const origem = String(nota?.origem || '').toLowerCase();
        const tipoDoc = origem.includes('cte') ? 'cte' : 'nfe';
        if (tipoDoc !== tipoDocumentoFiltro) {
          return false;
        }
      }

      if (tipoOperacaoFiltro !== 'todas') {
        const tipoNota = String(nota?.tipo_operacao || '').toLowerCase();
        if (tipoNota !== tipoOperacaoFiltro) {
          return false;
        }
      }

      if (financeiroFiltro !== 'todos') {
        const geraFinanceiro = parseBooleanFlag(nota?.gera_financeiro, true);
        if (financeiroFiltro === 'gera' && !geraFinanceiro) {
          return false;
        }
        if (financeiroFiltro === 'naoGera' && geraFinanceiro) {
          return false;
        }
      }

      if (situacaoFiltro !== 'todas') {
        const situacao = getSituacaoFinanceira(nota)?.key || 'aguardando';
        if (situacao !== situacaoFiltro) {
          return false;
        }
      }

      return true;
    });
  }, [notas, notasFilters.financeiro, notasFilters.search, notasFilters.situacao, notasFilters.tipoDocumento, notasFilters.tipoOperacao]);

  const filteredNotaKeys = useMemo(
    () =>
      filteredNotas
        .map((nota) => getNotaSelectionKey(nota))
        .filter((key) => Boolean(key)),
    [filteredNotas]
  );

  const filteredSelectedCount = useMemo(() => {
    if (filteredNotaKeys.length === 0) return 0;
    let count = 0;
    filteredNotaKeys.forEach((key) => {
      if (key && selectedNotas.has(key)) {
        count += 1;
      }
    });
    return count;
  }, [filteredNotaKeys, selectedNotas]);

  const allFilteredSelected = filteredNotaKeys.length > 0 && filteredSelectedCount === filteredNotaKeys.length;
  const someFilteredSelected = filteredSelectedCount > 0 && !allFilteredSelected;

  const selectedNotasList = useMemo(() => {
    if (selectionKeys.length === 0) return [];
    const validKeys = new Set(selectionKeys);
    return notas.filter((nota) => {
      const key = getNotaSelectionKey(nota);
      return key ? validKeys.has(key) : false;
    });
  }, [notas, selectionKeys]);

  const selectedCount = selectedNotasList.length;

  const filtrosAtivos = useMemo(
    () =>
      Boolean(
        notasFilters.search.trim() ||
          notasFilters.tipoDocumento !== 'todos' ||
          notasFilters.tipoOperacao !== 'todas' ||
          notasFilters.financeiro !== 'todos' ||
          notasFilters.situacao !== 'todas'
      ),
    [notasFilters]
  );
  const massPreviewResumo = massPreview.resumo || null;
  const massPreviewItens = massPreviewResumo?.itens || [];
  const massPreviewTotais = massPreviewResumo?.totais || { valor: 0, parcelas: 0, pagar: 0, receber: 0 };
  const massPreviewContagens = massPreviewResumo?.contagens || {
    warnings: 0,
    errors: 0,
    semFinanceiro: 0,
    jaLancados: 0,
    prontos: 0,
    bloqueados: 0
  };

  useEffect(() => {
    if (!selectAllCheckboxRef.current) return;
    selectAllCheckboxRef.current.indeterminate = someFilteredSelected;
  }, [someFilteredSelected, allFilteredSelected]);

  useEffect(() => {
    if (massPreview.open && selectedNotasList.length === 0) {
      setMassPreview({ open: false, type: null, itens: [], resumo: null });
      setMassActionFeedback(null);
      setForceGenerateSemFinanceiro(false);
    }
  }, [massPreview.open, selectedNotasList.length]);

  const handleNotaFilterChange = useCallback((campo, valor) => {
    setNotasFilters((prev) => ({ ...prev, [campo]: valor }));
  }, []);

  const handleClearNotasFilters = useCallback(() => {
    setNotasFilters({
      search: '',
      tipoDocumento: 'todos',
      tipoOperacao: 'todas',
      financeiro: 'todos',
      situacao: 'todas'
    });
  }, []);

  const handleNotaSelectionToggle = useCallback((nota) => {
    const key = getNotaSelectionKey(nota);
    if (!key) return;
    setSelectedNotas((prev) => {
      const base = prev instanceof Set ? new Set(prev) : new Set();
      if (base.has(key)) {
        base.delete(key);
      } else {
        base.add(key);
      }
      return base;
    });
  }, []);

  const handleToggleSelectAllFiltered = useCallback(() => {
    setSelectedNotas((prev) => {
      const base = prev instanceof Set ? new Set(prev) : new Set();
      if (allFilteredSelected) {
        filteredNotaKeys.forEach((key) => {
          if (key) {
            base.delete(key);
          }
        });
      } else {
        filteredNotaKeys.forEach((key) => {
          if (key) {
            base.add(key);
          }
        });
      }
      return base;
    });
  }, [allFilteredSelected, filteredNotaKeys]);

  const handleSelectFiltered = useCallback(() => {
    setSelectedNotas((prev) => {
      const base = prev instanceof Set ? new Set(prev) : new Set();
      filteredNotaKeys.forEach((key) => {
        if (key) {
          base.add(key);
        }
      });
      return base;
    });
  }, [filteredNotaKeys]);

  const handleClearSelection = useCallback(() => {
    setSelectedNotas(new Set());
  }, []);

  const openMassActionPreview = useCallback(
    (type) => {
      if (!type) return;
      if (selectedNotasList.length === 0) {
        alert('Selecione ao menos um documento para executar ações em massa.');
        return;
      }
      const resumo = construirResumoMassivo(selectedNotasList);
      setMassPreview({ open: true, type, itens: resumo.itens, resumo });
      setMassActionFeedback(null);
      setForceGenerateSemFinanceiro(false);
    },
    [selectedNotasList]
  );

  const closeMassActionPreview = useCallback(() => {
    setMassPreview({ open: false, type: null, itens: [], resumo: null });
    setMassActionFeedback(null);
    setForceGenerateSemFinanceiro(false);
  }, []);

  const loadNotas = useCallback(
    async ({ silent = false, empresaCnpj } = {}) => {
      if (!silent) {
        setLoading(true);
      }

      try {
        const data = await api.getNotas();
        const cnpjBase = empresaCnpj ?? empresaInfo?.cnpj;
        const normalizadas = (data || []).map((nota) => {
          const base = normalizeNotaFromApi(nota);
          if (!base.tipo_operacao && cnpjBase) {
            const tipo = inferTipoOperacao(base.emitente?.cnpj, base.destinatario?.cnpj, cnpjBase);
            if (tipo) {
              base.tipo_operacao = tipo;
            }
          }
          const notaKey =
            base.id != null
              ? `id:${base.id}`
              : base.numero
                ? `numero:${base.numero}`
                : null;
          if (notaKey && recentlyLaunchedRef.current.has(notaKey)) {
            base.indicador_lancamentos = 'lancado';
            base.lancamentos_status = 'lancado';
          } else if (notaKey && resolveLancamentoIndicator(base) === 'lancado') {
            recentlyLaunchedRef.current.delete(notaKey);
          }
          return base;
        });
        setNotas(normalizadas);
        return true;
      } catch (error) {
        console.error('Erro ao carregar notas:', error);
        if (!silent) {
          alert('Erro ao carregar notas fiscais');
        }
        setNotas([]);
        return false;
      } finally {
        if (!silent) {
          setLoading(false);
        }
      }
    },
    [empresaInfo?.cnpj]
  );

  const handleConfirmMassGenerate = useCallback(async () => {
    if (!massPreview.open || massPreview.type !== 'generate') {
      return;
    }
    const itens = Array.isArray(massPreview.itens) ? massPreview.itens : [];
    if (itens.length === 0) {
      return;
    }

    setMassActionProcessing(true);
    setGlobalOverlay({ message: 'Gerando lançamentos selecionados...' });
    const resultados = { gerados: 0, ignorados: [], erros: [], mensagens: [] };
    let houveSucesso = false;

    try {
      for (const item of itens) {
        const nota = item?.nota;
        if (!nota) continue;
        const avaliacao = item.avaliacao;

        if (!forceGenerateSemFinanceiro && !avaliacao.geraFinanceiro) {
          resultados.ignorados.push({
            nota,
            motivo: 'sem_financeiro',
            mensagens: ['Documento marcado como sem efeito financeiro.']
          });
          continue;
        }

        if (item.errorMessages.length > 0) {
          resultados.ignorados.push({
            nota,
            motivo: 'pendencias',
            mensagens: item.errorMessages
          });
          continue;
        }

        const resultado = await executeGenerateLancamentos(nota, { silent: true });
        if (resultado?.success) {
          resultados.gerados += 1;
          houveSucesso = true;
          if (resultado?.message) {
            resultados.mensagens.push({ nota, mensagem: resultado.message });
          } else if (item.warningMessages.length > 0) {
            resultados.mensagens.push({ nota, mensagem: item.warningMessages.join(' • ') });
          }
          continue;
        }

        if (resultado?.reason === 'parcelas_invalidas' || resultado?.reason === 'tipo_operacao') {
          resultados.ignorados.push({
            nota,
            motivo: 'validacao',
            mensagens: resultado?.message ? [resultado.message] : []
          });
          continue;
        }

        resultados.erros.push({
          nota,
          motivo: 'erro',
          mensagens: resultado?.message ? [resultado.message] : ['Não foi possível gerar lançamentos para este documento.']
        });
      }

      if (houveSucesso) {
        await loadNotas({ silent: true });
        setSelectedNotas(new Set());
      }

      setMassActionFeedback(resultados);
    } catch (error) {
      console.error('Erro na geração em massa de lançamentos:', error);
      resultados.erros.push({
        nota: null,
        motivo: 'erro',
        mensagens: [error?.message || 'Erro inesperado na geração em massa.']
      });
      setMassActionFeedback(resultados);
    } finally {
      setMassActionProcessing(false);
      setGlobalOverlay(null);
    }
  }, [executeGenerateLancamentos, forceGenerateSemFinanceiro, loadNotas, massPreview, setGlobalOverlay, setSelectedNotas]);

  const handleMassDeleteSelected = useCallback(async () => {
    if (selectedNotasList.length === 0) {
      alert('Selecione ao menos um documento para excluir.');
      return;
    }

    const confirmou = window.confirm(
      `Tem certeza que deseja excluir ${selectedNotasList.length} documento(s) selecionado(s)? Esta ação não pode ser desfeita.`
    );
    if (!confirmou) {
      return;
    }

    setMassActionProcessing(true);
    setGlobalOverlay({ message: 'Excluindo documentos selecionados...' });

    const erros = [];
    try {
      for (const nota of selectedNotasList) {
        try {
          if (nota.id) {
            await api.deleteNota(nota.id);
          }
        } catch (error) {
          console.error('Erro ao excluir nota em massa:', error);
          erros.push({ nota, error });
        }
      }

      if (erros.length > 0) {
        alert(`Alguns documentos não puderam ser excluídos (${erros.length}). Consulte o console para detalhes.`);
      } else {
        alert('Documentos selecionados excluídos com sucesso.');
      }

      setSelectedNotas(new Set());
      await loadNotas({ silent: true });
    } finally {
      setMassActionProcessing(false);
      setGlobalOverlay(null);
    }
  }, [loadNotas, selectedNotasList]);

  const handlePrintResumo = useCallback(() => {
    if (!massPreview.open || massPreview.type !== 'resumo' || !massPreview.resumo) {
      return;
    }

    const { resumo } = massPreview;
    const itens = Array.isArray(resumo?.itens) ? resumo.itens : [];
    const totais = resumo?.totais || { valor: 0, parcelas: 0, pagar: 0, receber: 0 };

    const linhas = itens
      .map((item) => {
        const numeroDocumento = item.nota?.numero ? `nº ${item.nota.numero}` : 'Sem número';
        const proximoVencimento = item.avaliacao.parcelas.find((parcela) => parcela.data_vencimento)?.data_vencimento || '';
        const avisos = [...item.warningMessages, ...item.errorMessages];
        return `
          <tr>
            <td>${item.documentoTipo} ${numeroDocumento}</td>
            <td>${item.empresaDescricao?.nome || ''}</td>
            <td>${formatCurrency(item.avaliacao.valorTotalNumero)}</td>
            <td>${item.tipoOperacaoLabel || ''}</td>
            <td>${item.avaliacao.parcelas.length}</td>
            <td>${proximoVencimento ? formatDate(proximoVencimento) : '-'}</td>
            <td>${avisos.length > 0 ? avisos.join(' • ') : '-'}</td>
          </tr>
        `;
      })
      .join('');

    const html = `<!DOCTYPE html>
      <html lang="pt-BR">
        <head>
          <meta charSet="utf-8" />
          <title>Resumo de lançamentos</title>
          <style>
            body { font-family: 'Segoe UI', system-ui, -apple-system, sans-serif; padding: 24px; color: #0f172a; }
            h1 { font-size: 20px; margin-bottom: 8px; }
            p { margin: 4px 0 16px; color: #475569; }
            table { width: 100%; border-collapse: collapse; margin-top: 16px; }
            th, td { border: 1px solid #cbd5f5; padding: 8px; font-size: 12px; text-align: left; }
            th { background: #e2e8f0; text-transform: uppercase; letter-spacing: 0.05em; }
            tfoot td { font-weight: bold; background: #f8fafc; }
          </style>
        </head>
        <body>
          <h1>Resumo dos lançamentos selecionados</h1>
          <p>Total de documentos: ${itens.length}</p>
          <table>
            <thead>
              <tr>
                <th>Documento</th>
                <th>Empresa</th>
                <th>Valor</th>
                <th>Operação</th>
                <th>Parcelas</th>
                <th>Próximo vencimento</th>
                <th>Alertas</th>
              </tr>
            </thead>
            <tbody>
              ${linhas || '<tr><td colspan="7">Nenhum documento selecionado.</td></tr>'}
            </tbody>
            <tfoot>
              <tr>
                <td colspan="2">Totais</td>
                <td>${formatCurrency(totais.valor)}</td>
                <td>${formatCurrency(totais.receber - totais.pagar)}</td>
                <td>${totais.parcelas}</td>
                <td colspan="2">A pagar: ${formatCurrency(totais.pagar)} | A receber: ${formatCurrency(totais.receber)}</td>
              </tr>
            </tfoot>
          </table>
        </body>
      </html>`;

    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      alert('Não foi possível abrir a janela de impressão. Verifique o bloqueador de pop-ups.');
      return;
    }
    printWindow.document.write(html);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
    }, 250);
  }, [massPreview]);

  useEffect(() => {
    const shouldLockScroll =
      (isBulkPreviewOpen && bulkImportItems.length > 0) || Boolean(bulkImportResult) || Boolean(globalOverlay);

    if (shouldLockScroll) {
      const previousOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = previousOverflow;
      };
    }

    return undefined;
  }, [isBulkPreviewOpen, bulkImportItems.length, bulkImportResult, globalOverlay]);
  const [isDragOver, setIsDragOver] = useState(false);
  const importCounterRef = useRef(0);
  const fileInputRef = useRef(null);
  const [formData, setFormData] = useState(initialManualForm);
  const [formParcelas, setFormParcelas] = useState([]);
  const [generatingLancamentoId, setGeneratingLancamentoId] = useState(null);
  const [deletingNotaId, setDeletingNotaId] = useState(null);
  const [showFinanceiroOverride, setShowFinanceiroOverride] = useState(false);
  const [notaParaOverride, setNotaParaOverride] = useState(null);
  const [selectedNotaDirty, setSelectedNotaDirty] = useState(false);
  const [savingNotaId, setSavingNotaId] = useState(null);
  const normalizeCfopFn = useMemo(
    () => financialClassificationUtils?.normalizeCfop ?? fallbackNormalizeCfop,
    []
  );
  const formatCfopFn = useMemo(
    () => financialClassificationUtils?.formatCfop ?? fallbackFormatCfop,
    []
  );
  const loadImportRules = useCallback(async () => {
    setRulesLoading(true);
    try {
      const regras = await api.getNotaImportRules();
      setImportRules(Array.isArray(regras) ? regras : []);
    } catch (error) {
      console.error('Erro ao carregar regras de importação de notas:', error);
      setImportRules([]);
    } finally {
      setRulesLoading(false);
    }
  }, []);
  const savingSelectedNota =
    selectedNotaState?.id != null && savingNotaId != null
      ? String(savingNotaId) === String(selectedNotaState.id)
      : false;
  const canPersistSelectedNota = selectedNotaState
    ? selectedNotaState.id
      ? selectedNotaDirty && !savingSelectedNota
      : selectedNotaDirty
    : false;
  const importedNotaAnalise = importedNota ? analisarParcelasNota(importedNota) : null;
  const importedNotaFinanceiroAtivo = importedNota ? parseBooleanFlag(importedNota.gera_financeiro, true) : false;
  const importedNotaSemVencimento = importedNotaFinanceiroAtivo && importedNota ? !notaPossuiAlgumVencimento(importedNota) : false;
  const importedNotaParcelasSemData = Boolean(importedNotaAnalise?.possuiParcelas && importedNotaAnalise.possuiParcelasSemVencimento);
  const importedNotaContatoEhDestinatario =
    importedNota?.tipo_operacao === 'receber' ||
    (!importedNota?.tipo_operacao && !importedNota?.emitente?.nome && Boolean(importedNota?.destinatario?.nome));
  const importedNotaContatoLabel = importedNotaContatoEhDestinatario ? 'Nome do destinatário' : 'Nome do emitente';
  const importedNotaContatoNome = importedNotaContatoEhDestinatario
    ? importedNota?.destinatario?.nome || ''
    : importedNota?.emitente?.nome || '';
  const selectedNotaAnalise = selectedNotaState ? analisarParcelasNota(selectedNotaState) : null;
  const selectedNotaFinanceiroAtivo = selectedNotaState ? parseBooleanFlag(selectedNotaState.gera_financeiro, true) : false;
  const selectedNotaParcelasInfo = selectedNotaState ? getParcelasInfo(selectedNotaState) : null;
  const selectedNotaSemVencimento = selectedNotaFinanceiroAtivo && !selectedNotaParcelasInfo?.next?.data_vencimento;
  const selectedNotaParcelasSemData = Boolean(selectedNotaAnalise?.possuiParcelas && selectedNotaAnalise.possuiParcelasSemVencimento);
  const selectedNotaSituacaoFinanceira = selectedNotaState ? getSituacaoFinanceira(selectedNotaState) : null;
  const selectedNotaIdentity = useMemo(() => {
    if (!selectedNotaState) return null;
    if (selectedNotaState.id != null) {
      return `id:${selectedNotaState.id}`;
    }
    if (selectedNotaState.numero != null) {
      return `numero:${selectedNotaState.numero}`;
    }
    return null;
  }, [selectedNotaState?.id, selectedNotaState?.numero]);
  const getCfopDescricao = useCallback(
    (cfop) => {
      const normalizado = normalizeCfopFn(cfop);
      if (!normalizado) return null;
      return cfopClassificacoes[normalizado] || null;
    },
    [cfopClassificacoes, normalizeCfopFn]
  );
  const selectedNotaCfopCodigo = selectedNotaState ? selectedNotaState.cfop ?? null : null;
  const selectedNotaCfopInfo = useMemo(() => {
    if (!selectedNotaCfopCodigo) return null;
    return getCfopDescricao(selectedNotaCfopCodigo);
  }, [getCfopDescricao, selectedNotaCfopCodigo]);
  const selectedNotaCfopFormatado = selectedNotaCfopCodigo
    ? selectedNotaCfopInfo?.cfop_formatado || formatCfopFn(selectedNotaCfopCodigo)
    : '';
  const selectedNotaClassificacaoDescricao = selectedNotaCfopInfo?.descricao?.trim() || '';

  useEffect(() => {
    loadImportRules();
  }, [loadImportRules]);

  useEffect(() => {
    if (!selectedNotaEditingField) {
      setInlineEditDraft('');
      setInlineEditOriginal('');
      return;
    }

    const valorAtual = selectedNotaState
      ? Object.prototype.hasOwnProperty.call(selectedNotaState, selectedNotaEditingField)
        ? selectedNotaState[selectedNotaEditingField]
        : ''
      : '';
    const baseValue = valorAtual === null || valorAtual === undefined ? '' : String(valorAtual);
    setInlineEditOriginal(baseValue);
    setInlineEditDraft(baseValue);
  }, [selectedNotaEditingField, selectedNotaState]);

  useEffect(() => {
    const initialize = async () => {
      setLoading(true);
      let info = { ...EmpresaInfoFallback };

      try {
        const empresa = await api.getEmpresaConfig();
        info = { ...EmpresaInfoFallback, ...mapEmpresaInfo(empresa) };
        setEmpresaInfo(info);
      } catch (error) {
        console.error('Erro ao carregar dados da empresa para notas fiscais:', error);
        setEmpresaInfo({ ...EmpresaInfoFallback });
      }

      const sucesso = await loadNotas({ silent: true, empresaCnpj: info.cnpj });
      if (!sucesso) {
        alert('Erro ao carregar notas fiscais');
      }
      setLoading(false);
    };

    initialize();
  }, []);

  useEffect(() => {
    let ativo = true;

    const carregarClassificacoes = async () => {
      try {
        const resposta =
          typeof api.getCfopClassificacoes === 'function' ? await api.getCfopClassificacoes() : [];
        if (!ativo) return;

        const mapa = {};
        (Array.isArray(resposta) ? resposta : []).forEach((item) => {
          const codigoBruto =
            item?.cfop_codigo || item?.cfop || item?.cfopFormatado || item?.cfop_formatado;
          const normalizado = normalizeCfopFn(codigoBruto);
          if (!normalizado) {
            return;
          }

          mapa[normalizado] = {
            descricao: item?.descricao || '',
            cfop_formatado: item?.cfop_formatado || formatCfopFn(normalizado),
            gera_financeiro:
              item?.gera_financeiro === null || item?.gera_financeiro === undefined
                ? null
                : Boolean(item?.gera_financeiro),
            motivo: item?.motivo || '',
          };
        });

        setCfopClassificacoes(mapa);
      } catch (error) {
        console.error('Falha ao carregar classificações de CFOP:', error);
      }
    };

    carregarClassificacoes();

    return () => {
      ativo = false;
    };
  }, [normalizeCfopFn, formatCfopFn]);

  useEffect(() => {
    if (!selectedNotaId) {
      setSelectedNotaState(null);
      setSelectedNotaDirty(false);
      setSelectedNotaEditingField(null);
      return;
    }

    const notaAtual = notas.find((nota) => nota.id === selectedNotaId || nota.numero === selectedNotaId);
    if (!notaAtual) {
      setSelectedNotaState(null);
      setSelectedNotaDirty(false);
      setSelectedNotaEditingField(null);
      return;
    }

    const notaIdentity =
      notaAtual.id != null ? `id:${notaAtual.id}` : notaAtual.numero != null ? `numero:${notaAtual.numero}` : null;
    const isSameNota = Boolean(selectedNotaIdentity && notaIdentity && selectedNotaIdentity === notaIdentity);

    if (selectedNotaDirty && isSameNota) {
      return;
    }

    if (isSameNota && selectedNotaEditingField) {
      return;
    }

    setSelectedNotaState(JSON.parse(JSON.stringify(notaAtual)));
    setSelectedNotaDirty(false);
    if (!isSameNota) {
      setSelectedNotaEditingField(null);
    }
  }, [selectedNotaId, notas, selectedNotaDirty, selectedNotaIdentity, selectedNotaEditingField]);

  useEffect(() => {
    if (!empresaInfo?.cnpj) return;
    setNotas((prev) =>
      prev.map((nota) => {
        if (nota.tipo_operacao) return nota;
        const tipo = inferTipoOperacao(nota.emitente?.cnpj, nota.destinatario?.cnpj, empresaInfo.cnpj);
        return tipo ? { ...nota, tipo_operacao: tipo } : nota;
      })
    );
  }, [empresaInfo.cnpj]);

  const handleCreateImportRule = useCallback(
    async (data) => {
      setRulesSubmitting(true);
      try {
        const created = await api.createNotaImportRule(data);
        if (created) {
          await loadImportRules();
          alert('Regra criada com sucesso!');
          return true;
        }
      } catch (error) {
        console.error('Erro ao criar regra de importação:', error);
        alert(error?.message || 'Não foi possível criar a regra de importação.');
      } finally {
        setRulesSubmitting(false);
      }
      return false;
    },
    [loadImportRules]
  );

  const handleUpdateImportRule = useCallback(
    async (id, data) => {
      if (!id) return false;
      setRulesBusyId(id);
      setRulesSubmitting(true);
      try {
        const updated = await api.updateNotaImportRule(id, data);
        if (updated) {
          await loadImportRules();
          alert('Regra atualizada com sucesso!');
          return true;
        }
      } catch (error) {
        console.error('Erro ao atualizar regra de importação:', error);
        alert(error?.message || 'Não foi possível atualizar a regra de importação.');
      } finally {
        setRulesSubmitting(false);
        setRulesBusyId(null);
      }
      return false;
    },
    [loadImportRules]
  );

  const handleDeleteImportRule = useCallback(
    async (id) => {
      if (!id) return false;
      setRulesBusyId(id);
      setRulesSubmitting(true);
      try {
        const removed = await api.deleteNotaImportRule(id);
        if (removed) {
          await loadImportRules();
          alert('Regra removida com sucesso!');
          return true;
        }
      } catch (error) {
        console.error('Erro ao remover regra de importação:', error);
        alert(error?.message || 'Não foi possível remover a regra de importação.');
      } finally {
        setRulesSubmitting(false);
        setRulesBusyId(null);
      }
      return false;
    },
    [loadImportRules]
  );

  const handleDuplicateImportRule = useCallback(
    async (rule) => {
      if (!rule || typeof rule !== 'object') {
        return false;
      }

      const payload = {
        nome: rule.nome ? `${rule.nome} (cópia)` : 'Regra duplicada',
        prioridade: Number(rule.prioridade) || 0,
        is_active: rule.is_active !== false,
        conditions: Array.isArray(rule.conditions)
          ? rule.conditions.map((condition) => ({
              field: condition.field,
              operator: condition.operator,
              value: condition.value
            }))
          : [],
        actions: Array.isArray(rule.actions)
          ? rule.actions.map((action) => ({
              type: action.type,
              params: action.params ? { ...action.params } : {}
            }))
          : []
      };

      if (rule.id !== undefined && rule.id !== null) {
        setRulesBusyId(rule.id);
      }
      setRulesSubmitting(true);

      try {
        const created = await api.createNotaImportRule(payload);
        if (created) {
          await loadImportRules();
          alert('Regra duplicada com sucesso!');
          return true;
        }
      } catch (error) {
        console.error('Erro ao duplicar regra de importação:', error);
        alert(error?.message || 'Não foi possível duplicar a regra de importação.');
      } finally {
        setRulesSubmitting(false);
        setRulesBusyId(null);
      }

      return false;
    },
    [loadImportRules]
  );

  const handleSubmitManual = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        numero: formData.numero,
        fornecedor:
          formData.tipo_operacao === 'pagar'
            ? formData.emitente_nome || formData.destinatario_nome
            : formData.destinatario_nome || formData.emitente_nome,
        valor_total: parseCurrencyString(formData.valor_total),
        data_emissao: formData.data_emissao,
        observacoes: formData.observacoes || '',
        tipo_operacao: formData.tipo_operacao,
        data_vencimento: formData.data_vencimento,
        parcelas: formParcelas,
        empresa_id: empresaInfo.id,
        cfop: formData.cfop || null,
        natureza_operacao: formData.natureza_operacao || null,
        gera_financeiro:
          formData.gera_financeiro === null || formData.gera_financeiro === undefined
            ? true
            : Boolean(formData.gera_financeiro),
        motivo_financeiro: formData.motivo_financeiro || null,
        metadata: {
          emitente: {
            nome: formData.emitente_nome,
            cnpj: formData.emitente_cnpj
          },
          destinatario: {
            nome: formData.destinatario_nome,
            cnpj: formData.destinatario_cnpj
          },
          parcelas: formParcelas,
          pagamentos: [],
          faturas: [],
          origem: 'manual',
          cfop: formData.cfop || null,
          natureza_operacao: formData.natureza_operacao || null,
          gera_financeiro:
            formData.gera_financeiro === null || formData.gera_financeiro === undefined
              ? true
              : Boolean(formData.gera_financeiro),
          motivo_financeiro: formData.motivo_financeiro || null
        }
      };

      let permitirDuplicado = false;
      const duplicada = findDuplicateNota(notas, {
        numero: payload.numero,
        chave_acesso: payload.metadata?.chave_acesso
      });

      if (duplicada) {
        const confirmaDuplicata = confirm(
          `A nota fiscal ${payload.numero} já foi cadastrada. Deseja registrar novamente mesmo assim?`
        );
        if (!confirmaDuplicata) {
          return;
        }
        permitirDuplicado = true;
      }

      if (permitirDuplicado) {
        payload.permitir_duplicado = true;
        payload.allowDuplicate = true;
        payload.metadata = {
          ...payload.metadata,
          permitir_duplicado: true,
          allowDuplicate: true
        };
      }

      await api.createNota(payload);
      const sucesso = await loadNotas({ silent: true });
      if (!sucesso) {
        alert('Nota criada, mas houve erro ao recarregar a listagem. Atualize a página.');
      }

      setShowModal(false);
      setFormData(initialManualForm);
      setFormParcelas([]);
    } catch (error) {
      console.error('Erro ao salvar nota manual:', error);
      alert(error?.message || 'Erro ao salvar nota fiscal manualmente');
    }
  };

  const handleDelete = async (id) => {
    const nota = notas.find((item) => item.id === id || item.numero === id);
    if (!nota) return false;

    const identificador = nota.numero ? `NF ${nota.numero}` : 'este documento';
    const confirmou = confirm(`Tem certeza que deseja excluir ${identificador}?`);
    if (!confirmou) {
      return false;
    }

    const notaKey = nota.id != null ? nota.id : nota.numero;
    setDeletingNotaId(notaKey);
    const tipoLabel = nota.origem === 'xml_cte' ? 'CT-e' : 'nota fiscal';
    setGlobalOverlay({ message: `Excluindo ${tipoLabel}...` });

    try {
      if (nota.id) {
        await api.deleteNota(nota.id);
      }

      setNotas((prev) =>
        prev.filter((item) => {
          if (nota.id) {
            return item.id !== nota.id;
          }
          return item.numero !== nota.numero;
        })
      );
      if (selectedNotaId === (nota.id || nota.numero)) {
        setSelectedNotaId(null);
      }
      setSelectedNotaDirty(false);
      const sucessoRemocao = await loadNotas({ silent: true });
      if (!sucessoRemocao) {
        alert('Nota removida, mas houve erro ao recarregar a listagem. Atualize a página.');
      }
      return true;
    } catch (error) {
      console.error('Erro ao deletar nota no servidor:', error);
      alert('Não foi possível excluir esta nota fiscal. Tente novamente.');
      return false;
    } finally {
      setGlobalOverlay(null);
      setDeletingNotaId(null);
    }
  };

  const handlePersistNota = async () => {
    if (!selectedNotaState) return;

    const notaAtual = selectedNotaState;

    if (!notaAtual.id) {
      alert('Não foi possível localizar esta nota no banco de dados. Atualize a página e tente novamente.');
      return;
    }

    if (!selectedNotaDirty) {
      return;
    }

    const geraFinanceiro = parseBooleanFlag(notaAtual.gera_financeiro, true);
    if (geraFinanceiro) {
      const analise = analisarParcelasNota(notaAtual);
      const possuiVencimentoUnico = Boolean(notaAtual.data_vencimento);
      if (!analise.possuiVencimentos && !possuiVencimentoUnico) {
        const confirmar = confirm('Esta nota está sem data de vencimento. Deseja salvar mesmo assim?');
        if (!confirmar) {
          return;
        }
      } else if (analise.possuiParcelasSemVencimento) {
        const confirmar = confirm('Existem parcelas sem data de vencimento nas parcelas. Deseja continuar mesmo assim?');
        if (!confirmar) {
          return;
        }
      }
    }

    const payload = buildNotaPayloadForApi(notaAtual);
    if (!payload) return;
    payload.empresa_id = notaAtual.empresa_id || empresaInfo.id;

    const duplicada = findDuplicateNota(notas, payload, notaAtual.id);
    if (duplicada) {
      const confirma = confirm(
        `Já existe outra nota com o número ${payload.numero}. Deseja manter assim mesmo?`
      );
      if (!confirma) {
        return;
      }
    }

    try {
      setSavingNotaId(notaAtual.id);
      const resposta = await api.updateNota(notaAtual.id, payload);
      const { success: _success, ...notaSalva } = resposta || {};
      const notaComDados = {
        ...notaSalva,
        id: notaSalva.id ?? notaAtual.id,
        empresa_id: notaSalva.empresa_id ?? notaAtual.empresa_id ?? payload.empresa_id ?? null,
        numero: notaSalva.numero ?? payload.numero,
        fornecedor: notaSalva.fornecedor ?? notaAtual.fornecedor ?? '',
        data_emissao: notaSalva.data_emissao ?? payload.data_emissao,
        data_vencimento: notaSalva.data_vencimento ?? payload.data_vencimento,
        valor_total: notaSalva.valor_total ?? payload.valor_total,
        observacoes: notaSalva.observacoes ?? payload.observacoes,
        tipo_operacao: notaSalva.tipo_operacao ?? payload.tipo_operacao,
        origem: notaSalva.origem ?? payload.origem,
        status: notaSalva.status ?? payload.status,
        emitente: notaSalva.emitente ?? notaAtual.emitente,
        destinatario: notaSalva.destinatario ?? notaAtual.destinatario,
        chave_acesso: notaSalva.chave_acesso ?? payload.chave_acesso,
        parcelas: Array.isArray(notaSalva.parcelas) ? notaSalva.parcelas : payload.parcelas,
        pagamentos: Array.isArray(notaSalva.pagamentos) ? notaSalva.pagamentos : payload.pagamentos,
        faturas: Array.isArray(notaSalva.faturas) ? notaSalva.faturas : payload.faturas
      };

      setNotas((prev) =>
        prev.map((nota) => (nota.id === notaAtual.id ? { ...nota, ...notaComDados } : nota))
      );
      const snapshot = JSON.parse(JSON.stringify(notaComDados));
      setSelectedNotaState(snapshot);
      setSelectedNotaDirty(false);
      const sucessoAtualizacao = await loadNotas({ silent: true });
      if (!sucessoAtualizacao) {
        alert('Alterações salvas, mas houve erro ao recarregar a listagem. Atualize a página.');
      }
    } catch (error) {
      console.error('Erro ao atualizar nota fiscal:', error);
      alert('Não foi possível salvar as alterações desta nota fiscal.');
    } finally {
      setSavingNotaId(null);
    }
  };

  async function executeGenerateLancamentos(nota, { silent = false } = {}) {
    const parcelas = Array.isArray(nota.parcelas) && nota.parcelas.length > 0
      ? nota.parcelas
      : [
          {
            numero: 1,
            data_vencimento: nota.data_vencimento,
            valor: nota.valor_total
          }
        ];
    const parcelasInvalidas = parcelas.filter((parcela) => !parcela.data_vencimento || !parcela.valor);
    const resultadoBase = {
      success: false,
      reason: null,
      message: '',
      createdCount: 0,
      skippedDuplicates: []
    };
    if (parcelasInvalidas.length > 0) {
      const message = 'Preencha data de vencimento e valor para todas as parcelas antes de gerar os lançamentos.';
      if (!silent) {
        alert(message);
      }
      return { ...resultadoBase, reason: 'parcelas_invalidas', message };
    }

    if (!nota.tipo_operacao) {
      const message = 'Defina se a nota é uma conta a pagar ou a receber antes de gerar os lançamentos.';
      if (!silent) {
        alert(message);
      }
      return { ...resultadoBase, reason: 'tipo_operacao', message };
    }

    setGeneratingLancamentoId(nota.id || nota.numero || 'local');
    const overlayMessage =
      nota.origem === 'xml_cte' ? 'Criando lançamentos do CT-e...' : 'Criando lançamento(s)...';
    if (!silent) {
      setGlobalOverlay({ message: overlayMessage });
    }

    try {
      // Determinar fornecedor ou cliente com base no tipo da nota
      const resolvedFornecedor =
        nota.emitente?.nome ||
        nota.emitente?.fantasia ||
        nota.fornecedor ||
        nota.destinatario?.nome ||
        nota.destinatario?.fantasia ||
        '';

      const resolvedCliente =
        nota.destinatario?.nome ||
        nota.destinatario?.fantasia ||
        nota.fornecedor ||
        nota.emitente?.nome ||
        nota.emitente?.fantasia ||
        '';

      const tipoDoc = nota.origem === 'xml_cte' ? 'CT-e' : 'NF';
      const fornecedor = nota.tipo_operacao === 'pagar' ? resolvedFornecedor || 'Fornecedor não identificado' : null;
      const cliente = nota.tipo_operacao === 'receber' ? resolvedCliente || 'Cliente não identificado' : null;
      const empresaEntidade = nota.tipo_operacao === 'pagar' ? fornecedor : cliente;
      const observacoesBase = `Gerado de ${tipoDoc} ${nota.numero || ''}${empresaEntidade ? ' - ' + empresaEntidade : ''}`;

      const existingLancamentos = Array.isArray(nota.lancamentos) ? nota.lancamentos : [];
      const existingLancamentoKeys = new Set(
        existingLancamentos.map((item) => buildLancamentoDuplicateKey(item))
      );
      const skippedDuplicates = [];
      const createdPlaceholders = [];
      let createdCount = 0;

      const describeParcela = (parcela) => {
        if (!parcela || typeof parcela !== 'object') return '';
        const numeroRef =
          parcela.numero ??
          parcela.parcela_numero ??
          parcela.parcelaNumero ??
          null;
        if (numeroRef !== null && numeroRef !== undefined && numeroRef !== '') {
          return `parcela ${numeroRef}`;
        }
        if (parcela.data_vencimento) {
          return `vencimento ${formatDate(parcela.data_vencimento)}`;
        }
        return 'parcela';
      };

      for (const parcela of parcelas) {
        const duplicateKey = buildLancamentoDuplicateKey({
          nota_parcela_id: parcela?.id ?? parcela?.nota_parcela_id ?? null,
          parcela_numero: parcela?.numero ?? parcela?.parcela_numero ?? parcela?.parcelaNumero ?? null,
          data_vencimento: parcela?.data_vencimento || null
        });

        if (existingLancamentoKeys.has(duplicateKey)) {
          skippedDuplicates.push({ parcela, reason: 'existing' });
          continue;
        }

        const payload = {
          tipo: nota.tipo_operacao === 'pagar' ? 'despesa' : 'receita',
          descricao: `${tipoDoc} ${nota.numero || 's/n'} - Parcela ${parcela.numero}`,
          valor: parcela.valor,
          data_vencimento: parcela.data_vencimento,
          status: 'pendente',
          categoria: nota.origem === 'xml_cte' ? 'Transporte' : (nota.tipo_operacao === 'pagar' ? 'Fornecedores' : 'Clientes'),
          fornecedor: fornecedor,
          cliente: cliente,
          nota_fiscal_id: nota.id ?? null,
          nota_numero: nota.numero ?? null,
          nota_parcela_id: parcela?.id ?? null,
          parcela_numero: parcela?.numero ?? null,
          observacoes: observacoesBase
        };

        try {
          await api.createLancamento(payload);
          createdCount += 1;
          existingLancamentoKeys.add(duplicateKey);
          createdPlaceholders.push({
            id: null,
            nota_parcela_id: parcela?.id ?? null,
            parcela_numero: parcela?.numero ?? null,
            data_vencimento: parcela?.data_vencimento || null,
            valor: parseCurrencyString(parcela?.valor),
            status: 'pendente'
          });
        } catch (error) {
          if (error?.status === 409) {
            console.warn('Lançamento ignorado para evitar duplicidade:', error);
            existingLancamentoKeys.add(duplicateKey);
            skippedDuplicates.push({ parcela, reason: 'api', message: error.message, duplicate: error.payload?.duplicate });
            continue;
          }
          throw error;
        }
      }

      const shouldMarkLaunched = createdCount > 0 || skippedDuplicates.length > 0;
      const notaKey = nota.id != null ? `id:${nota.id}` : nota.numero ? `numero:${nota.numero}` : null;
      if (notaKey && createdCount > 0) {
        recentlyLaunchedRef.current.add(notaKey);
      }

      if (notaKey && shouldMarkLaunched) {
        setNotas((prev) =>
          prev.map((item) => {
            if (!notaKey) return item;
            if (item.id != null && nota.id != null) {
              if (item.id !== nota.id) return item;
            } else if (!(nota.numero && item.numero && String(item.numero) === String(nota.numero))) {
              return item;
            }

            const lancamentosExistentesItem = Array.isArray(item.lancamentos) ? item.lancamentos : [];
            const lancamentosAtualizados =
              createdPlaceholders.length > 0
                ? [...lancamentosExistentesItem, ...createdPlaceholders]
                : lancamentosExistentesItem;

            return {
              ...item,
              indicador_lancamentos: 'lancado',
              lancamentos_status: 'lancado',
              lancamentos: lancamentosAtualizados
            };
          })
        );
      }

      setSelectedNotaState((prev) => {
        if (!prev || !notaKey) return prev;
        const matchesId = nota.id != null && prev.id != null && String(prev.id) === String(nota.id);
        const matchesNumero = !matchesId && nota.numero && prev.numero && String(prev.numero) === String(nota.numero);
        if (!matchesId && !matchesNumero) {
          return prev;
        }

        const lancamentosExistentesPrev = Array.isArray(prev.lancamentos) ? prev.lancamentos : [];
        const lancamentosAtualizados =
          createdPlaceholders.length > 0
            ? [...lancamentosExistentesPrev, ...createdPlaceholders]
            : lancamentosExistentesPrev;

        if (!shouldMarkLaunched) {
          return {
            ...prev,
            lancamentos: lancamentosAtualizados
          };
        }

        return {
          ...prev,
          indicador_lancamentos: 'lancado',
          lancamentos_status: 'lancado',
          lancamentos: lancamentosAtualizados
        };
      });

      let sucessoRecarregar = true;
      if (shouldMarkLaunched && !silent) {
        sucessoRecarregar = await loadNotas({ silent: true });
        if (!sucessoRecarregar) {
          console.warn('Lançamentos gerados, mas não foi possível atualizar a listagem automaticamente.');
        }
      }

      const duplicatesSummary = Array.from(
        new Set(
          skippedDuplicates
            .map((item) => describeParcela(item.parcela))
            .filter((texto) => Boolean(texto))
        )
      ).join(', ');

      let feedbackMessage = 'Lançamentos gerados com sucesso!';
      if (createdCount === 0 && skippedDuplicates.length > 0) {
        feedbackMessage = duplicatesSummary
          ? `Nenhum lançamento novo foi gerado porque ${duplicatesSummary} já possui lançamentos registrados.`
          : 'Nenhum lançamento novo foi gerado porque já existem lançamentos financeiros registrados para esta nota ou CT-e.';
      } else if (skippedDuplicates.length > 0) {
        feedbackMessage = duplicatesSummary
          ? `Lançamentos gerados com sucesso! As seguintes parcelas já possuíam lançamentos e foram mantidas: ${duplicatesSummary}.`
          : 'Lançamentos gerados com sucesso! Algumas parcelas já possuíam lançamentos e foram mantidas.';
      }

      if (!silent) {
        alert(feedbackMessage);
      }
      return {
        ...resultadoBase,
        success: createdCount > 0 || skippedDuplicates.length > 0,
        createdCount,
        skippedDuplicates,
        message: feedbackMessage
      };
    } catch (error) {
      console.error('Erro ao gerar lançamentos:', error);
      const message = 'Erro ao gerar lançamentos para esta nota.';
      if (!silent) {
        alert(message);
      }
      return { ...resultadoBase, reason: 'exception', message, error };
    } finally {
      setGeneratingLancamentoId(null);
      if (!silent) {
        setGlobalOverlay(null);
      }
    }
  }

  const buildPayloadFromNota = (nota, { allowDuplicate = false } = {}) => {
    if (!nota) return null;

    const metadata = buildMetadataFromNota(nota);

    const payload = {
      numero: nota.numero,
      fornecedor:
        nota.tipo_operacao === 'pagar'
          ? nota.emitente?.nome || nota.emitente?.cnpj || ''
          : nota.destinatario?.nome || nota.destinatario?.cnpj || '',
      valor_total: nota.valor_total,
      data_emissao: nota.data_emissao,
      observacoes: nota.observacoes || '',
      tipo_operacao: nota.tipo_operacao,
      data_vencimento: nota.data_vencimento,
      parcelas: Array.isArray(nota.parcelas) ? nota.parcelas : [],
      empresa_id: empresaInfo.id,
      metadata,
      cfop: nota.cfop || metadata?.cfop || null,
      natureza_operacao: nota.natureza_operacao || metadata?.natureza_operacao || null,
      gera_financeiro:
        nota.gera_financeiro === null || nota.gera_financeiro === undefined
          ? metadata?.gera_financeiro ?? null
          : Boolean(nota.gera_financeiro),
      motivo_financeiro: nota.motivo_financeiro || metadata?.motivo_financeiro || null,
      origem: nota.origem || metadata?.origem || 'xml'
    };

    if (nota.chave_acesso) {
      payload.chave_acesso = nota.chave_acesso;
    }

    if (nota.faturas) {
      payload.faturas = nota.faturas;
    }

    if (nota.pagamentos) {
      payload.pagamentos = nota.pagamentos;
    }

    if (allowDuplicate) {
      payload.permitir_duplicado = true;
      payload.allowDuplicate = true;
      payload.metadata = {
        ...payload.metadata,
        permitir_duplicado: true,
        permitirDuplicado: true
      };
    }

    return payload;
  };

  const handleImportedFiles = async (files) => {
    const lista = Array.from(files || []).filter((file) => file && file.name && file.name.toLowerCase().endsWith('.xml'));
    if (lista.length === 0) {
      return;
    }

    setImportProcessing(true);
    setImportError('');

    const novosItens = [];
    let erros = 0;

    try {
      for (const file of lista) {
        importCounterRef.current += 1;
        const itemId = `import-${Date.now()}-${importCounterRef.current}`;

        try {
          const texto = await file.text();
          let notaProcessada = parseXmlNota(texto, empresaInfo?.cnpj);

          if (!notaProcessada) {
            throw new Error('Não foi possível interpretar o arquivo selecionado.');
          }

          if (!notaProcessada.tipo_operacao && empresaInfo?.cnpj) {
            const tipoInferido = inferTipoOperacao(
              notaProcessada.emitente?.cnpj,
              notaProcessada.destinatario?.cnpj,
              empresaInfo.cnpj
            );
            if (tipoInferido) {
              notaProcessada.tipo_operacao = tipoInferido;
            }
          }

          const contextoRegra = {
            empresa: empresaInfo ? { ...empresaInfo } : null,
            arquivo: { nome: file.name },
            tipoDocumento: notaProcessada?.origem === 'xml_cte' ? 'cte' : 'nfe'
          };
          const resultadoRegras = applyNotaImportRules(notaProcessada, importRules, contextoRegra, {
            parseCurrencyString,
            parseBooleanFlag,
            sanitizeCnpj,
            inferTipoOperacao
          });
          notaProcessada = { ...resultadoRegras.nota, arquivo_nome: file.name };
          const notaClonada = JSON.parse(JSON.stringify(notaProcessada));

          novosItens.push({
            id: itemId,
            fileName: file.name,
            nota: notaClonada,
            status: 'ready',
            selected: undefined,
            allowDuplicate: false,
            appliedImportRules: resultadoRegras.appliedRules,
            importRuleAdjustments: resultadoRegras.adjustments,
            importRuleChangedFields: resultadoRegras.changedFields
          });
        } catch (error) {
          console.error('Erro ao importar nota:', error);
          erros += 1;
          novosItens.push({
            id: itemId,
            fileName: file.name,
            status: 'error',
            error: error?.message || 'Não foi possível importar o arquivo selecionado.',
            selected: false
          });
        }
      }

      setBulkImportItems((prev) => {
        const combinados = [...prev, ...novosItens];
        return enhanceBulkImportItems(combinados, notas, { preserveSelection: true });
      });
      setBulkImportResult(null);
      setShowBulkResultDetails(false);
      setIsBulkPreviewOpen(true);

      if (lista.length === 1) {
        const itemEditavel = novosItens.find((item) => item.status === 'ready' && item.nota);
        if (itemEditavel) {
          setEditingImportId(itemEditavel.id);
          setImportedNota(JSON.parse(JSON.stringify(itemEditavel.nota)));
        }
      }

      if (erros > 0) {
        setImportError(
          erros === lista.length
            ? 'Nenhum dos arquivos enviados pôde ser processado. Verifique os XMLs e tente novamente.'
            : 'Alguns arquivos apresentaram erros e foram sinalizados na pré-visualização.'
        );
      }
    } catch (error) {
      console.error('Falha ao processar arquivos:', error);
      setImportError(error?.message || 'Falha ao processar os arquivos selecionados.');
    } finally {
      setImportProcessing(false);
      setIsDragOver(false);
    }
  };

  const handleCancelImportEdit = () => {
    setImportedNota(null);
    setEditingImportId(null);
  };

  const handleApplyImportEdits = () => {
    if (!importedNota) return;

    const contextoRegra = {
      empresa: empresaInfo ? { ...empresaInfo } : null,
      tipoDocumento: importedNota?.origem === 'xml_cte' ? 'cte' : 'nfe'
    };
    const resultadoRegras = applyNotaImportRules(importedNota, importRules, contextoRegra, {
      parseCurrencyString,
      parseBooleanFlag,
      sanitizeCnpj,
      inferTipoOperacao
    });
    const notaReaplicada = {
      ...resultadoRegras.nota,
      arquivo_nome: resultadoRegras.nota?.arquivo_nome || importedNota.arquivo_nome || 'Nota importada'
    };

    const analise = analisarParcelasNota(notaReaplicada);
    const geraFinanceiro = parseBooleanFlag(notaReaplicada.gera_financeiro, true);
    const possuiVencimentoUnico = Boolean(notaReaplicada.data_vencimento);

    if (geraFinanceiro) {
      if (!analise.possuiVencimentos && !possuiVencimentoUnico) {
        const confirmar = confirm('Esta nota está sem data de vencimento. Deseja manter assim na prévia?');
        if (!confirmar) {
          return;
        }
      } else if (analise.possuiParcelasSemVencimento) {
        const confirmar = confirm('Existem parcelas sem data de vencimento. Deseja manter assim na prévia?');
        if (!confirmar) {
          return;
        }
      }
    }

    if (editingImportId) {
      setBulkImportItems((prev) => {
        const atualizados = prev.map((item) =>
          item.id === editingImportId
            ? {
                ...item,
                nota: JSON.parse(JSON.stringify(notaReaplicada)),
                status: item.status === 'error' ? 'ready' : item.status,
                appliedImportRules: resultadoRegras.appliedRules,
                importRuleAdjustments: resultadoRegras.adjustments,
                importRuleChangedFields: resultadoRegras.changedFields
              }
            : item
        );
        return enhanceBulkImportItems(atualizados, notas, { preserveSelection: true });
      });
      handleCancelImportEdit();
      return;
    }

    importCounterRef.current += 1;
    const novoId = `import-${Date.now()}-${importCounterRef.current}`;
    setBulkImportItems((prev) =>
      enhanceBulkImportItems(
        [
          ...prev,
          {
            id: novoId,
            fileName: notaReaplicada.arquivo_nome || importedNota.arquivo_nome || 'Nota importada',
            nota: JSON.parse(JSON.stringify(notaReaplicada)),
            status: 'ready',
            selected: undefined,
            allowDuplicate: false,
            appliedImportRules: resultadoRegras.appliedRules,
            importRuleAdjustments: resultadoRegras.adjustments,
            importRuleChangedFields: resultadoRegras.changedFields
          }
        ],
        notas,
        { preserveSelection: true }
      )
    );
    setBulkImportResult(null);
    setShowBulkResultDetails(false);
    setIsBulkPreviewOpen(true);
    handleCancelImportEdit();
  };

  const beginInlineEdit = (field, value) => {
    const baseValue = value === null || value === undefined ? '' : String(value);
    setInlineEditOriginal(baseValue);
    setInlineEditDraft(baseValue);
    setSelectedNotaEditingField(field);
  };

  const finalizeInlineEdit = (field) => {
    const draftValue = inlineEditDraft ?? '';
    const originalValue = inlineEditOriginal ?? '';
    if (draftValue === originalValue) {
      setInlineEditDraft('');
      setInlineEditOriginal('');
      setSelectedNotaEditingField(null);
      return;
    }

    atualizarNotaSelecionada({ [field]: draftValue });
    setInlineEditDraft('');
    setInlineEditOriginal('');
    setSelectedNotaEditingField(null);
  };

  const cancelInlineEdit = () => {
    setInlineEditDraft('');
    setInlineEditOriginal('');
    setSelectedNotaEditingField(null);
  };

  const renderInlineEditableField = (field, { label, value, displayValue, placeholder, type = 'text', inputMode }) => {
    const isEditing = selectedNotaEditingField === field;
    const hasValue = value !== null && value !== undefined && String(value).trim() !== '';
    const shownValue = hasValue ? displayValue || value : placeholder;

    return (
      <div className="space-y-1">
        <p className="text-xs uppercase font-semibold text-gray-500 dark:text-gray-400">{label}</p>
        {isEditing ? (
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
            <input
              type={type}
              inputMode={inputMode}
              className="input-field flex-1"
              value={inlineEditDraft}
              onChange={(event) => setInlineEditDraft(event.target.value)}
              autoFocus
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault();
                  finalizeInlineEdit(field);
                }
                if (event.key === 'Escape') {
                  event.preventDefault();
                  cancelInlineEdit();
                }
              }}
            />
            <div className="flex items-center gap-1">
              <button
                type="button"
                className="px-3 py-2 rounded-lg text-xs font-semibold text-white bg-primary-600 hover:bg-primary-500 dark:bg-primary-500 dark:hover:bg-primary-400 transition-colors"
                onClick={() => finalizeInlineEdit(field)}
              >
                Salvar
              </button>
              <button
                type="button"
                className="px-3 py-2 rounded-lg text-xs font-semibold text-gray-600 hover:text-gray-800 dark:text-gray-300 dark:hover:text-white hover:bg-gray-100/70 dark:hover:bg-gray-800/50 transition-colors"
                onClick={cancelInlineEdit}
              >
                Cancelar
              </button>
            </div>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => beginInlineEdit(field, value)}
            className="w-full text-left px-3 py-2 rounded-xl border border-gray-200/70 dark:border-gray-700/60 bg-white/60 dark:bg-gray-900/40 hover:border-primary-300 hover:bg-primary-50/40 dark:hover:bg-primary-900/30 transition-colors"
          >
            <div
              className={`text-sm font-semibold break-words ${
                hasValue ? 'text-gray-900 dark:text-white' : 'text-gray-500 dark:text-gray-400'
              }`}
            >
              {shownValue}
            </div>
            <div className="text-[11px] text-gray-400 dark:text-gray-500 mt-1">Clique para editar</div>
          </button>
        )}
      </div>
    );
  };

  const handleToggleImportSelection = (itemId, checked) => {
    const alvo = bulkImportItems.find((item) => item.id === itemId);
    if (!alvo) return;
    if (alvo.status === 'error' && checked) return;

    let allowDuplicate = alvo.allowDuplicate;
    if (checked && (alvo.duplicateExisting || alvo.duplicateInBatch) && !allowDuplicate) {
      const mensagem = alvo.duplicateExisting
        ? 'Esta nota já existe no sistema. Deseja selecioná-la mesmo assim para salvar novamente?'
        : 'Outro arquivo importado possui os mesmos dados desta nota. Deseja selecioná-la mesmo assim?';
      const confirmar = window.confirm(mensagem);
      if (!confirmar) {
        return;
      }
      allowDuplicate = true;
    }

    setBulkImportItems((prev) => {
      const atualizados = prev.map((item) =>
        item.id === itemId
          ? {
              ...item,
              selected: checked,
              allowDuplicate: allowDuplicate || item.allowDuplicate
            }
          : item
      );
      return enhanceBulkImportItems(atualizados, notas, { preserveSelection: true });
    });
  };

  const handleBulkFilterChange = (key, value) => {
    setBulkImportFilters((prev) => ({ ...prev, [key]: value }));
  };

  const handleRemoveImportItem = (itemId) => {
    setBulkImportItems((prev) => {
      const filtrados = prev.filter((item) => item.id !== itemId);
      return enhanceBulkImportItems(filtrados, notas, { preserveSelection: true });
    });
    if (editingImportId === itemId) {
      handleCancelImportEdit();
    }
  };

  const handleBulkSaveSelected = async () => {
    const prontos = bulkImportItems.filter((item) => item.status !== 'error' && item.nota);
    const selecionados = prontos.filter((item) => item.selected);

    if (selecionados.length === 0) {
      alert('Selecione ao menos uma nota válida para salvar.');
      return;
    }

    setBulkSaving(true);
    setShowBulkResultDetails(false);
    setBulkImportResult(null);
    setGlobalOverlay({ message: 'Salvando documentos selecionados...' });

    const resultados = {
      totalSelecionadas: selecionados.length,
      salvas: 0,
      duplicadas: 0,
      falhas: 0,
      erros: []
    };

    const novosRegistros = [];
    const idsSalvos = new Set();

    try {
      for (const item of selecionados) {
        const payload = buildPayloadFromNota(item.nota, { allowDuplicate: item.allowDuplicate });
        try {
          const resposta = await api.createNota(payload);
          resultados.salvas += 1;
          idsSalvos.add(item.id);
          if (resposta) {
            try {
              novosRegistros.push(normalizeNotaFromApi(resposta));
            } catch (error) {
              console.warn('Falha ao normalizar nota importada:', error);
            }
          }
        } catch (error) {
          console.error('Erro ao salvar nota importada do lote:', error);
          resultados.falhas += 1;
          const mensagem = error?.message || 'Erro desconhecido ao salvar nota.';
          if (mensagem.toLowerCase().includes('duplic')) {
            resultados.duplicadas += 1;
          }
          resultados.erros.push({
            id: item.id,
            numero: item.nota?.numero || 's/n',
            mensagem
          });
        }
      }

      if (resultados.salvas > 0) {
        await loadNotas({ silent: true });
      }

      setBulkImportItems((prev) => {
        const atualizados = prev.map((item) => {
          if (idsSalvos.has(item.id)) {
            return { ...item, status: 'saved', selected: false };
          }
          if (selecionados.some((sel) => sel.id === item.id)) {
            return { ...item, selected: false };
          }
          return item;
        });
        const existentesAtualizados = [...notas, ...novosRegistros];
        return enhanceBulkImportItems(atualizados, existentesAtualizados, { preserveSelection: true });
      });

      const resumo = {
        ...resultados,
        ignoradas: prontos.length - selecionados.length
      };

      setBulkImportResult({
        summary: resumo,
        savedNotas: novosRegistros
      });
      setIsBulkPreviewOpen(false);
    } finally {
      setBulkSaving(false);
      setGlobalOverlay(null);
    }
  };

  const closeBulkImportResult = () => {
    setBulkImportResult(null);
    setShowBulkResultDetails(false);
  };

  const handleViewSavedNota = (nota) => {
    if (!nota) return;
    closeBulkImportResult();
    const key = nota.id != null ? nota.id : nota.numero;
    if (key) {
      setSelectedNotaId(key);
    }
  };

  const handleDeleteSavedNota = async (nota) => {
    if (!nota) return;
    const key = nota.id != null ? nota.id : nota.numero;
    if (!key) return;
    const sucesso = await handleDelete(key);
    if (sucesso) {
      setBulkImportResult((prev) => {
        if (!prev) return prev;
        const atualizadas = prev.savedNotas.filter((item) => {
          const itemKey = item.id != null ? item.id : item.numero;
          return itemKey !== key;
        });
        return {
          summary: {
            ...prev.summary,
            salvas: atualizadas.length
          },
          savedNotas: atualizadas
        };
      });
    }
  };

  const filteredBulkImportItems = useMemo(() => {
    const { tipo, situacao, financeiro } = bulkImportFilters;
    return bulkImportItems.filter((item) => {
      if (tipo !== 'todos') {
        if (tipo === 'nfe' && item.tipoDocumento !== 'nfe') return false;
        if (tipo === 'cte' && item.tipoDocumento !== 'cte') return false;
      }

      if (situacao === 'pendencias' && !(item.possuiPendencias || item.status === 'error')) {
        return false;
      }
      if (situacao === 'sem_pendencias' && (item.possuiPendencias || item.status === 'error')) {
        return false;
      }
      if (situacao === 'duplicados' && !(item.duplicateExisting || item.duplicateInBatch)) {
        return false;
      }
      if (situacao === 'erros' && item.status !== 'error') {
        return false;
      }

      if (financeiro === 'gera' && !item.geraFinanceiro) {
        return false;
      }
      if (financeiro === 'naoGera' && item.geraFinanceiro) {
        return false;
      }

      return true;
    });
  }, [bulkImportFilters, bulkImportItems]);

  const bulkImportStats = useMemo(() => {
    const prontos = bulkImportItems.filter((item) => item.status !== 'error' && item.nota);
    const selecionados = prontos.filter((item) => item.selected).length;
    const pendencias = prontos.filter((item) => item.possuiPendencias).length;
    const duplicados = prontos.filter((item) => item.duplicateExisting || item.duplicateInBatch).length;
    const erros = bulkImportItems.filter((item) => item.status === 'error').length;
    const salvos = bulkImportItems.filter((item) => item.status === 'saved').length;

    return {
      total: bulkImportItems.length,
      prontos: prontos.length,
      selecionados,
      pendencias,
      duplicados,
      erros,
      salvos
    };
  }, [bulkImportItems]);

  const handleGenerateLancamentos = async (nota, { force = false } = {}) => {
    if (!nota) return;
    const geraFinanceiro = parseBooleanFlag(nota.gera_financeiro, true);
    const lancamentoIndicator = resolveLancamentoIndicator(nota);
    const lancamentosExistentes = Array.isArray(nota.lancamentos) ? nota.lancamentos : [];

    if (lancamentoIndicator === 'lancado' || lancamentosExistentes.length > 0) {
      const confirma = window.confirm(
        'Já existem lançamentos financeiros associados a esta nota ou CT-e. Gerar novamente pode resultar em duplicidades. Deseja continuar mesmo assim?'
      );
      if (!confirma) {
        return;
      }
    }

    if (!force && !geraFinanceiro) {
      setNotaParaOverride(nota);
      setShowFinanceiroOverride(true);
      return;
    }
    await executeGenerateLancamentos(nota);
  };

  const cancelarOverrideFinanceiro = () => {
    setShowFinanceiroOverride(false);
    setNotaParaOverride(null);
  };

  const confirmarOverrideFinanceiro = async () => {
    if (!notaParaOverride) {
      cancelarOverrideFinanceiro();
      return;
    }
    const notaAlvo = notaParaOverride;
    cancelarOverrideFinanceiro();
    await executeGenerateLancamentos(notaAlvo);
  };

  

  const handleEmpresaChange = (campo, valor) => {
    setEmpresaInfo((prev) => ({ ...prev, [campo]: valor }));
    if (campo === 'cnpj' && importedNota && !importedNota.tipo_operacao) {
      const tipo = inferTipoOperacao(importedNota.emitente?.cnpj, importedNota.destinatario?.cnpj, valor);
      if (tipo) {
        setImportedNota((prev) => ({ ...prev, tipo_operacao: tipo }));
      }
    }
  };

  const closeDrawer = () => {
    setSelectedNotaId(null);
    setSelectedNotaEditingField(null);
  };

  const atualizarNotaSelecionada = (updater) => {
    setSelectedNotaState((prev) => {
      if (!prev) return prev;
      const atualizado = typeof updater === 'function' ? updater(prev) : { ...prev, ...updater };
      setSelectedNotaDirty(true);
      return { ...atualizado };
    });
  };

  const adicionarParcelaImportada = () => {
    setImportedNota((prev) => {
      if (!prev) return prev;
      const novaParcela = {
        numero: (prev.parcelas?.length || 0) + 1,
        data_vencimento: '',
        valor: 0
      };
      return {
        ...prev,
        parcelas: [...(prev.parcelas || []), novaParcela]
      };
    });
  };

  const removerParcelaImportada = (index) => {
    setImportedNota((prev) => {
      if (!prev) return prev;
      const novas = [...(prev.parcelas || [])];
      novas.splice(index, 1);
      return {
        ...prev,
        parcelas: novas
      };
    });
  };

  const adicionarParcelaManual = () => {
    setFormParcelas((prev) => [
      ...prev,
      {
        numero: prev.length + 1,
        data_vencimento: '',
        valor: ''
      }
    ]);
  };

  const atualizarParcelaManual = (index, campo, valor) => {
    setFormParcelas((prev) =>
      prev.map((parcela, idx) =>
        idx === index
          ? {
              ...parcela,
              [campo]: campo === 'valor' ? valor : valor
            }
          : parcela
      )
    );
  };

  const removerParcelaManual = (index) => {
    setFormParcelas((prev) => prev.filter((_, idx) => idx !== index));
  };

  const totalParcelasManual = formParcelas.reduce((acc, parcela) => acc + parseCurrencyString(parcela.valor), 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="spinner h-12 w-12 mx-auto"></div>
          <p className="mt-4 text-gray-600 dark:text-gray-400">Carregando notas fiscais...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Notas Fiscais</h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            Importe e gerencie notas fiscais eletrônicas com geração automática de lançamentos
          </p>
        </div>
        <div className="flex flex-wrap gap-3">
          <button onClick={() => setShowModal(true)} className="btn-primary flex items-center gap-2">
            <Type className="w-5 h-5" />
            Cadastro manual
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4">
        <div className="card p-5 space-y-6">
          <div className="space-y-1.5">
            <div className="flex flex-wrap items-center gap-3">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Importação em lote de XML</h2>
              <button
                type="button"
                onClick={() => setIsRulesModalOpen(true)}
                className="inline-flex items-center gap-2 rounded-lg border border-gray-300 dark:border-gray-700 px-3 py-1.5 text-xs font-semibold text-gray-700 dark:text-gray-200 hover:border-primary-400 hover:text-primary-600 dark:hover:border-primary-500 dark:hover:text-primary-300 transition-colors"
              >
                <Settings className="w-4 h-4" />
                <span className="hidden sm:inline">Regras de importação</span>
              </button>
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Arraste vários XMLs de NF-e ou CT-e ao mesmo tempo, revise pendências e escolha quais notas salvar.
            </p>
          </div>

          <div
            className={`transition border-2 border-dashed rounded-2xl px-5 py-6 text-left flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between ${
              isDragOver
                ? 'border-primary-500 bg-primary-50/40 dark:bg-primary-900/20'
                : 'border-gray-200 dark:border-gray-700 bg-gray-50/40 dark:bg-gray-900/20'
            } ${importProcessing ? 'opacity-70' : ''}`}
            onDragEnter={(event) => {
              event.preventDefault();
              setIsDragOver(true);
            }}
            onDragOver={(event) => {
              event.preventDefault();
              setIsDragOver(true);
            }}
            onDragLeave={(event) => {
              if (!event.currentTarget.contains(event.relatedTarget)) {
                setIsDragOver(false);
              }
            }}
            onDrop={(event) => {
              event.preventDefault();
              const arquivos = Array.from(event.dataTransfer?.files || []).filter((file) =>
                file.name.toLowerCase().endsWith('.xml')
              );
              if (arquivos.length > 0) {
                handleImportedFiles(arquivos);
              }
              setIsDragOver(false);
            }}
            onClick={(event) => {
              if (event.target === event.currentTarget) {
                fileInputRef.current?.click();
              }
            }}
          >
            <div className="flex items-start gap-3">
              <div className="p-2.5 rounded-full bg-primary-100 text-primary-600 dark:bg-primary-900/40 dark:text-primary-200">
                <Upload className="w-5 h-5" />
              </div>
              <div className="space-y-1">
                <p className="text-sm font-semibold text-gray-900 dark:text-white">
                  Selecione ou arraste os XMLs para iniciar a importação
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400 leading-relaxed">
                  O sistema identifica duplicidades, sinaliza pendências obrigatórias e permite ajustar os dados antes de salvar
                  as notas.
                </p>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3">
              <button
                type="button"
                className="btn-secondary inline-flex items-center gap-2"
                onClick={() => fileInputRef.current?.click()}
                disabled={importProcessing}
              >
                <Upload className="w-4 h-4" />
                {importProcessing ? 'Processando...' : 'Selecionar arquivos'}
              </button>
              {importError && (
                <div className="text-xs text-danger-600 dark:text-danger-300 flex items-center gap-1.5">
                  <AlertTriangle className="w-4 h-4" />
                  {importError}
                </div>
              )}
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept=".xml"
              multiple
              className="hidden"
              onChange={(event) => {
                const arquivos = Array.from(event.target.files || []).filter((file) =>
                  file.name.toLowerCase().endsWith('.xml')
                );
                if (arquivos.length > 0) {
                  handleImportedFiles(arquivos);
                }
                event.target.value = '';
              }}
            />
        </div>

        {!isBulkPreviewOpen && bulkImportItems.length > 0 && (
          <div className="border border-primary-200 dark:border-primary-900/60 bg-primary-50/50 dark:bg-primary-900/20 rounded-2xl p-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Pré-visualização em andamento</h3>
              <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                {bulkImportStats.selecionados > 0
                  ? `${bulkImportStats.selecionados} documento(s) selecionado(s) aguardando salvamento.`
                  : 'Revise os documentos importados e selecione quais deseja salvar no sistema.'}
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              <span className="inline-flex items-center gap-1 px-3 py-1 text-xs font-semibold rounded-full bg-white/70 dark:bg-gray-900/60 text-primary-700 dark:text-primary-200 border border-primary-200/70 dark:border-primary-800/60">
                <Layers className="w-3.5 h-3.5" /> {bulkImportStats.total} arquivo(s)
              </span>
              <button type="button" className="btn-secondary" onClick={() => setIsBulkPreviewOpen(true)}>
                Reabrir pré-visualização
              </button>
            </div>
          </div>
        )}

        {importedNota && bulkImportItems.length === 0 && (
          <div className="border border-primary-200 dark:border-primary-800 bg-primary-50/60 dark:bg-primary-900/30 rounded-2xl p-5 space-y-5">
            <div className="flex justify-between items-start gap-3">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                    <span className="text-2xl">{importedNota.origem === 'xml_cte' ? '🚚' : '📄'}</span>
                    Revisando {importedNota.origem === 'xml_cte' ? 'CT-e' : 'NF-e'} importado
                  </h3>
                  {editingImportId && (
                    <p className="text-xs text-gray-600 dark:text-gray-400 mt-0.5">
                      As alterações serão refletidas na linha correspondente da pré-visualização em lote.
                    </p>
                  )}
                  {!editingImportId && (
                    <p className="text-xs text-gray-600 dark:text-gray-400 mt-0.5">
                      Salve esta prévia para adicioná-la à lista de importação em lote.
                    </p>
                  )}
                </div>
                <button className="icon-button" onClick={handleCancelImportEdit}>
                  <X className="w-4 h-4" />
                </button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {importedNotaFinanceiroAtivo && (importedNotaSemVencimento || importedNotaParcelasSemData) && (
                  <div className="md:col-span-2 rounded-2xl border border-warning-400 bg-warning-50/90 dark:bg-warning-900/40 p-4 flex gap-3">
                    <div className="pt-1">
                      <AlertTriangle className={`w-5 h-5 text-warning-600 ${importedNotaSemVencimento ? 'animate-pulse' : ''}`} />
                    </div>
                    <div className="space-y-1">
                      <p className="text-sm font-semibold text-warning-800 dark:text-warning-100">Pendências para gerar lançamentos</p>
                      <ul className="list-disc space-y-1 text-xs text-warning-700 dark:text-warning-100/80 ml-4">
                        {importedNotaSemVencimento && <li>Informe a data de vencimento para liberar a geração financeira.</li>}
                        {importedNotaParcelasSemData && <li>Revise as parcelas sem data de vencimento preenchendo cada uma delas.</li>}
                      </ul>
                    </div>
                  </div>
                )}
                <div>
                  <label className="text-xs uppercase font-semibold text-gray-500 dark:text-gray-400">Número</label>
                  <div className="flex items-center gap-2 mt-1">
                    <Hash className="w-4 h-4 text-primary-500" />
                    <input
                      type="text"
                      className="input-field"
                      value={importedNota.numero || ''}
                      onChange={(event) => setImportedNota((prev) => ({ ...prev, numero: event.target.value }))}
                    />
                  </div>
                </div>
                <div>
                  <label className="text-xs uppercase font-semibold text-gray-500 dark:text-gray-400">Data de emissão</label>
                  <div className="flex items-center gap-2 mt-1">
                    <Calendar className="w-4 h-4 text-primary-500" />
                    <input
                      type="date"
                      className="input-field"
                      value={importedNota.data_emissao || ''}
                      onChange={(event) => setImportedNota((prev) => ({ ...prev, data_emissao: event.target.value }))}
                    />
                  </div>
                </div>
                <div>
                  <label className="text-xs uppercase font-semibold text-gray-500 dark:text-gray-400">Data de vencimento</label>
                  <div className="flex items-center gap-2 mt-1">
                    <Clock className={`w-4 h-4 ${importedNotaSemVencimento ? 'text-warning-600 animate-pulse' : 'text-primary-500'}`} />
                    <input
                      type="date"
                      className={`input-field ${importedNotaSemVencimento ? 'border-warning-400 focus:ring-warning-500 focus:border-warning-500' : ''}`}
                      value={importedNota.data_vencimento || ''}
                      onChange={(event) =>
                        setImportedNota((prev) => ({ ...prev, data_vencimento: event.target.value }))
                      }
                    />
                  </div>
                  <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-1">
                    Use este campo quando a nota possuir vencimento único.
                  </p>
                </div>
                <div>
                  <label className="text-xs uppercase font-semibold text-gray-500 dark:text-gray-400">Valor total</label>
                  <div className="flex items-center gap-2 mt-1">
                    <DollarSign className="w-4 h-4 text-primary-500" />
                    <input
                      type="number"
                      step="0.01"
                      className="input-field"
                      value={importedNota.valor_total}
                      onChange={(event) =>
                        setImportedNota((prev) => ({
                          ...prev,
                          valor_total: parseFloat(event.target.value) || 0
                        }))
                      }
                    />
                  </div>
                </div>
                <div>
                  <label className="text-xs uppercase font-semibold text-gray-500 dark:text-gray-400">Tipo da operação</label>
                  <div className="grid grid-cols-2 gap-2 mt-1">
                    <button
                      type="button"
                      onClick={() => setImportedNota((prev) => ({ ...prev, tipo_operacao: 'pagar' }))}
                      className={`px-3 py-2 rounded-lg border text-sm font-medium transition ${
                        importedNota.tipo_operacao === 'pagar'
                          ? 'border-danger-500 bg-danger-50 text-danger-700 dark:bg-danger-900/40 dark:text-danger-200'
                          : 'border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300'
                      }`}
                    >
                      Conta a pagar
                    </button>
                    <button
                      type="button"
                      onClick={() => setImportedNota((prev) => ({ ...prev, tipo_operacao: 'receber' }))}
                      className={`px-3 py-2 rounded-lg border text-sm font-medium transition ${
                        importedNota.tipo_operacao === 'receber'
                          ? 'border-success-500 bg-success-50 text-success-700 dark:bg-success-900/40 dark:text-success-200'
                          : 'border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300'
                      }`}
                    >
                      Conta a receber
                    </button>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="card bg-white/80 dark:bg-gray-900/60 border border-gray-200 dark:border-gray-800">
                  <div className="flex items-center gap-2 text-sm font-semibold text-gray-700 dark:text-gray-200 mb-2">
                    <Building2 className="w-4 h-4 text-primary-500" />
                    Emitente
                  </div>
                  <div className="space-y-2 text-sm">
                    <input
                      type="text"
                      className="input-field"
                      placeholder="Razão social do emitente"
                      value={importedNota.emitente?.nome || ''}
                      onChange={(event) =>
                        setImportedNota((prev) => ({
                          ...prev,
                          emitente: { ...prev.emitente, nome: event.target.value }
                        }))
                      }
                    />
                    <input
                      type="text"
                      className="input-field"
                      placeholder="CNPJ do emitente"
                      value={importedNota.emitente?.cnpj || ''}
                      onChange={(event) =>
                        setImportedNota((prev) => ({
                          ...prev,
                          emitente: { ...prev.emitente, cnpj: event.target.value }
                        }))
                      }
                    />
                  </div>
                </div>

                <div className="card bg-white/80 dark:bg-gray-900/60 border border-gray-200 dark:border-gray-800">
                  <div className="flex items-center gap-2 text-sm font-semibold text-gray-700 dark:text-gray-200 mb-2">
                    <Building2 className="w-4 h-4 text-primary-500" />
                    Destinatário
                  </div>
                  <div className="space-y-2 text-sm">
                    <input
                      type="text"
                      className="input-field"
                      placeholder="Razão social do destinatário"
                      value={importedNota.destinatario?.nome || ''}
                      onChange={(event) =>
                        setImportedNota((prev) => ({
                          ...prev,
                          destinatario: { ...prev.destinatario, nome: event.target.value }
                        }))
                      }
                    />
                    <input
                      type="text"
                      className="input-field"
                      placeholder="CNPJ do destinatário"
                      value={importedNota.destinatario?.cnpj || ''}
                      onChange={(event) =>
                        setImportedNota((prev) => ({
                          ...prev,
                          destinatario: { ...prev.destinatario, cnpj: event.target.value }
                        }))
                      }
                    />
                  </div>
                </div>
              </div>

              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <h4 className="text-sm font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                    <ClipboardList className="w-4 h-4 text-primary-500" />
                    Parcelas identificadas
                  </h4>
                  <button onClick={adicionarParcelaImportada} className="text-sm text-primary-600 dark:text-primary-300 flex items-center gap-1">
                    <Plus className="w-4 h-4" />
                    Adicionar parcela
                  </button>
                </div>
                {importedNota.parcelas && importedNota.parcelas.length > 0 ? (
                  <div className="space-y-3">
                    {importedNota.parcelas.map((parcela, index) => (
                      <div
                        key={`import-parcela-${index}`}
                        className="grid grid-cols-1 md:grid-cols-[auto,1fr,1fr,auto] gap-3 items-end bg-white dark:bg-gray-900/70 border border-gray-200 dark:border-gray-800 rounded-xl p-3"
                      >
                        <div>
                          <label className="text-xs uppercase font-semibold text-gray-500 dark:text-gray-400">#</label>
                          <input
                            type="text"
                            className="input-field"
                            value={parcela.numero}
                            onChange={(event) =>
                              setImportedNota((prev) => {
                                const parcelas = [...prev.parcelas];
                                parcelas[index] = { ...parcelas[index], numero: event.target.value };
                                return { ...prev, parcelas };
                              })
                            }
                          />
                        </div>
                        <div>
                          <label className="text-xs uppercase font-semibold text-gray-500 dark:text-gray-400">Vencimento</label>
                          <input
                            type="date"
                            className={`input-field ${
                              !parcela.data_vencimento ? 'border-warning-400 focus:ring-warning-500 focus:border-warning-500' : ''
                            }`}
                            value={parcela.data_vencimento || ''}
                            onChange={(event) =>
                              setImportedNota((prev) => {
                                const parcelas = [...prev.parcelas];
                                parcelas[index] = { ...parcelas[index], data_vencimento: event.target.value };
                                return { ...prev, parcelas };
                              })
                            }
                          />
                        </div>
                        <div>
                          <label className="text-xs uppercase font-semibold text-gray-500 dark:text-gray-400">Valor</label>
                          <input
                            type="number"
                            step="0.01"
                            className="input-field"
                            value={parcela.valor}
                            onChange={(event) =>
                              setImportedNota((prev) => {
                                const parcelas = [...prev.parcelas];
                                parcelas[index] = {
                                  ...parcelas[index],
                                  valor: parseFloat(event.target.value) || 0
                                };
                                return { ...prev, parcelas };
                              })
                            }
                          />
                        </div>
                        <button
                          type="button"
                          onClick={() => removerParcelaImportada(index)}
                          className="icon-button text-danger-500 hover:text-danger-600"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-sm text-gray-600 dark:text-gray-400">
                    Nenhuma parcela encontrada. Informe um vencimento único abaixo para geração de lançamento.
                  </div>
                )}

                {(!importedNota.parcelas || importedNota.parcelas.length === 0) && (
                  <div className="text-sm text-gray-600 dark:text-gray-400 bg-gray-50/80 dark:bg-gray-900/40 border border-gray-200 dark:border-gray-800 rounded-xl p-3">
                    Utilize o campo "Data de vencimento" acima para informar um vencimento único antes de salvar.
                  </div>
                )}
              </div>

              {(importedNota.faturas?.length || importedNota.pagamentos?.length) ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {importedNota.faturas?.length > 0 && (
                    <div className="card bg-white/90 dark:bg-gray-900/70 border border-gray-200 dark:border-gray-800">
                      <div className="flex items-center gap-2 text-sm font-semibold text-gray-700 dark:text-gray-200 mb-2">
                        <FileText className="w-4 h-4 text-primary-500" />
                        Faturas
                      </div>
                      <ul className="space-y-2 text-sm">
                        {importedNota.faturas.map((fatura, index) => (
                          <li key={`fatura-${index}`} className="flex flex-col gap-1">
                            <span className="font-semibold text-gray-800 dark:text-gray-100">{fatura.numero || `Fatura ${index + 1}`}</span>
                            <div className="flex flex-wrap gap-3 text-xs text-gray-600 dark:text-gray-400">
                              <span>Original: {formatCurrency(fatura.valor_original)}</span>
                              <span>Desconto: {formatCurrency(fatura.valor_desconto)}</span>
                              <span>Líquido: {formatCurrency(fatura.valor_liquido)}</span>
                            </div>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {importedNota.pagamentos?.length > 0 && (
                    <div className="card bg-white/90 dark:bg-gray-900/70 border border-gray-200 dark:border-gray-800">
                      <div className="flex items-center gap-2 text-sm font-semibold text-gray-700 dark:text-gray-200 mb-2">
                        <CreditCard className="w-4 h-4 text-primary-500" />
                        Pagamentos identificados
                      </div>
                      <ul className="space-y-2 text-sm">
                        {importedNota.pagamentos.map((pagamento, index) => (
                          <li key={`pag-${index}`} className="flex flex-col gap-1">
                            <span className="font-semibold text-gray-800 dark:text-gray-100">{pagamento.meio || 'Forma de pagamento'}</span>
                            <div className="flex flex-wrap gap-3 text-xs text-gray-600 dark:text-gray-400">
                              <span>Valor: {formatCurrency(pagamento.valor)}</span>
                              {pagamento.indicador && <span>Indicador: {pagamento.indicador}</span>}
                            </div>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              ) : null}

              <div className="flex flex-col sm:flex-row gap-3">
                <button onClick={handleApplyImportEdits} className="btn-primary flex-1">
                  {editingImportId ? 'Aplicar alterações na prévia' : 'Adicionar à pré-visualização'}
                </button>
                <button onClick={handleCancelImportEdit} className="btn-secondary flex-1">
                  Fechar revisão
                </button>
              </div>
            </div>
          )}

          {isBulkPreviewOpen && bulkImportItems.length > 0 && (
            <div className="fixed inset-0 z-40 bg-slate-900/70 backdrop-blur-sm overflow-y-auto custom-scrollbar">
              <div className="flex min-h-full items-start justify-center px-4 py-10 sm:px-6 lg:px-8">
                <div className="w-full max-w-7xl bg-white/95 dark:bg-gray-900/95 border border-gray-200 dark:border-gray-800 rounded-3xl shadow-2xl flex h-[90vh] max-h-[90vh] flex-col overflow-hidden">
                  <div className="border-b border-gray-200 dark:border-gray-800 px-6 py-5 space-y-4">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                      <div>
                        <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Pré-visualização da importação</h2>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          Revise, ajuste e selecione as notas antes de salvar no sistema.
                        </p>
                      </div>
                      <div className="flex flex-wrap items-center gap-2">
                        <button
                          type="button"
                          className="btn-tertiary inline-flex items-center gap-2"
                          onClick={() => {
                            setIsBulkPreviewOpen(false);
                            setShowBulkResultDetails(false);
                            handleCancelImportEdit();
                          }}
                        >
                          <X className="w-4 h-4" />
                          Fechar
                        </button>
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-2 text-xs font-semibold text-gray-600 dark:text-gray-400">
                      <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-gray-100 dark:bg-gray-800/60">
                        <Layers className="w-4 h-4 text-primary-500" />
                        {bulkImportStats.total} arquivos
                      </span>
                      <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-gray-100 dark:bg-gray-800/60">
                        <CheckCircle2 className="w-4 h-4 text-success-500" />
                        {bulkImportStats.selecionados} selecionados
                      </span>
                      <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-gray-100 dark:bg-gray-800/60">
                        <AlertTriangle className="w-4 h-4 text-warning-500" />
                        {bulkImportStats.pendencias} com pendências
                      </span>
                      <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-gray-100 dark:bg-gray-800/60">
                        <FileWarning className="w-4 h-4 text-danger-500" />
                        {bulkImportStats.duplicados} duplicadas
                      </span>
                    </div>
                    <div className="flex flex-wrap items-center gap-3 text-xs">
                      <ListFilter className="w-4 h-4 text-gray-400 dark:text-gray-500" />
                      <div className="flex items-center gap-2">
                        <span className="text-gray-500 dark:text-gray-400">Tipo</span>
                        <select
                          className="input-field text-xs py-1"
                          value={bulkImportFilters.tipo}
                          onChange={(event) => handleBulkFilterChange('tipo', event.target.value)}
                        >
                          <option value="todos">Todos</option>
                          <option value="nfe">NF-e</option>
                          <option value="cte">CT-e</option>
                        </select>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-gray-500 dark:text-gray-400">Situação</span>
                        <select
                          className="input-field text-xs py-1"
                          value={bulkImportFilters.situacao}
                          onChange={(event) => handleBulkFilterChange('situacao', event.target.value)}
                        >
                          <option value="todas">Todas</option>
                          <option value="pendencias">Com pendências</option>
                          <option value="sem_pendencias">Sem pendências</option>
                          <option value="duplicados">Duplicadas</option>
                          <option value="erros">Com erro</option>
                        </select>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-gray-500 dark:text-gray-400">Financeiro</span>
                        <select
                          className="input-field text-xs py-1"
                          value={bulkImportFilters.financeiro}
                          onChange={(event) => handleBulkFilterChange('financeiro', event.target.value)}
                        >
                          <option value="todos">Todos</option>
                          <option value="gera">Gera financeiro</option>
                          <option value="naoGera">Sem efeito financeiro</option>
                        </select>
                      </div>
                    </div>
                  </div>
                  <div className="flex-1 overflow-hidden min-h-0">
                    <div className="h-full overflow-y-auto custom-scrollbar px-6 py-6 space-y-6">
                      {importedNota && (
                        <div className="border border-primary-200 dark:border-primary-800 bg-primary-50/70 dark:bg-primary-900/30 rounded-2xl p-5 space-y-5">
                          <div className="flex justify-between items-start gap-3">
                            <div>
                              <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                                <span className="text-2xl">{importedNota.origem === 'xml_cte' ? '🚚' : '📄'}</span>
                                Revisando {importedNota.origem === 'xml_cte' ? 'CT-e' : 'NF-e'} importado
                              </h3>
                              {editingImportId && (
                                <p className="text-xs text-gray-600 dark:text-gray-400 mt-0.5">
                                  As alterações serão refletidas na linha correspondente da pré-visualização em lote.
                                </p>
                              )}
                              {!editingImportId && (
                                <p className="text-xs text-gray-600 dark:text-gray-400 mt-0.5">
                                  Salve esta prévia para adicioná-la à lista de importação em lote.
                                </p>
                              )}
                            </div>
                            <button onClick={handleCancelImportEdit} className="icon-button" title="Fechar revisão">
                              <X className="w-4 h-4" />
                            </button>
                          </div>

                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="space-y-4">
                              <div>
                                <label className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">
                                  {importedNotaContatoLabel}
                                </label>
                                <input
                                  type="text"
                                  className="input-field mt-1"
                                  value={importedNotaContatoNome}
                                  onChange={(event) => {
                                    const novoNome = event.target.value;
                                    setImportedNota((prev) => {
                                      if (!prev) return prev;
                                      const atualizarDestinatario =
                                        prev.tipo_operacao === 'receber' ||
                                        (!prev.tipo_operacao && !prev.emitente?.nome && Boolean(prev.destinatario?.nome));
                                      if (atualizarDestinatario) {
                                        return {
                                          ...prev,
                                          destinatario: { ...(prev.destinatario || {}), nome: novoNome }
                                        };
                                      }
                                      return {
                                        ...prev,
                                        emitente: { ...(prev.emitente || {}), nome: novoNome }
                                      };
                                    });
                                  }}
                                />
                              </div>

                              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                <div>
                                  <label className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">Número</label>
                                  <input
                                    type="text"
                                    className="input-field mt-1"
                                    value={importedNota.numero || ''}
                                    onChange={(event) => setImportedNota((prev) => ({ ...prev, numero: event.target.value }))}
                                  />
                                </div>
                                <div>
                                  <label className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">Data de emissão</label>
                                  <input
                                    type="date"
                                    className="input-field mt-1"
                                    value={importedNota.data_emissao || ''}
                                    onChange={(event) => setImportedNota((prev) => ({ ...prev, data_emissao: event.target.value }))}
                                  />
                                </div>
                              </div>

                              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                <div>
                                  <label className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">Valor total</label>
                                  <input
                                    type="number"
                                    step="0.01"
                                    className="input-field mt-1"
                                    value={importedNota.valor_total || ''}
                                    onChange={(event) =>
                                      setImportedNota((prev) => ({ ...prev, valor_total: event.target.value }))
                                    }
                                  />
                                </div>
                                <div>
                                  <label className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">Tipo de operação</label>
                                  <div className="grid grid-cols-2 gap-2 mt-1">
                                    <button
                                      type="button"
                                      onClick={() => setImportedNota((prev) => ({ ...prev, tipo_operacao: 'pagar' }))}
                                      className={`px-3 py-2 rounded-lg border text-sm font-semibold transition-colors ${
                                        importedNota.tipo_operacao === 'pagar'
                                          ? 'border-danger-500 bg-danger-500 text-white shadow-sm hover:bg-danger-600 dark:border-danger-400 dark:bg-danger-500 dark:text-white'
                                          : 'border-danger-200 bg-danger-50 text-danger-700 hover:bg-danger-100 dark:border-danger-700 dark:bg-danger-900/30 dark:text-danger-200'
                                      }`}
                                    >
                                      Pagar
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => setImportedNota((prev) => ({ ...prev, tipo_operacao: 'receber' }))}
                                      className={`px-3 py-2 rounded-lg border text-sm font-semibold transition-colors ${
                                        importedNota.tipo_operacao === 'receber'
                                          ? 'border-success-500 bg-success-500 text-white shadow-sm hover:bg-success-600 dark:border-success-400 dark:bg-success-500 dark:text-white'
                                          : 'border-success-200 bg-success-50 text-success-700 hover:bg-success-100 dark:border-success-700 dark:bg-success-900/30 dark:text-success-200'
                                      }`}
                                    >
                                      Receber
                                    </button>
                                  </div>
                                </div>
                              </div>

                              <div>
                                <label className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">Observações</label>
                                <textarea
                                  className="input-field mt-1"
                                  rows={3}
                                  value={importedNota.observacoes || ''}
                                  onChange={(event) => setImportedNota((prev) => ({ ...prev, observacoes: event.target.value }))}
                                />
                              </div>

                              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                <div>
                                  <label className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">CFOP</label>
                                  <input
                                    type="text"
                                    className="input-field mt-1"
                                    value={importedNota.cfop || ''}
                                    onChange={(event) => setImportedNota((prev) => ({ ...prev, cfop: event.target.value }))}
                                  />
                                </div>
                                <div>
                                  <label className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">
                                    Natureza da operação
                                  </label>
                                  <input
                                    type="text"
                                    className="input-field mt-1"
                                    value={importedNota.natureza_operacao || ''}
                                    onChange={(event) => setImportedNota((prev) => ({ ...prev, natureza_operacao: event.target.value }))}
                                  />
                                </div>
                              </div>

                              <div className="space-y-2">
                                <div className="flex items-center justify-between text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">
                                  <span>Financeiro</span>
                                  <button
                                    type="button"
                                    className="inline-flex items-center gap-1 text-primary-600 dark:text-primary-300"
                                    onClick={() =>
                                      setImportedNota((prev) => ({
                                        ...prev,
                                        gera_financeiro: !parseBooleanFlag(prev.gera_financeiro, true)
                                      }))
                                    }
                                  >
                                    <DollarSign className="w-3 h-3" />
                                    {parseBooleanFlag(importedNota.gera_financeiro, true) ? 'Com financeiro' : 'Sem financeiro'}
                                  </button>
                                </div>
                                {!parseBooleanFlag(importedNota.gera_financeiro, true) && (
                                  <div className="text-xs text-gray-600 dark:text-gray-400">
                                    Indique o motivo do não lançamento financeiro (opcional).
                                  </div>
                                )}
                                <textarea
                                  className="input-field mt-1"
                                  rows={2}
                                  value={importedNota.motivo_financeiro || ''}
                                  onChange={(event) => setImportedNota((prev) => ({ ...prev, motivo_financeiro: event.target.value }))}
                                />
                              </div>
                            </div>

                            <div className="space-y-4">
                              <div className={`rounded-xl border ${importedNotaSemVencimento ? 'border-warning-400 bg-warning-50/80 dark:border-warning-500/60 dark:bg-warning-900/20' : 'border-gray-200 dark:border-gray-700'} p-4 space-y-2`}>
                                <div className="flex items-center justify-between">
                                  <div className="flex items-center gap-2 text-sm font-semibold text-gray-800 dark:text-gray-100">
                                    <Calendar className="w-4 h-4 text-primary-500" />
                                    Próximo vencimento
                                  </div>
                                  {importedNotaSemVencimento && (
                                    <span className="inline-flex items-center gap-1 text-xs font-semibold text-warning-600 dark:text-warning-300">
                                      <AlertTriangle className="w-3 h-3" /> Preencha o vencimento
                                    </span>
                                  )}
                                </div>
                                <p className="text-xs text-gray-500 dark:text-gray-400">
                                  Informe a data de vencimento principal ou cadastre as parcelas abaixo.
                                </p>
                                <input
                                  type="date"
                                  className={`input-field mt-1 ${importedNotaSemVencimento ? 'border-warning-400 focus:ring-warning-500 dark:border-warning-500/60' : ''}`}
                                  value={importedNota.data_vencimento || ''}
                                  onChange={(event) => setImportedNota((prev) => ({ ...prev, data_vencimento: event.target.value }))}
                                />
                                {importedNotaSemVencimento && (
                                  <div className="text-xs text-warning-600 dark:text-warning-300">
                                    Esta nota gera financeiro e precisa de pelo menos um vencimento definido antes de salvar.
                                  </div>
                                )}
                              </div>

                              <div className="border border-gray-200 dark:border-gray-700 rounded-xl p-4 space-y-3">
                                <div className="flex items-center justify-between">
                                  <div className="flex items-center gap-2 text-sm font-semibold text-gray-800 dark:text-gray-100">
                                    <Layers className="w-4 h-4 text-primary-500" />
                                    Parcelas identificadas
                                  </div>
                                  <button type="button" className="btn-secondary text-xs px-3 py-1" onClick={adicionarParcelaImportada}>
                                    <Plus className="w-3 h-3" /> Adicionar parcela
                                  </button>
                                </div>
                                <div className="space-y-3 max-h-56 overflow-auto pr-1 custom-scrollbar">
                                  {Array.isArray(importedNota.parcelas) && importedNota.parcelas.length > 0 ? (
                                    importedNota.parcelas.map((parcela, index) => (
                                      <div key={index} className="grid grid-cols-1 sm:grid-cols-12 gap-2 sm:gap-3 items-end">
                                        <div className="sm:col-span-3">
                                          <label className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">Número</label>
                                          <input
                                            type="text"
                                            className="input-field mt-1"
                                            value={parcela.numero || index + 1}
                                            onChange={(event) =>
                                              setImportedNota((prev) => {
                                                const parcelasAtualizadas = [...(prev.parcelas || [])];
                                                parcelasAtualizadas[index] = {
                                                  ...parcelasAtualizadas[index],
                                                  numero: event.target.value,
                                                };
                                                return { ...prev, parcelas: parcelasAtualizadas };
                                              })
                                            }
                                          />
                                        </div>
                                        <div className="sm:col-span-5">
                                          <label className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">Data de vencimento</label>
                                          <input
                                            type="date"
                                            className="input-field mt-1"
                                            value={parcela.data_vencimento || ''}
                                            onChange={(event) =>
                                              setImportedNota((prev) => {
                                                const parcelasAtualizadas = [...(prev.parcelas || [])];
                                                parcelasAtualizadas[index] = {
                                                  ...parcelasAtualizadas[index],
                                                  data_vencimento: event.target.value,
                                                };
                                                return { ...prev, parcelas: parcelasAtualizadas };
                                              })
                                            }
                                          />
                                        </div>
                                        <div className="sm:col-span-3">
                                          <label className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">Valor</label>
                                          <input
                                            type="number"
                                            step="0.01"
                                            className="input-field mt-1"
                                            value={parcela.valor || ''}
                                            onChange={(event) =>
                                              setImportedNota((prev) => {
                                                const parcelasAtualizadas = [...(prev.parcelas || [])];
                                                parcelasAtualizadas[index] = {
                                                  ...parcelasAtualizadas[index],
                                                  valor: event.target.value,
                                                };
                                                return { ...prev, parcelas: parcelasAtualizadas };
                                              })
                                            }
                                          />
                                        </div>
                                        <div className="sm:col-span-1 flex justify-end">
                                          <button
                                            type="button"
                                            className="icon-button text-danger-500 hover:text-danger-600"
                                            onClick={() => removerParcelaImportada(index)}
                                          >
                                            <Trash2 className="w-4 h-4" />
                                          </button>
                                        </div>
                                      </div>
                                    ))
                                  ) : (
                                    <div className="text-xs text-gray-500 dark:text-gray-400">
                                      Nenhuma parcela identificada automaticamente.
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>

                          <div className="flex flex-col sm:flex-row gap-3">
                            <button onClick={handleApplyImportEdits} className="btn-primary flex-1">
                              {editingImportId ? 'Aplicar alterações na prévia' : 'Adicionar à pré-visualização'}
                            </button>
                            <button onClick={handleCancelImportEdit} className="btn-secondary flex-1">
                              Fechar revisão
                            </button>
                          </div>
                        </div>
                      )}

                      <div className="space-y-5">
                        <div className="border border-gray-200 dark:border-gray-800 rounded-2xl overflow-hidden">
                          {filteredBulkImportItems.length > 0 ? (
                            <div className="overflow-x-auto custom-scrollbar">
                              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-800">
                                <thead className="bg-gray-50 dark:bg-gray-900/40">
                                  <tr>
                                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">Sel.</th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">Documento</th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">Empresa</th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">Classificação</th>
                                    <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">Valor total</th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">Situação financeira</th>
                                    <th className="px-3 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 w-40 min-w-[9rem]">Parcelas / vencimentos</th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">Alertas</th>
                                    <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">Ações</th>
                                  </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                                  {filteredBulkImportItems.map((item) => {
                                    const nota = item.nota;
                                    const empresaDescricao = nota ? describeEmpresaNota(nota) : { nome: 'Arquivo inválido', subtitulo: '' };
                                    const situacaoFinanceira = item.situacaoFinanceira;
                                    const parcelasInfo = nota ? getParcelasInfo(nota) : null;
                                    const proximoVencimento = parcelasInfo?.next?.data_vencimento || nota?.data_vencimento || null;
                                    const valorNormalizado = item.valorTotalNormalizado ?? (nota ? parseCurrencyString(nota.valor_total) : 0);
                                    const documentoTipo = nota?.origem === 'xml_cte' ? 'CT-e' : 'NF-e';
                                    const DocIcon = nota?.origem === 'xml_cte' ? Truck : FileText;
                                    const cfopInfo = nota?.cfop ? getCfopDescricao(nota.cfop) : null;
                                    const cfopFormatado = nota?.cfop ? cfopInfo?.cfop_formatado || formatCfopFn(nota.cfop) : '';
                                    const classificacaoPrincipal =
                                      cfopInfo?.descricao?.trim() ||
                                      nota?.natureza_operacao ||
                                      (cfopFormatado ? `CFOP ${cfopFormatado}` : 'Sem classificação');
                                    const ajustesTooltip = Array.isArray(item.importRuleAdjustments)
                                      ? item.importRuleAdjustments
                                          .map((ajuste) =>
                                            ajuste.ruleName
                                              ? `${ajuste.ruleName}: ${ajuste.message}`
                                              : ajuste.message
                                          )
                                          .join('\n')
                                      : '';
                                    const rowTone = item.status === 'error'
                                      ? 'bg-danger-50/80 dark:bg-danger-900/20'
                                      : item.status === 'saved'
                                        ? 'bg-success-50/70 dark:bg-success-900/15'
                                        : '';

                                    return (
                                      <tr key={item.id} className={`${rowTone}`}>
                                        <td className="px-4 py-3 align-top">
                                          <input
                                            type="checkbox"
                                            className="checkbox"
                                            disabled={item.status === 'error' || item.status === 'saved'}
                                            checked={Boolean(item.selected) && item.status !== 'saved'}
                                            onChange={(event) => handleToggleImportSelection(item.id, event.target.checked)}
                                          />
                                        </td>
                                        <td className="px-4 py-3 align-top w-44 min-w-[10rem]" title={item.fileName}>
                                          <div className="flex flex-col gap-1">
                                            <div className="flex items-center gap-2">
                                              <span
                                                className={`inline-flex items-center gap-1 px-2.5 py-1 text-[11px] font-semibold rounded-full ${
                                                  documentoTipo === 'CT-e'
                                                    ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-200'
                                                    : 'bg-primary-100 text-primary-700 dark:bg-primary-900/40 dark:text-primary-200'
                                                }`}
                                              >
                                                <DocIcon className="w-3.5 h-3.5" />
                                                {documentoTipo}
                                              </span>
                                              {item.status === 'saved' && (
                                                <span className="text-[11px] font-semibold text-success-600 dark:text-success-300">Salvo</span>
                                              )}
                                              {item.status === 'error' && (
                                                <span className="text-[11px] font-semibold text-danger-600 dark:text-danger-300">Com erro</span>
                                              )}
                                            </div>
                                            <div className="text-sm font-semibold text-gray-900 dark:text-white">
                                              {nota?.numero || 'Sem número'}
                                            </div>
                                            <div className="text-xs text-gray-500 dark:text-gray-400">
                                              {nota?.data_emissao ? `Emitida em ${formatDate(nota.data_emissao)}` : 'Data de emissão não informada'}
                                            </div>
                                          </div>
                                        </td>
                                        <td className="px-4 py-3 align-top">
                                          <div className="text-sm font-semibold text-gray-900 dark:text-white">{empresaDescricao.nome}</div>
                                          {empresaDescricao.subtitulo && (
                                            <div className="text-xs text-gray-500 dark:text-gray-400">{empresaDescricao.subtitulo}</div>
                                          )}
                                        </td>
                                        <td className="px-4 py-3 align-top">
                                          <div className="space-y-1">
                                            <div className="text-sm font-semibold text-gray-900 dark:text-white">
                                              {classificacaoPrincipal || 'Sem classificação'}
                                            </div>
                                            {cfopFormatado && (
                                              <div className="text-xs text-gray-500 dark:text-gray-400">CFOP {cfopFormatado}</div>
                                            )}
                                          </div>
                                        </td>
                                        <td className="px-4 py-3 align-top text-right text-sm font-semibold text-gray-900 dark:text-white">
                                          {nota ? formatCurrency(valorNormalizado || nota.valor_total || 0) : '—'}
                                        </td>
                                        <td className="px-4 py-3 align-top">
                                          {situacaoFinanceira ? (
                                            <span
                                              className={`inline-flex items-center gap-1 px-2.5 py-1 text-[11px] font-semibold rounded-full ${situacaoFinanceira.tone}`}
                                              title={situacaoFinanceira.tooltip}
                                            >
                                              <situacaoFinanceira.Icon className="w-3.5 h-3.5" />
                                              {situacaoFinanceira.label}
                                            </span>
                                          ) : (
                                            <span className="inline-flex items-center px-2.5 py-1 text-[11px] font-semibold rounded-full bg-gray-200 text-gray-600 dark:bg-gray-800 dark:text-gray-300">
                                              Análise indisponível
                                            </span>
                                          )}
                                        </td>
                                        <td className="px-3 py-3 align-top text-xs text-gray-600 dark:text-gray-300 space-y-1 w-40 min-w-[9rem]">
                                          {item.status === 'error' ? (
                                            <span className="block text-danger-600 dark:text-danger-300">Não foi possível ler os vencimentos.</span>
                                          ) : (
                                            <>
                                              {item.analiseParcelas?.possuiParcelas ? (
                                                <div className="font-semibold text-gray-700 dark:text-gray-200">
                                                  {item.parcelasCount} parcela{item.parcelasCount !== 1 ? 's' : ''}
                                                </div>
                                              ) : (
                                                <div className="font-semibold text-gray-700 dark:text-gray-200">
                                                  {nota?.data_vencimento ? `Vencimento único em ${formatDate(nota.data_vencimento)}` : 'Sem vencimento'}
                                                </div>
                                              )}
                                              {proximoVencimento && (
                                                <div className="text-[11px] text-gray-500 dark:text-gray-400">Próximo vencimento: {formatDate(proximoVencimento)}</div>
                                              )}
                                              {item.analiseParcelas?.possuiParcelasSemVencimento && (
                                                <div className="text-[11px] text-warning-600 dark:text-warning-300">Parcelas sem data definida</div>
                                              )}
                                            </>
                                          )}
                                        </td>
                                        <td className="px-4 py-3 align-top">
                                          <div className="flex flex-wrap gap-1">
                                            {item.status === 'error' ? (
                                              <span className="inline-flex items-center gap-1 px-2 py-1 text-[11px] font-semibold rounded-full bg-danger-100 text-danger-700 dark:bg-danger-900/40 dark:text-danger-200">
                                                <FileWarning className="w-3 h-3" /> Erro ao ler XML
                                              </span>
                                            ) : (
                                              <>
                                                {item.hasAutomaticAdjustments && (
                                                  <span
                                                    className="inline-flex items-center gap-1 px-2 py-1 text-[11px] font-semibold rounded-full bg-primary-100 text-primary-700 dark:bg-primary-900/40 dark:text-primary-200"
                                                    title={ajustesTooltip || 'Ajustes automáticos aplicados pelas regras de importação.'}
                                                  >
                                                    <Settings className="w-3 h-3" /> Ajuste automático
                                                  </span>
                                                )}
                                                {item.duplicateExisting && (
                                                  <span className="inline-flex items-center gap-1 px-2 py-1 text-[11px] font-semibold rounded-full bg-danger-100 text-danger-700 dark:bg-danger-900/40 dark:text-danger-200">
                                                    <FileWarning className="w-3 h-3" /> Já cadastrada
                                                  </span>
                                                )}
                                                {item.duplicateInBatch && (
                                                  <span className="inline-flex items-center gap-1 px-2 py-1 text-[11px] font-semibold rounded-full bg-warning-100 text-warning-700 dark:bg-warning-900/40 dark:text-warning-200">
                                                    <Layers className="w-3 h-3" /> Duplicada no lote
                                                  </span>
                                                )}
                                                {item.pendencias?.slice(0, 3).map((pendencia, index) => (
                                                  <span
                                                    key={`${item.id}-pendencia-${index}`}
                                                    className="inline-flex items-center gap-1 px-2 py-1 text-[11px] font-semibold rounded-full bg-warning-100 text-warning-700 dark:bg-warning-900/40 dark:text-warning-200"
                                                  >
                                                    <AlertTriangle className="w-3 h-3" />
                                                    {pendencia}
                                                  </span>
                                                ))}
                                              </>
                                            )}
                                          </div>
                                        </td>
                                        <td className="px-4 py-3 align-top">
                                          <div className="flex items-center justify-end gap-2">
                                            <button
                                              type="button"
                                              className={`p-2 rounded-lg transition-colors ${
                                                item.status === 'error'
                                                  ? 'text-gray-400 cursor-not-allowed'
                                                  : 'text-warning-500 hover:bg-warning-500/15'
                                              }`}
                                              title="Editar prévia"
                                              disabled={item.status === 'error'}
                                              onClick={() => {
                                                if (item.status === 'error' || !item.nota) return;
                                                setEditingImportId(item.id);
                                                setImportedNota(JSON.parse(JSON.stringify(item.nota)));
                                              }}
                                            >
                                              <Edit className="w-4 h-4" />
                                            </button>
                                            <button
                                              type="button"
                                              className="p-2 rounded-lg transition-colors text-danger-500 hover:bg-danger-500/15"
                                              title="Remover da prévia"
                                              onClick={() => handleRemoveImportItem(item.id)}
                                            >
                                              <Trash2 className="w-4 h-4" />
                                            </button>
                                          </div>
                                        </td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                            </div>
                          ) : (
                            <div className="p-6 text-sm text-center text-gray-600 dark:text-gray-400">
                              Nenhuma nota corresponde aos filtros selecionados.
                            </div>
                          )}
                        </div>

                        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                          <div className="text-xs text-gray-500 dark:text-gray-400">
                            Selecione apenas as notas prontas para salvar. Duplicidades e pendências aparecem sinalizadas na tabela.
                          </div>
                          <button
                            type="button"
                            className="btn-primary inline-flex items-center gap-2"
                            onClick={handleBulkSaveSelected}
                            disabled={bulkSaving || bulkImportStats.selecionados === 0}
                          >
                            {bulkSaving ? 'Salvando notas...' : `Salvar selecionados (${bulkImportStats.selecionados})`}
                          </button>
                        </div>

                        <div className="text-[11px] text-gray-500 dark:text-gray-400">
                          Após salvar, um resumo detalhado será exibido automaticamente.
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      {notas.length > 0 && (
        <div className="card overflow-hidden">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Notas cadastradas</h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Visualize emitente, status, parcelas identificadas e gere lançamentos financeiros de forma automática.
              </p>
            </div>
          </div>

          <div className="space-y-5">
            <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
              <div className="flex flex-col gap-3 w-full">
                <div className="relative w-full sm:max-w-md lg:max-w-sm">
                  <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-gray-500" />
                  <input
                    type="text"
                    className="input-field pl-9"
                    placeholder="Busque por número, empresa ou CFOP"
                    value={notasFilters.search}
                    onChange={(event) => handleNotaFilterChange('search', event.target.value)}
                  />
                </div>
                <div className="flex flex-wrap items-center gap-3 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                  <span className="inline-flex items-center gap-1">
                    <ListFilter className="w-4 h-4 text-gray-400 dark:text-gray-500" />
                    Filtros inteligentes
                  </span>
                  <div className="flex items-center gap-2">
                    <span>Documento</span>
                    <select
                      className="input-field text-xs py-1"
                      value={notasFilters.tipoDocumento}
                      onChange={(event) => handleNotaFilterChange('tipoDocumento', event.target.value)}
                    >
                      <option value="todos">Todos</option>
                      <option value="nfe">NF-e</option>
                      <option value="cte">CT-e</option>
                    </select>
                  </div>
                  <div className="flex items-center gap-2">
                    <span>Operação</span>
                    <select
                      className="input-field text-xs py-1"
                      value={notasFilters.tipoOperacao}
                      onChange={(event) => handleNotaFilterChange('tipoOperacao', event.target.value)}
                    >
                      <option value="todas">Todas</option>
                      <option value="pagar">Conta a pagar</option>
                      <option value="receber">Conta a receber</option>
                    </select>
                  </div>
                  <div className="flex items-center gap-2">
                    <span>Financeiro</span>
                    <select
                      className="input-field text-xs py-1"
                      value={notasFilters.financeiro}
                      onChange={(event) => handleNotaFilterChange('financeiro', event.target.value)}
                    >
                      <option value="todos">Todos</option>
                      <option value="gera">Gera financeiro</option>
                      <option value="naoGera">Sem efeito financeiro</option>
                    </select>
                  </div>
                  <div className="flex items-center gap-2">
                    <span>Situação</span>
                    <select
                      className="input-field text-xs py-1"
                      value={notasFilters.situacao}
                      onChange={(event) => handleNotaFilterChange('situacao', event.target.value)}
                    >
                      <option value="todas">Todas</option>
                      <option value="aguardando">{situacaoFinanceiraLabel.aguardando}</option>
                      <option value="lancado">{situacaoFinanceiraLabel.lancado}</option>
                      <option value="classificacao_pendente">{situacaoFinanceiraLabel.classificacao_pendente}</option>
                      <option value="sem_financeiro">{situacaoFinanceiraLabel.sem_financeiro}</option>
                    </select>
                  </div>
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400">
                  Exibindo {filteredNotas.length} de {notas.length} documento(s).
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={handleSelectFiltered}
                  disabled={filteredNotas.length === 0 || massActionProcessing}
                >
                  Selecionar filtrados ({filteredNotas.length})
                </button>
                <button
                  type="button"
                  className="btn-tertiary"
                  onClick={handleClearSelection}
                  disabled={selectedCount === 0 || massActionProcessing}
                >
                  Limpar seleção
                </button>
                <button
                  type="button"
                  className="btn-tertiary"
                  onClick={handleClearNotasFilters}
                  disabled={!filtrosAtivos}
                >
                  Limpar filtros
                </button>
              </div>
            </div>

            {selectedCount > 0 && (
              <div className="border border-primary-200 dark:border-primary-800 bg-primary-50/60 dark:bg-primary-900/20 rounded-2xl p-4 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                <div>
                  <p className="text-sm font-semibold text-primary-800 dark:text-primary-200">
                    {selectedCount} documento(s) selecionado(s)
                  </p>
                  <p className="text-xs text-primary-700/80 dark:text-primary-300/80">
                    {filteredSelectedCount !== selectedCount
                      ? `${filteredSelectedCount} visíveis com os filtros atuais. `
                      : ''}
                    Escolha uma ação em massa para agilizar o processamento financeiro.
                  </p>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <button
                    type="button"
                    className="btn-primary inline-flex items-center gap-2"
                    onClick={() => openMassActionPreview('generate')}
                    disabled={massActionProcessing}
                    aria-busy={massActionProcessing}
                  >
                    <Rocket className="w-4 h-4" />
                    {massActionProcessing ? 'Processando...' : 'Gerar lançamentos'}
                  </button>
                  <button
                    type="button"
                    className="btn-tertiary inline-flex items-center gap-2"
                    onClick={() => openMassActionPreview('resumo')}
                    disabled={massActionProcessing}
                  >
                    <Printer className="w-4 h-4" /> Imprimir resumo
                  </button>
                  <button
                    type="button"
                    className="btn-danger inline-flex items-center gap-2"
                    onClick={handleMassDeleteSelected}
                    disabled={massActionProcessing}
                  >
                    <Trash2 className="w-4 h-4" /> Excluir selecionados
                  </button>
                </div>
              </div>
            )}

            <div className="relative">
              {filteredNotas.length > 0 ? (
                <div className="max-h-[70vh] overflow-auto custom-scrollbar">
                  <table className="min-w-full table-fixed divide-y divide-gray-200 dark:divide-gray-700">
                    <colgroup>
                      <col className="w-[64px]" />
                      <col className="w-[140px]" />
                      <col className="w-[280px]" />
                      <col className="w-[140px]" />
                      <col className="w-[160px]" />
                      <col className="w-[180px]" />
                      <col className="w-[240px]" />
                      <col className="w-[140px]" />
                    </colgroup>
                    <thead className="bg-gray-50 dark:bg-gray-800/60">
                      <tr>
                        <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          <input
                            ref={selectAllCheckboxRef}
                            type="checkbox"
                            className="h-4 w-4 text-primary-500 border-gray-300 rounded focus:ring-primary-500"
                            onChange={handleToggleSelectAllFiltered}
                            checked={allFilteredSelected && filteredNotaKeys.length > 0}
                            disabled={filteredNotas.length === 0 || massActionProcessing}
                          />
                        </th>
                        <th className="px-4 py-3 text-center whitespace-nowrap text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Documento
                        </th>
                        <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Empresa
                        </th>
                        <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Valor Total
                        </th>
                        <th className="px-4 py-3 text-center whitespace-nowrap text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Tipo de operação
                        </th>
                        <th className="px-4 py-3 text-center whitespace-nowrap text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Situação financeira
                        </th>
                        <th className="px-3 py-3 text-center text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Parcelas / vencimentos
                        </th>
                        <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Ações
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                      {filteredNotas.map((nota) => {
                        const parcelas = Array.isArray(nota.parcelas) ? nota.parcelas : [];
                        const tipoLabel = tipoOperacaoLabel(nota.tipo_operacao);
                        const empresaResumo = describeEmpresaNota(nota);
                        const geraFinanceiro = parseBooleanFlag(nota.gera_financeiro, true);
                        const situacaoFinanceira = getSituacaoFinanceira(nota);
                        const docTipoLabel = nota.origem === 'xml_cte' ? 'CT\u2011e' : 'NF\u2011e';
                        const DocIcon = nota.origem === 'xml_cte' ? Truck : FileText;
                        const docNumero = nota.numero ? `nº ${nota.numero}` : 'nº —';
                        const notaKey = getNotaSelectionKey(nota);
                        const selecionada = notaKey ? selectedNotas.has(notaKey) : false;

                        return (
                          <tr
                            key={notaKey || nota.id || nota.numero}
                            className={`hover:bg-gray-50 dark:hover:bg-gray-800/40 transition-colors ${selecionada ? 'bg-primary-50/40 dark:bg-primary-900/15' : ''}`}
                            aria-selected={selecionada}
                          >
                            <td className="px-4 py-4 text-center align-top">
                              <input
                                type="checkbox"
                                className="h-4 w-4 text-primary-500 border-gray-300 rounded focus:ring-primary-500"
                                onChange={() => handleNotaSelectionToggle(nota)}
                                checked={selecionada}
                                disabled={massActionProcessing || !notaKey}
                                aria-label={`Selecionar ${docTipoLabel} ${docNumero}`}
                              />
                            </td>
                            <td className="px-4 py-4 text-sm text-center text-gray-900 dark:text-white">
                              <button
                                type="button"
                                onClick={() => setSelectedNotaId(nota.id || nota.numero)}
                                className="flex items-center gap-2 text-primary-600 dark:text-primary-300 hover:text-primary-800 dark:hover:text-primary-100"
                              >
                                <DocIcon size={18} className="text-primary-500" />
                                <span className="font-semibold">{docTipoLabel}</span>
                                <span className="text-xs text-gray-500 dark:text-gray-400">{docNumero}</span>
                              </button>
                            </td>
                            <td className="px-4 py-4 text-sm text-center text-gray-700 dark:text-gray-300">
                              <div className="flex flex-col items-center gap-0.5">
                                <span className="font-semibold text-gray-900 dark:text-white">{empresaResumo?.nome || 'Não identificado'}</span>
                                {empresaResumo?.subtitulo && (
                                  <span className="text-xs text-gray-500 dark:text-gray-400">{empresaResumo.subtitulo}</span>
                                )}
                              </div>
                            </td>
                            <td className="px-4 py-4 text-sm text-center text-gray-900 dark:text-white font-semibold">
                              {formatCurrency(nota.valor_total)}
                            </td>
                            <td className="px-4 py-4 text-xs text-center">
                              <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full font-semibold ${getTipoOperacaoBadge(nota.tipo_operacao)}`}>
                                {tipoLabel}
                              </span>
                            </td>
                            <td className="px-4 py-4 text-xs text-center">
                              <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full font-semibold ${situacaoFinanceira.tone}`}>
                                <situacaoFinanceira.Icon size={14} />
                                {situacaoFinanceira.label}
                              </span>
                            </td>
                            <td className="px-3 py-4 align-top text-center">
                              <ParcelasCell nota={nota} formatDate={formatDate} formatCurrency={formatCurrency} compact />
                            </td>
                            <td className="px-3 py-4 text-center">
                              <div className="flex justify-center gap-1.5">
                                <button
                                  onClick={() => setSelectedNotaId(nota.id || nota.numero)}
                                  className="p-2 hover:bg-primary-500/20 rounded-lg transition-colors text-primary-400"
                                  title="Ver detalhes"
                                >
                                  <Eye size={18} />
                                </button>
                                <button
                                  onClick={() => setSelectedNotaId(nota.id || nota.numero)}
                                  className="p-2 hover:bg-yellow-500/20 rounded-lg transition-colors text-yellow-400"
                                  title="Editar"
                                >
                                  <Edit size={18} />
                                </button>
                                <button
                                  onClick={() => handleGenerateLancamentos(nota)}
                                  disabled={generatingLancamentoId === (nota.id || nota.numero)}
                                  className="p-2 hover:bg-green-500/20 rounded-lg transition-colors text-green-400 disabled:opacity-50 disabled:pointer-events-none"
                                  title={
                                    generatingLancamentoId === (nota.id || nota.numero)
                                      ? 'Gerando lançamentos...'
                                      : !geraFinanceiro
                                        ? 'Nota marcada como sem efeito financeiro. Clique para confirmar a geração mesmo assim.'
                                        : 'Gerar lançamentos'
                                  }
                                  aria-busy={generatingLancamentoId === (nota.id || nota.numero)}
                                >
                                  <Rocket size={18} />
                                </button>
                                <button
                                  onClick={() => handleDelete(nota.id)}
                                  className="p-2 hover:bg-red-500/20 rounded-lg transition-colors text-red-400 disabled:opacity-50 disabled:pointer-events-none"
                                  title={deletingNotaId === (nota.id || nota.numero) ? 'Excluindo...' : 'Excluir'}
                                  disabled={deletingNotaId === (nota.id || nota.numero)}
                                >
                                  <Trash2 size={18} />
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="px-6 py-12 text-center text-sm text-gray-600 dark:text-gray-400 space-y-3">
                  <p>Nenhuma nota corresponde aos filtros selecionados.</p>
                  {filtrosAtivos && (
                    <button type="button" className="btn-tertiary inline-flex items-center gap-2" onClick={handleClearNotasFilters}>
                      <ListFilter className="w-4 h-4" /> Limpar filtros
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      {massPreview.open && (
        <div
          className="fixed inset-0 z-50 bg-slate-900/70 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={closeMassActionPreview}
        >
          <div
            className="w-full max-w-5xl bg-white dark:bg-gray-900 rounded-2xl shadow-2xl max-h-[90vh] flex flex-col overflow-hidden"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="px-6 py-5 border-b border-gray-200 dark:border-gray-800 flex items-start justify-between gap-3">
              <div className="space-y-1">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                  {massPreview.type === 'generate' ? 'Pré-visualização dos lançamentos' : 'Resumo dos documentos selecionados'}
                </h2>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {massPreview.type === 'generate'
                    ? 'Confira os documentos, parcelas e alertas antes de gerar os lançamentos financeiros em massa.'
                    : 'Visualize totais consolidados e alertas antes de imprimir o resumo.'}
                </p>
              </div>
              <button type="button" className="icon-button" onClick={closeMassActionPreview}>
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-800">
              <div className="flex flex-wrap items-center gap-2 text-xs font-semibold text-gray-600 dark:text-gray-300">
                <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-gray-100 dark:bg-gray-800/60">
                  <Layers className="w-4 h-4 text-primary-500" />
                  {massPreviewItens.length} documento(s)
                </span>
                <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-gray-100 dark:bg-gray-800/60">
                  <DollarSign className="w-4 h-4 text-primary-500" />
                  {formatCurrency(massPreviewTotais.valor)} em valor total
                </span>
                <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-gray-100 dark:bg-gray-800/60">
                  <ClipboardList className="w-4 h-4 text-primary-500" />
                  {massPreviewTotais.parcelas} parcela(s)
                </span>
                {massPreviewContagens.semFinanceiro > 0 && (
                  <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-gray-100 dark:bg-gray-800/60">
                    <Ban className="w-4 h-4 text-gray-500" />
                    {massPreviewContagens.semFinanceiro} sem efeito financeiro
                  </span>
                )}
                {massPreviewContagens.errors > 0 && (
                  <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-danger-100 text-danger-700 dark:bg-danger-900/30 dark:text-danger-200">
                    <AlertTriangle className="w-4 h-4" />
                    {massPreviewContagens.errors} pendência(s)
                  </span>
                )}
              </div>
              {massPreview.type === 'generate' && massPreviewContagens.semFinanceiro > 0 && (
                <label className="mt-3 inline-flex items-center gap-2 text-xs text-gray-600 dark:text-gray-300">
                  <input
                    type="checkbox"
                    className="h-4 w-4 text-primary-500 border-gray-300 rounded focus:ring-primary-500"
                    checked={forceGenerateSemFinanceiro}
                    onChange={(event) => setForceGenerateSemFinanceiro(event.target.checked)}
                  />
                  Incluir documentos marcados como sem efeito financeiro ({massPreviewContagens.semFinanceiro})
                </label>
              )}
              {massPreview.type === 'generate' && massPreviewContagens.errors > 0 && (
                <p className="mt-2 text-xs text-danger-600 dark:text-danger-300">
                  Alguns documentos possuem pendências e serão ignorados automaticamente até que sejam corrigidos.
                </p>
              )}
            </div>

            <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4 custom-scrollbar">
              {massPreviewItens.length === 0 ? (
                <div className="text-center text-sm text-gray-500 dark:text-gray-400 py-12">
                  Nenhum documento selecionado para exibir.
                </div>
              ) : (
                massPreviewItens.map((item, index) => {
                  const nota = item.nota;
                  const DocIcon = nota?.origem === 'xml_cte' ? Truck : FileText;
                  const proximoVencimento = item.avaliacao.parcelas.find((parcela) => parcela.data_vencimento);
                  const cardKey = item.key || nota?.id || nota?.numero || index;

                  return (
                    <div
                      key={cardKey}
                      className="border border-gray-200 dark:border-gray-700 rounded-2xl p-4 bg-white/90 dark:bg-gray-900/70 space-y-3"
                    >
                      <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                        <div className="space-y-1">
                          <div className="flex items-center gap-2 text-gray-900 dark:text-white">
                            <DocIcon className="w-5 h-5 text-primary-500" />
                            <span className="font-semibold">
                              {item.documentoTipo} {nota?.numero ? `nº ${nota.numero}` : 'sem número'}
                            </span>
                            <span className="text-sm text-primary-600 dark:text-primary-300">
                              {formatCurrency(item.avaliacao.valorTotalNumero)}
                            </span>
                          </div>
                          <div className="text-xs text-gray-500 dark:text-gray-400">
                            {item.empresaDescricao?.nome || 'Empresa não informada'}
                          </div>
                        </div>
                        <div className="flex flex-col sm:items-end gap-2">
                          <span
                            className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold ${getTipoOperacaoBadge(nota?.tipo_operacao)}`}
                          >
                            {item.tipoOperacaoLabel}
                          </span>
                          {!item.avaliacao.geraFinanceiro && (
                            <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-[11px] font-semibold bg-gray-200 text-gray-700 dark:bg-gray-800 dark:text-gray-200">
                              <Ban className="w-3 h-3" /> Sem efeito financeiro
                            </span>
                          )}
                        </div>
                      </div>

                      <div className="flex flex-wrap items-center gap-4 text-xs text-gray-600 dark:text-gray-300">
                        <span className="inline-flex items-center gap-1">
                          <ClipboardList className="w-4 h-4 text-primary-500" />
                          {item.avaliacao.parcelas.length} parcela(s)
                        </span>
                        <span className="inline-flex items-center gap-1">
                          <Calendar className="w-4 h-4 text-primary-500" />
                          Próximo vencimento:{' '}
                          {proximoVencimento?.data_vencimento ? formatDate(proximoVencimento.data_vencimento) : '—'}
                        </span>
                      </div>

                      {item.errorMessages.length > 0 && (
                        <div className="flex items-start gap-2 rounded-xl border border-danger-200 dark:border-danger-500/40 bg-danger-50/80 dark:bg-danger-900/20 p-3 text-danger-700 dark:text-danger-200">
                          <AlertTriangle className="w-4 h-4 mt-0.5" />
                          <div className="space-y-1">
                            {item.errorMessages.map((mensagem, idx) => (
                              <p key={idx} className="text-xs">
                                {mensagem}
                              </p>
                            ))}
                          </div>
                        </div>
                      )}

                      {item.warningMessages.length > 0 && (
                        <div className="flex items-start gap-2 rounded-xl border border-warning-200 dark:border-warning-500/40 bg-warning-50/80 dark:bg-warning-900/20 p-3 text-warning-700 dark:text-warning-200">
                          <Info className="w-4 h-4 mt-0.5" />
                          <div className="space-y-1">
                            {item.warningMessages.map((mensagem, idx) => (
                              <p key={idx} className="text-xs">
                                {mensagem}
                              </p>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })
              )}

              {massActionFeedback && (
                <div className="border border-primary-200 dark:border-primary-800 rounded-2xl p-4 bg-primary-50/60 dark:bg-primary-900/10 space-y-3">
                  <div className="flex items-center gap-2 text-sm font-semibold text-primary-800 dark:text-primary-200">
                    <CheckCircle2 className="w-4 h-4" /> Resumo da execução
                  </div>
                  <p className="text-xs text-primary-700 dark:text-primary-300">
                    {massActionFeedback.gerados} documento(s) geraram lançamentos. {massActionFeedback.ignorados.length}{' '}
                    ignorado(s). {massActionFeedback.erros.length} com erro.
                  </p>
                  {massActionFeedback.mensagens?.length > 0 && (
                    <ul className="text-xs text-gray-600 dark:text-gray-300 space-y-1 list-disc list-inside">
                      {massActionFeedback.mensagens.map((entrada, idx) => {
                        const nota = entrada.nota;
                        let label = 'Documento';
                        if (nota?.numero) {
                          label = `Documento nº ${nota.numero}`;
                        } else if (nota?.id) {
                          label = `Documento ID ${nota.id}`;
                        }
                        return (
                          <li key={idx}>
                            <span className="font-semibold">{label}:</span> {entrada.mensagem}
                          </li>
                        );
                      })}
                    </ul>
                  )}
                  {massActionFeedback.ignorados.length > 0 && (
                    <div className="text-xs text-warning-700 dark:text-warning-300 space-y-1">
                      <strong>Ignorados:</strong>
                      <ul className="list-disc list-inside space-y-1">
                        {massActionFeedback.ignorados.map((entrada, idx) => (
                          <li key={idx}>{entrada.mensagens?.join(' • ') || 'Documento ignorado por pendências.'}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {massActionFeedback.erros.length > 0 && (
                    <div className="text-xs text-danger-600 dark:text-danger-300 space-y-1">
                      <strong>Erros:</strong>
                      <ul className="list-disc list-inside space-y-1">
                        {massActionFeedback.erros.map((entrada, idx) => (
                          <li key={idx}>{entrada.mensagens?.join(' • ') || 'Erro inesperado ao gerar lançamentos.'}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="px-6 py-5 border-t border-gray-200 dark:border-gray-800 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div className="text-xs text-gray-500 dark:text-gray-400">
                Ações em massa ajudam a manter o financeiro sincronizado com poucos cliques.
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <button type="button" className="btn-tertiary" onClick={closeMassActionPreview} disabled={massActionProcessing}>
                  Fechar
                </button>
                {massPreview.type === 'generate' ? (
                  <button
                    type="button"
                    className="btn-primary"
                    onClick={handleConfirmMassGenerate}
                    disabled={massActionProcessing || massPreviewItens.length === 0}
                  >
                    {massActionProcessing ? 'Processando...' : 'Gerar lançamentos selecionados'}
                  </button>
                ) : (
                  <button type="button" className="btn-primary inline-flex items-center gap-2" onClick={handlePrintResumo}>
                    <Printer className="w-4 h-4" /> Imprimir
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {isRulesModalOpen && (
        <ImportRuleConfigurator
          isOpen={isRulesModalOpen}
          onClose={() => setIsRulesModalOpen(false)}
          rules={importRules}
          onCreateRule={handleCreateImportRule}
          onUpdateRule={handleUpdateImportRule}
          onDeleteRule={handleDeleteImportRule}
          onDuplicateRule={handleDuplicateImportRule}
          loading={rulesLoading}
          isSubmitting={rulesSubmitting}
          busyRuleId={rulesBusyId}
        />
      )}

      {showModal && (
        <div className="fixed inset-0 bg-black/40 z-40 flex items-center justify-center p-4" onClick={() => setShowModal(false)}>
          <div
            className="w-full max-w-3xl bg-white dark:bg-gray-900 rounded-2xl shadow-2xl max-h-[90vh] flex flex-col"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="p-6 border-b border-gray-200 dark:border-gray-800 flex justify-between items-center">
              <div>
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Cadastro manual de nota fiscal</h2>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Preencha os campos abaixo para registrar uma nota sem arquivo digital.
                </p>
              </div>
              <button className="icon-button" onClick={() => setShowModal(false)}>
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto custom-scrollbar">
              <form className="p-6 space-y-6" onSubmit={handleSubmitManual}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Número da NF-e</label>
                    <input
                      type="text"
                      className="input-field"
                      value={formData.numero}
                      onChange={(event) => setFormData((prev) => ({ ...prev, numero: event.target.value }))}
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Data de emissão</label>
                    <input
                      type="date"
                      className="input-field"
                      value={formData.data_emissao}
                      onChange={(event) => setFormData((prev) => ({ ...prev, data_emissao: event.target.value }))}
                      required
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Valor total</label>
                    <input
                      type="number"
                      step="0.01"
                      className="input-field"
                      value={formData.valor_total}
                      onChange={(event) => setFormData((prev) => ({ ...prev, valor_total: event.target.value }))}
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Tipo da operação</label>
                    <div className="grid grid-cols-2 gap-2">
                      <button
                        type="button"
                        onClick={() => setFormData((prev) => ({ ...prev, tipo_operacao: 'pagar' }))}
                        className={`px-3 py-2 rounded-lg border text-sm font-medium transition ${
                          formData.tipo_operacao === 'pagar'
                            ? 'border-danger-500 bg-danger-50 text-danger-700 dark:bg-danger-900/40 dark:text-danger-200'
                            : 'border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300'
                        }`}
                      >
                        Conta a pagar
                      </button>
                      <button
                        type="button"
                        onClick={() => setFormData((prev) => ({ ...prev, tipo_operacao: 'receber' }))}
                        className={`px-3 py-2 rounded-lg border text-sm font-medium transition ${
                          formData.tipo_operacao === 'receber'
                            ? 'border-success-500 bg-success-50 text-success-700 dark:bg-success-900/40 dark:text-success-200'
                            : 'border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300'
                        }`}
                      >
                        Conta a receber
                      </button>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <p className="text-sm font-semibold text-gray-900 dark:text-white">Emitente</p>
                    <input
                      type="text"
                      className="input-field"
                      placeholder="Razão social"
                      value={formData.emitente_nome}
                      onChange={(event) => setFormData((prev) => ({ ...prev, emitente_nome: event.target.value }))}
                    />
                    <input
                      type="text"
                      className="input-field"
                      placeholder="CNPJ"
                      value={formData.emitente_cnpj}
                      onChange={(event) => setFormData((prev) => ({ ...prev, emitente_cnpj: event.target.value }))}
                    />
                  </div>
                  <div className="space-y-2">
                    <p className="text-sm font-semibold text-gray-900 dark:text-white">Destinatário</p>
                    <input
                      type="text"
                      className="input-field"
                      placeholder="Razão social"
                      value={formData.destinatario_nome}
                      onChange={(event) => setFormData((prev) => ({ ...prev, destinatario_nome: event.target.value }))}
                    />
                    <input
                      type="text"
                      className="input-field"
                      placeholder="CNPJ"
                      value={formData.destinatario_cnpj}
                      onChange={(event) => setFormData((prev) => ({ ...prev, destinatario_cnpj: event.target.value }))}
                    />
                  </div>
                </div>

                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <h4 className="text-sm font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                      <ClipboardList className="w-4 h-4 text-primary-500" />
                      Parcelas
                    </h4>
                    <button type="button" onClick={adicionarParcelaManual} className="text-sm text-primary-600 dark:text-primary-300 flex items-center gap-1">
                      <Plus className="w-4 h-4" />
                      Adicionar parcela
                    </button>
                  </div>
                  {formParcelas.length > 0 ? (
                    <div className="space-y-3">
                      {formParcelas.map((parcela, index) => (
                        <div
                          key={`manual-parcela-${index}`}
                          className="grid grid-cols-1 md:grid-cols-[auto,1fr,1fr,auto] gap-3 items-end bg-gray-50 dark:bg-gray-900/40 border border-gray-200 dark:border-gray-800 rounded-xl p-3"
                        >
                          <div>
                            <label className="text-xs uppercase font-semibold text-gray-500 dark:text-gray-400">#</label>
                            <input
                              type="text"
                              className="input-field"
                              value={parcela.numero}
                              onChange={(event) => atualizarParcelaManual(index, 'numero', event.target.value)}
                            />
                          </div>
                          <div>
                            <label className="text-xs uppercase font-semibold text-gray-500 dark:text-gray-400">Vencimento</label>
                            <input
                              type="date"
                              className="input-field"
                              value={parcela.data_vencimento}
                              onChange={(event) => atualizarParcelaManual(index, 'data_vencimento', event.target.value)}
                            />
                          </div>
                          <div>
                            <label className="text-xs uppercase font-semibold text-gray-500 dark:text-gray-400">Valor</label>
                            <input
                              type="number"
                              step="0.01"
                              className="input-field"
                              value={parcela.valor}
                              onChange={(event) => atualizarParcelaManual(index, 'valor', event.target.value)}
                            />
                          </div>
                          <button type="button" onClick={() => removerParcelaManual(index)} className="icon-button text-danger-500 hover:text-danger-600">
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      Adicione parcelas ou preencha o vencimento único abaixo para gerar lançamento.
                    </p>
                  )}

                  {formParcelas.length === 0 && (
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Vencimento único</label>
                      <input
                        type="date"
                        className="input-field"
                        value={formData.data_vencimento}
                        onChange={(event) => setFormData((prev) => ({ ...prev, data_vencimento: event.target.value }))}
                      />
                    </div>
                  )}

                  {formParcelas.length > 0 && (
                    <div className="text-sm text-gray-600 dark:text-gray-400 flex items-center gap-2">
                      <Info className="w-4 h-4" />
                      Soma das parcelas: {formatCurrency(totalParcelasManual)}
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Observações</label>
                  <textarea
                    rows="3"
                    className="input-field"
                    value={formData.observacoes}
                    onChange={(event) => setFormData((prev) => ({ ...prev, observacoes: event.target.value }))}
                    placeholder="Informações adicionais sobre a nota fiscal..."
                  />
                </div>

                <div className="flex gap-3 pt-4">
                  <button type="submit" className="flex-1 btn-primary">
                    Salvar nota fiscal
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowModal(false);
                      setFormData(initialManualForm);
                      setFormParcelas([]);
                    }}
                    className="flex-1 btn-secondary"
                  >
                    Cancelar
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {selectedNotaState && (
        <div className="fixed inset-0 flex justify-end bg-black/40 z-40" onClick={closeDrawer}>
          <div
            className="w-full max-w-lg h-full bg-white dark:bg-gray-900 shadow-2xl animate-slide-in-right overflow-y-auto custom-scrollbar"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="p-6 border-b border-gray-200 dark:border-gray-800 flex justify-between items-center">
  <div>
    <p className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">
      Detalhes {selectedNotaState.origem === 'xml_cte' ? 'do CT-e' : 'da NF-e'}
    </p>
    <h2 className="text-2xl font-bold text-gray-900 dark:text-white">{selectedNotaState.numero}</h2>
                <div className="flex flex-wrap gap-2 mt-2">
                  <span className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold ${getTipoOperacaoBadge(selectedNotaState.tipo_operacao)}`}>
                    {tipoOperacaoLabel(selectedNotaState.tipo_operacao)}
                  </span>
                  <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300">
                    {selectedNotaState.origem ? selectedNotaState.origem.toUpperCase() : 'MANUAL'}
                  </span>
                  {selectedNotaSituacaoFinanceira && (
                    <span
                      className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold ${selectedNotaSituacaoFinanceira.tone}`}
                      title={selectedNotaSituacaoFinanceira.tooltip}
                    >
                      <selectedNotaSituacaoFinanceira.Icon className="w-3.5 h-3.5" />
                      {selectedNotaSituacaoFinanceira.label}
                    </span>
                  )}
                </div>
              </div>
              <button className="icon-button" onClick={closeDrawer}>
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 space-y-6">
              {selectedNotaFinanceiroAtivo && (selectedNotaSemVencimento || selectedNotaParcelasSemData) && (
                <div className="rounded-2xl border border-warning-400 bg-warning-50/90 dark:bg-warning-900/40 p-4 flex gap-3">
                  <div className="pt-1">
                    <AlertTriangle className={`w-5 h-5 text-warning-600 ${selectedNotaSemVencimento ? 'animate-pulse' : ''}`} />
                  </div>
                  <div className="space-y-1">
                    <p className="text-sm font-semibold text-warning-800 dark:text-warning-100">Pendências de vencimento</p>
                    <ul className="list-disc space-y-1 text-xs text-warning-700 dark:text-warning-100/80 ml-4">
                      {selectedNotaSemVencimento && (
                        <li>Informe um vencimento único ou preencha o vencimento das parcelas antes de salvar.</li>
                      )}
                      {selectedNotaParcelasSemData && (
                        <li>Revise as parcelas sem data de vencimento para garantir a geração financeira correta.</li>
                      )}
                    </ul>
                  </div>
                </div>
              )}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="card bg-gray-50 dark:bg-gray-800/60">
                  <p className="text-xs uppercase text-gray-500 dark:text-gray-400 font-semibold mb-2">Emitente</p>
                  <p className="text-lg font-semibold text-gray-900 dark:text-white">{selectedNotaState.emitente?.nome || '—'}</p>
                  {selectedNotaState.emitente?.cnpj && (
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">CNPJ: {formatCnpj(selectedNotaState.emitente.cnpj)}</p>
                  )}
                </div>
                <div className="card bg-gray-50 dark:bg-gray-800/60">
                  <p className="text-xs uppercase text-gray-500 dark:text-gray-400 font-semibold mb-2">Destinatário</p>
                  <p className="text-lg font-semibold text-gray-900 dark:text-white">{selectedNotaState.destinatario?.nome || '—'}</p>
                  {selectedNotaState.destinatario?.cnpj && (
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">CNPJ: {formatCnpj(selectedNotaState.destinatario.cnpj)}</p>
                  )}
                </div>
              </div>

              <div className="space-y-3">
                <div className="rounded-2xl border border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/60 p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                  <p className="text-xs uppercase text-gray-500 dark:text-gray-400 font-semibold">Valor total</p>
                  <p className="text-2xl font-bold text-primary-600 dark:text-primary-400 sm:text-right">
                    {formatCurrency(selectedNotaState.valor_total)}
                  </p>
                </div>
                <div className="rounded-2xl border border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/60 p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                  <p className="text-xs uppercase text-gray-500 dark:text-gray-400 font-semibold">Data de emissão</p>
                  <p className="text-lg font-semibold text-gray-900 dark:text-white sm:text-right">
                    {selectedNotaState.data_emissao ? formatDate(selectedNotaState.data_emissao) : '—'}
                  </p>
                </div>
                <div
                  className={`rounded-2xl border border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/60 p-4 ${
                    selectedNotaSemVencimento ? 'border-warning-400 bg-warning-50/80 dark:bg-warning-900/30' : ''
                  }`}
                >
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                    <div className="flex items-center gap-2 text-xs uppercase font-semibold">
                      {selectedNotaSemVencimento && (
                        <AlertTriangle className="w-4 h-4 text-warning-600" aria-hidden="true" />
                      )}
                      <p className={`tracking-wide ${selectedNotaSemVencimento ? 'text-warning-700 dark:text-warning-200' : 'text-gray-500 dark:text-gray-400'}`}>
                        Próximo vencimento
                      </p>
                    </div>
                    {selectedNotaAnalise?.possuiParcelas && (
                      <p
                        className={`text-lg font-semibold sm:text-right ${
                          selectedNotaSemVencimento ? 'text-warning-700 dark:text-warning-100' : 'text-gray-900 dark:text-white'
                        }`}
                      >
                        {selectedNotaParcelasInfo?.next?.data_vencimento
                          ? formatDate(selectedNotaParcelasInfo.next.data_vencimento)
                          : 'Defina nas parcelas abaixo'}
                      </p>
                    )}
                  </div>
                  {selectedNotaAnalise?.possuiParcelas ? (
                    <div className="mt-2">
                      {selectedNotaParcelasSemData ? (
                        <p className="text-xs text-warning-700 dark:text-warning-200">
                          Algumas parcelas estão sem data de vencimento.
                        </p>
                      ) : (
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          O primeiro vencimento das parcelas será utilizado para gerar os lançamentos.
                        </p>
                      )}
                    </div>
                  ) : (
                    <div className="mt-3 space-y-2">
                      <input
                        type="date"
                        className={`input-field w-full ${selectedNotaSemVencimento ? 'border-warning-400 focus:ring-warning-500 focus:border-warning-500' : ''}`}
                        value={selectedNotaState.data_vencimento || ''}
                        onChange={(event) => atualizarNotaSelecionada({ data_vencimento: event.target.value })}
                      />
                      <p
                        className={`text-xs ${
                          selectedNotaSemVencimento ? 'text-warning-700 dark:text-warning-200' : 'text-gray-500 dark:text-gray-400'
                        }`}
                      >
                        Necessário para gerar lançamentos quando não houver parcelas cadastradas.
                      </p>
                    </div>
                  )}
                </div>
              </div>

              <div className="card bg-gray-50 dark:bg-gray-900/50 space-y-5">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <div>
                    <p className="text-xs uppercase text-gray-500 dark:text-gray-400 font-semibold mb-1">
                      Classificação financeira
                    </p>
                    <p className="text-sm font-semibold text-gray-900 dark:text-white">
                      {parseBooleanFlag(selectedNotaState.gera_financeiro, true)
                        ? 'Gera lançamentos financeiros'
                        : 'Sem geração financeira'}
                    </p>
                    {selectedNotaState.motivo_financeiro && !parseBooleanFlag(selectedNotaState.gera_financeiro, true) && (
                      <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                        {selectedNotaState.motivo_financeiro}
                      </p>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() =>
                      atualizarNotaSelecionada((nota) => ({
                        ...nota,
                        ...(() => {
                          const atual = parseBooleanFlag(nota.gera_financeiro, true);
                          const proximo = !atual;
                          return {
                            gera_financeiro: proximo,
                            motivo_financeiro: proximo
                              ? null
                              : nota.motivo_financeiro || 'Marcada manualmente como não financeira.'
                          };
                        })()
                      }))
                    }
                    className="btn-secondary text-sm whitespace-nowrap"
                  >
                    {parseBooleanFlag(selectedNotaState.gera_financeiro, true)
                      ? 'Marcar como sem financeiro'
                      : 'Permitir geração financeira'}
                  </button>
                </div>

                <div className="space-y-4">
                  <div className="space-y-1">
                    <p className="text-xs uppercase font-semibold text-gray-500 dark:text-gray-400">Classificação do sistema</p>
                    <p className="text-sm font-semibold text-gray-900 dark:text-white break-words">
                      {selectedNotaClassificacaoDescricao || 'Sem classificação cadastrada para este CFOP'}
                    </p>
                    {selectedNotaCfopInfo?.decisao_sugerida && (
                      <p className="text-xs text-primary-600 dark:text-primary-300">
                        Sugerido: {selectedNotaCfopInfo.decisao_sugerida}
                      </p>
                    )}
                    {selectedNotaCfopInfo?.motivo && (
                      <p className="text-xs text-gray-600 dark:text-gray-400">
                        {selectedNotaCfopInfo.motivo}
                      </p>
                    )}
                  </div>

                  <div className="space-y-3">
                    {renderInlineEditableField('cfop', {
                      label: 'CFOP',
                      value: selectedNotaState.cfop || '',
                      displayValue: selectedNotaCfopFormatado || selectedNotaState.cfop || '',
                      placeholder: 'Clique para informar o CFOP',
                      inputMode: 'text'
                    })}
                    {renderInlineEditableField('natureza_operacao', {
                      label: 'Natureza da operação',
                      value: selectedNotaState.natureza_operacao || '',
                      placeholder: 'Clique para informar a natureza conforme o documento'
                    })}
                  </div>
                </div>

                {!parseBooleanFlag(selectedNotaState.gera_financeiro, true) && (
                  <div>
                    <label className="block text-xs uppercase font-semibold text-gray-500 dark:text-gray-400 mb-1">
                      Motivo
                    </label>
                    <textarea
                      rows={2}
                      className="input-field"
                      value={selectedNotaState.motivo_financeiro || ''}
                      onChange={(event) =>
                        atualizarNotaSelecionada({ motivo_financeiro: event.target.value })
                      }
                      placeholder="Descreva por que esta nota não gera lançamentos."
                    />
                  </div>
                )}
              </div>

              {selectedNotaState.chave_acesso && (
                <div className="card bg-gray-50 dark:bg-gray-800/60">
                  <p className="text-xs uppercase text-gray-500 dark:text-gray-400 font-semibold mb-2">Chave de acesso</p>
                  <p className="font-mono text-sm text-gray-700 dark:text-gray-300 break-all">{selectedNotaState.chave_acesso}</p>
                </div>
              )}

              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Parcelas</h3>
                  <button
                    onClick={() =>
                      atualizarNotaSelecionada((nota) => ({
                        ...nota,
                        parcelas: [
                          ...(nota.parcelas || []),
                          {
                            numero: (nota.parcelas?.length || 0) + 1,
                            data_vencimento: '',
                            valor: 0
                          }
                        ]
                      }))
                    }
                    className="text-sm text-primary-600 dark:text-primary-300 flex items-center gap-1"
                  >
                    <Plus className="w-4 h-4" /> Adicionar parcela
                  </button>
                </div>

                {selectedNotaState.parcelas && selectedNotaState.parcelas.length > 0 ? (
                  <div className="space-y-3">
                    {selectedNotaState.parcelas.map((parcela, index) => (
                      <div
                        key={`drawer-parcela-${index}`}
                        className="grid grid-cols-1 sm:grid-cols-[auto,1fr,1fr,auto] gap-3 items-end bg-gray-50 dark:bg-gray-900/40 border border-gray-200 dark:border-gray-800 rounded-xl p-3"
                      >
                        <div>
                          <label className="text-xs uppercase text-gray-500 dark:text-gray-400 font-semibold">#</label>
                          <input
                            type="text"
                            className="input-field"
                            value={parcela.numero}
                            onChange={(event) =>
                              atualizarNotaSelecionada((nota) => {
                                const parcelas = [...(nota.parcelas || [])];
                                parcelas[index] = { ...parcelas[index], numero: event.target.value };
                                return { ...nota, parcelas };
                              })
                            }
                          />
                        </div>
                        <div>
                          <label className="text-xs uppercase text-gray-500 dark:text-gray-400 font-semibold">Vencimento</label>
                          <input
                            type="date"
                            className="input-field"
                            value={parcela.data_vencimento || ''}
                            onChange={(event) =>
                              atualizarNotaSelecionada((nota) => {
                                const parcelas = [...(nota.parcelas || [])];
                                parcelas[index] = { ...parcelas[index], data_vencimento: event.target.value };
                                return { ...nota, parcelas };
                              })
                            }
                          />
                        </div>
                        <div>
  <label className="text-xs uppercase text-gray-500 dark:text-gray-400 font-semibold">Valor</label>
  <input
    type="number"
    step="0.01"
    className="input-field w-full"
    style={{ minWidth: '120px' }}
    value={parcela.valor}
                            onChange={(event) =>
                              atualizarNotaSelecionada((nota) => {
                                const parcelas = [...(nota.parcelas || [])];
                                parcelas[index] = { ...parcelas[index], valor: parseFloat(event.target.value) || 0 };
                                return { ...nota, parcelas };
                              })
                            }
                          />
                        </div>
                        <button
                          type="button"
                          onClick={() =>
                            atualizarNotaSelecionada((nota) => {
                              const parcelas = [...(nota.parcelas || [])];
                              parcelas.splice(index, 1);
                              return { ...nota, parcelas };
                            })
                          }
                          className="icon-button text-danger-500 hover:text-danger-600"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="space-y-2 text-sm text-gray-600 dark:text-gray-400">
                    <p>Esta nota não possui parcelas registradas.</p>
                    <p>Use o campo "Próximo vencimento" acima para informar o vencimento único antes de salvar.</p>
                  </div>
                )}
              </div>

              {selectedNotaState.faturas?.length > 0 && (
                <div className="card bg-gray-50 dark:bg-gray-900/50">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                    <FileText className="w-4 h-4 text-primary-500" /> Faturas
                  </h3>
                  <ul className="space-y-2 text-sm">
                    {selectedNotaState.faturas.map((fatura, index) => (
                      <li key={`drawer-fatura-${index}`} className="flex flex-col gap-1">
                        <span className="font-semibold text-gray-800 dark:text-gray-100">{fatura.numero || `Fatura ${index + 1}`}</span>
                        <div className="flex flex-wrap gap-3 text-xs text-gray-600 dark:text-gray-400">
                          <span>Original: {formatCurrency(fatura.valor_original)}</span>
                          <span>Desconto: {formatCurrency(fatura.valor_desconto)}</span>
                          <span>Líquido: {formatCurrency(fatura.valor_liquido)}</span>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {selectedNotaState.pagamentos?.length > 0 && (
                <div className="card bg-gray-50 dark:bg-gray-900/50">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                    <CreditCard className="w-4 h-4 text-primary-500" /> Pagamentos identificados
                  </h3>
                  <ul className="space-y-2 text-sm">
                    {selectedNotaState.pagamentos.map((pagamento, index) => (
                      <li key={`drawer-pagamento-${index}`} className="flex flex-col gap-1">
                        <span className="font-semibold text-gray-800 dark:text-gray-100">
  {getPaymentMethodName(pagamento.meio) || 'Forma de pagamento'}
</span>                        <div className="flex flex-wrap gap-3 text-xs text-gray-600 dark:text-gray-400">
                          <span>Valor: {formatCurrency(pagamento.valor)}</span>
                          {pagamento.indicador && <span>Indicador: {pagamento.indicador}</span>}
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="space-y-3">
                <label className="text-sm font-semibold text-gray-700 dark:text-gray-300">Observações</label>
                <textarea
                  rows="4"
                  className="input-field"
                  value={selectedNotaState.observacoes || ''}
                  onChange={(event) => atualizarNotaSelecionada({ observacoes: event.target.value })}
                  placeholder="Anotações internas, pendências ou complementos desta nota."
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <button
                  onClick={handlePersistNota}
                  className={`btn-secondary flex-1 ${
                    canPersistSelectedNota || savingSelectedNota ? '' : 'opacity-60 cursor-not-allowed'
                  }`}
                  disabled={!canPersistSelectedNota}
                >
                  {selectedNotaState?.id
                    ? savingSelectedNota
                      ? 'Salvando...'
                      : selectedNotaDirty
                        ? 'Salvar Alterações'
                        : 'Alterações Salvas'
                    : selectedNotaDirty
                      ? 'Salvar localmente'
                      : 'Sem Alterações'}
                </button>
                <button
  onClick={() => handleGenerateLancamentos(selectedNotaState)}
  className="btn-primary flex-1 flex items-center justify-center gap-2"
  title={
    generatingLancamentoId === (selectedNotaState.id || selectedNotaState.numero)
      ? 'Gerando lançamentos...'
      : !parseBooleanFlag(selectedNotaState.gera_financeiro, true)
        ? 'Nota marcada como sem efeito financeiro. Confirme para gerar mesmo assim.'
        : 'Gerar lançamentos financeiros'
  }
>
  <Rocket size={30} />
  {generatingLancamentoId === (selectedNotaState.id || selectedNotaState.numero)
    ? 'Gerando Lançamento...'
    : 'Gerar Lançamento'}
</button>
                <button
                  onClick={() => handleDelete(selectedNotaState.id || selectedNotaState.numero)}
                  className={`btn-danger flex-1 ${
                    deletingNotaId === (selectedNotaState.id || selectedNotaState.numero)
                      ? 'opacity-70 cursor-wait'
                      : ''
                  }`}
                  disabled={deletingNotaId === (selectedNotaState.id || selectedNotaState.numero)}
                >
                  {deletingNotaId === (selectedNotaState.id || selectedNotaState.numero) ? 'Excluindo...' : 'Excluir Nota'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {bulkImportResult &&
        (() => {
          const summary = bulkImportResult.summary || {};
          const savedNotas = Array.isArray(bulkImportResult.savedNotas) ? bulkImportResult.savedNotas : [];
          const savedNfeCount = savedNotas.filter((nota) => nota?.origem !== 'xml_cte').length;
          const savedCteCount = savedNotas.filter((nota) => nota?.origem === 'xml_cte').length;
          const hasSaved = savedNotas.length > 0;
          const SummaryIcon = hasSaved ? CheckCircle2 : AlertTriangle;
          const summaryHighlight = hasSaved
            ? savedNfeCount > 0 && savedCteCount > 0
              ? `Sucesso! ${savedNfeCount} NF-e e ${savedCteCount} CT-e foram salvos com sucesso.`
              : savedNfeCount > 0
                ? `Sucesso! ${savedNfeCount} nota(s) fiscal(is) foram salvas com sucesso.`
                : `Sucesso! ${savedCteCount} CT-e foram salvos com sucesso.`
            : 'Nenhum documento foi salvo. Revise as informações antes de tentar novamente.';
          const errorMessages = Array.isArray(summary.erros) ? summary.erros : [];

          return (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/70 backdrop-blur-sm px-4 py-8">
              <div className="w-full max-w-4xl bg-white dark:bg-gray-900 rounded-3xl shadow-2xl overflow-hidden">
                {showBulkResultDetails ? (
                  <>
                    <div className="px-6 py-5 border-b border-gray-200 dark:border-gray-800 flex items-center justify-between gap-3">
                      <button
                        type="button"
                        className="btn-tertiary inline-flex items-center gap-2"
                        onClick={() => setShowBulkResultDetails(false)}
                      >
                        <ArrowLeft className="w-4 h-4" />
                        Voltar ao resumo
                      </button>
                      <button className="icon-button" onClick={closeBulkImportResult}>
                        <X className="w-5 h-5" />
                      </button>
                    </div>
                    <div className="px-6 py-6 space-y-4 max-h-[60vh] overflow-y-auto custom-scrollbar">
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Documentos salvos</h3>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          Revise os documentos importados recentemente. Você pode abri-los ou removê-los diretamente daqui.
                        </p>
                      </div>

                      {savedNotas.length > 0 ? (
                        <div className="space-y-4">
                          {savedNotas.map((nota, index) => {
                            const empresaDescricao = describeEmpresaNota(nota);
                            const DocIcon = nota?.origem === 'xml_cte' ? Truck : FileText;
                            const docLabel = nota?.origem === 'xml_cte' ? 'CT-e' : 'NF-e';
                            const notaKey = nota?.id != null ? nota.id : nota?.numero;
                            return (
                              <div
                                key={notaKey ?? `saved-${index}`}
                                className="border border-gray-200 dark:border-gray-800 rounded-2xl px-4 py-3 flex flex-col md:flex-row md:items-center md:justify-between gap-3 bg-gray-50/60 dark:bg-gray-900/40"
                              >
                                <div className="space-y-1">
                                  <div className="flex items-center gap-2 text-sm font-semibold text-gray-900 dark:text-white">
                                    <span
                                      className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold ${
                                        nota?.origem === 'xml_cte'
                                          ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-200'
                                          : 'bg-primary-100 text-primary-700 dark:bg-primary-900/40 dark:text-primary-200'
                                      }`}
                                    >
                                      <DocIcon className="w-4 h-4" /> {docLabel}
                                    </span>
                                    <span>{nota?.numero || 'Sem número'}</span>
                                  </div>
                                  <div className="text-xs text-gray-600 dark:text-gray-400">
                                    {empresaDescricao?.nome || 'Empresa não identificada'}
                                  </div>
                                  <div className="text-xs text-gray-500 dark:text-gray-500">
                                    Valor total: {formatCurrency(nota?.valor_total || 0)}
                                  </div>
                                  {nota?.data_emissao && (
                                    <div className="text-xs text-gray-500 dark:text-gray-500">
                                      Emitida em {formatDate(nota.data_emissao)}
                                    </div>
                                  )}
                                </div>
                                <div className="flex flex-wrap gap-2">
                                  <button
                                    type="button"
                                    className="btn-secondary"
                                    onClick={() => handleViewSavedNota(nota)}
                                  >
                                    Ver detalhes
                                  </button>
                                  <button
                                    type="button"
                                    className="btn-danger"
                                    onClick={() => handleDeleteSavedNota(nota)}
                                    disabled={deletingNotaId === notaKey}
                                  >
                                    {deletingNotaId === notaKey ? 'Excluindo...' : 'Excluir'}
                                  </button>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        <div className="text-sm text-gray-600 dark:text-gray-400">
                          Nenhum documento salvo está disponível para revisão.
                        </div>
                      )}
                    </div>
                    <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-800 flex flex-col sm:flex-row gap-3">
                      <button className="btn-secondary flex-1" onClick={() => setShowBulkResultDetails(false)}>
                        Voltar
                      </button>
                      <button className="btn-primary flex-1" onClick={closeBulkImportResult}>
                        Concluir
                      </button>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="px-6 py-5 border-b border-gray-200 dark:border-gray-800 flex items-start justify-between gap-3">
                      <div className="space-y-1">
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Importação finalizada</h3>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          Veja os resultados do salvamento dos documentos importados.
                        </p>
                      </div>
                      <button className="icon-button" onClick={closeBulkImportResult}>
                        <X className="w-5 h-5" />
                      </button>
                    </div>
                    <div className="px-6 py-6 space-y-6">
                      <div
                        className={`rounded-2xl px-4 py-3 flex items-start gap-3 ${
                          hasSaved
                            ? 'bg-success-50 dark:bg-success-900/30 text-success-700 dark:text-success-200'
                            : 'bg-warning-50 dark:bg-warning-900/30 text-warning-700 dark:text-warning-200'
                        }`}
                      >
                        <SummaryIcon className="w-5 h-5 mt-0.5" />
                        <div className="space-y-1">
                          <p className="text-sm font-semibold">{summaryHighlight}</p>
                          <p className="text-xs text-gray-600 dark:text-gray-300">
                            Documentos não salvos permanecem disponíveis na pré-visualização para novos ajustes.
                          </p>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                        {[
                          { label: 'Selecionadas', value: summary.totalSelecionadas ?? 0 },
                          { label: 'Salvas', value: summary.salvas ?? 0 },
                          { label: 'Ignoradas', value: summary.ignoradas ?? 0 },
                          { label: 'Duplicadas', value: summary.duplicadas ?? 0 },
                          { label: 'Falhas', value: summary.falhas ?? 0 }
                        ].map((item) => (
                          <div
                            key={item.label}
                            className="border border-gray-200 dark:border-gray-800 rounded-2xl px-4 py-3 bg-gray-50/60 dark:bg-gray-900/40"
                          >
                            <p className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">{item.label}</p>
                            <p className="text-lg font-semibold text-gray-900 dark:text-white">{item.value}</p>
                          </div>
                        ))}
                      </div>

                      {hasSaved && (
                        <div className="flex flex-wrap gap-2 text-xs font-semibold text-gray-600 dark:text-gray-400">
                          {savedNfeCount > 0 && (
                            <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-primary-100 text-primary-700 dark:bg-primary-900/40 dark:text-primary-200">
                              <FileText className="w-3.5 h-3.5" /> {savedNfeCount} NF-e
                            </span>
                          )}
                          {savedCteCount > 0 && (
                            <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-primary-100 text-primary-700 dark:bg-primary-900/40 dark:text-primary-200">
                              <Truck className="w-3.5 h-3.5" /> {savedCteCount} CT-e
                            </span>
                          )}
                        </div>
                      )}

                      {errorMessages.length > 0 && (
                        <div className="border border-warning-200 dark:border-warning-900/50 bg-warning-50/80 dark:bg-warning-900/30 rounded-2xl p-4 space-y-2 text-sm text-warning-800 dark:text-warning-100">
                          <p className="font-semibold flex items-center gap-2">
                            <AlertTriangle className="w-4 h-4" /> Ocorreram problemas ao salvar alguns documentos:
                          </p>
                          <ul className="list-disc ml-5 space-y-1">
                            {errorMessages.map((erro) => (
                              <li key={erro.id}>
                                Nota {erro.numero}: {erro.mensagem}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                    <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-800 flex flex-col sm:flex-row gap-3">
                      {hasSaved && (
                        <button className="btn-secondary flex-1" onClick={() => setShowBulkResultDetails(true)}>
                          Ver detalhes
                        </button>
                      )}
                      <button className="btn-primary flex-1" onClick={closeBulkImportResult}>
                        Fechar
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          );
        })()}

      {showFinanceiroOverride && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
          onClick={cancelarOverrideFinanceiro}
        >
          <div
            className="w-full max-w-md bg-white dark:bg-gray-900 rounded-2xl shadow-xl p-6 space-y-4"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-3">
              <div>
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white">Gerar lançamentos mesmo assim?</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                  Esta nota está marcada como sem efeito financeiro.
                  {notaParaOverride?.motivo_financeiro && (
                    <>
                      {' '}
                      Motivo registrado: {notaParaOverride.motivo_financeiro}
                    </>
                  )}
                </p>
              </div>
              <button className="icon-button" onClick={cancelarOverrideFinanceiro}>
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="bg-gray-50 dark:bg-gray-800/60 rounded-xl p-4 space-y-2 text-sm text-gray-700 dark:text-gray-300">
              {notaParaOverride?.cfop && (
                <div>
                  <span className="font-semibold">CFOP:</span> {notaParaOverride.cfop}
                </div>
              )}
              {notaParaOverride?.natureza_operacao && (
                <div>
                  <span className="font-semibold">Natureza da operação:</span> {notaParaOverride.natureza_operacao}
                </div>
              )}
              <div>
                <span className="font-semibold">Valor total:</span>{' '}
                {formatCurrency(notaParaOverride?.valor_total || 0)}
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-3 pt-2">
              <button onClick={cancelarOverrideFinanceiro} className="btn-secondary flex-1">
                Cancelar
              </button>
              <button onClick={confirmarOverrideFinanceiro} className="btn-primary flex-1">
                Gerar mesmo assim
              </button>
            </div>
          </div>
        </div>
      )}

      {globalOverlay && <LoadingOverlay message={globalOverlay.message} submessage={globalOverlay.submessage} />}
    </div>
  );
}
